import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, NgZone, Inject, ViewEncapsulation, ChangeDetectorRef, HostBinding } from '@angular/core';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EventService } from "../core/services/event.service";
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';

// utils function
import { environment } from "src/environments/environment";
import * as SparkMD5 from 'spark-md5';
import { UploadService } from "../core/services/upload.service";
import type { S3Client } from '@aws-sdk/client-s3';
import { createS3Client, createCognitoCredentials, createUpload, loadS3ClientModule, withTimeout, S3_REQUEST_TIMEOUT_MS } from '../utils/aws-s3.util';
import { DomSanitizer, Meta, SafeUrl } from "@angular/platform-browser";
import { firstValueFrom } from 'rxjs';
import { LogToS3Service, describeError } from "../core/services/log.service";
import { AnalyticsService } from "../core/services/analytics.service";
import firebase from "firebase/compat";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { HttpClient } from '@angular/common/http';
import { SafeStorage } from '../core/utils/safe-storage';
import { ToastService } from '../core/services/toast.service';
import { UploadBannerService } from '../core/services/upload-banner.service';
import { GuestService } from '../core/services/guest.service';
import { GuestHostPromptService } from '../core/services/guest-host-prompt.service';
import { GUEST_AUTO_NAME_KEY, isGuestAutoName } from '../core/utils/guest-auto-name';
import {
  DiagnosedFailure,
  UploadFailureKind,
  classifyUploadError,
  inPhase,
  isFileStillReadable,
  needsReachabilityProbe,
  probeFileRead,
  probeUploadReachability,
  ReachabilityReport,
  refineWithReachability,
  retryDelayMs,
  uploadFailureCause,
} from '../utils/upload-error.util';
import {
  BROWSE_WHILE_UPLOADING,
  KEEP_PICAGGO_OPEN_HINT,
  STOPPING_NOTE,
  STOPPING_TITLE,
  STOP_UPLOAD_CONFIRM,
  STOP_UPLOAD_KEEP,
  TRANSFER_NOTE,
  transferTitleFor,
  albumWord,
  UPLOAD_STOPPED_TITLE,
  UPLOAD_QUEUED_NOTE,
  UPLOAD_QUEUED_TITLE,
  UNREADABLE_TAG,
  STOPPED_REASON,
  STOPPED_SECTION_TITLE,
  stoppedGroupMessage,
  stopUploadBody,
  stopUploadTitle,
  transferCount,
  failureGroupMessage,
  failureReason,
  slowConnectionNotice,
  stalledConnectionNotice,
  unreadableInProgress,
  unreadablePickBody,
  unreadablePickTitle,
  unreadableResultSubtitle,
  unreadableResultTitle,
  unreadableStillBlocked,
  waitingForConnectionNotice,
} from '../utils/upload-failure-copy';

/**
 * What actually reached `failedFiles`, alongside the diagnosis that determines
 * whether offering "Retry" is honest. `'invalid-file'` covers the pre-upload
 * validation rejections (0 bytes, over the size cap, malformed File object),
 * which never went through `classifyUploadError` and so have no `UploadFailureKind`.
 */
type FailedFileKind = UploadFailureKind | 'invalid-file';

interface FailedFileEntry {
  file: File;
  error: string;
  retries: number;
  kind: FailedFileKind;
  retryable: boolean;
  /** Left out because the person pressed Stop, not because anything went wrong. */
  stopped?: boolean;
}

/**
 * A verdict reached by post-upload verification rather than by the SDK, so the
 * retry loop can tell "S3 says this is wrong" from a transport error and never
 * retries past our own conclusion.
 */
class UploadVerificationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadVerificationError';
  }
}

/**
 * Thrown inside a file's retry loop once the person has stopped the upload, so
 * the file is recorded as stopped rather than as whatever failure it last hit.
 */
const UPLOAD_STOPPED = new Error('The upload was stopped');

interface UploadProgressInfo {
  tracking_id?: number;
  file_name: string;
  original_file_name: string;
  status: 'uploading' | 'processing' | 'completed' | 'failed';
  progress_percentage: number;
  stage: string;
  error_message?: string;
  error_stage?: string;
  retry_count: number;
}

@Component({
  selector: "app-add-from-gallery-popup",
  templateUrl: "./add-from-gallery-popup.component.html",
  styleUrls: ["./add-from-gallery-popup.component.css"],
  encapsulation: ViewEncapsulation.Emulated
})
export class AddFromGalleryPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: any;
  @ViewChild('folderInput') folderInput!: any;
  @ViewChild('stopKeepBtn') stopKeepBtn?: any;
  compressedb64Image: any;
  compressed: any;
  selectedFiles: File[] = [];
  uploadedFiles: File[] = [];
  delayTimeInMilliseconds = 8000; // 8 seconds delay
  eventId: any;
  eventData: any;
  totalImages: any;
  eventDetail: any;
  isLoading = false;
  isFileLoading = false;
  /** Gates the header minimize button; see environment.features. */
  readonly uploadBannerEnabled = environment.features.persistentUploadBanner === true;
  isProcessing = false; // Track when files are processing in background
  isMinimized = false; // Track if dialog is minimized
  processingCompleted = 0; // Track completed files during processing
  processingFailed = 0; // Track failed files during processing
  processingRemaining = 0; // Track remaining files during processing
  /**
   * Set only when this dialog was reopened from the banner. The batch size the
   * snapshot recorded and how many files had landed: server rows alone give a
   * different number every time, because rows appear as chunks start.
   */
  batchTotal = 0;
  restoredLanded = 0;
  selectedFileNames: string[] = [];
  uploadedCheck: boolean[] = [];
  fileCount: any;
  count: any;
  userId: any;
  files: { name: string; progress: number }[] = [];
  uploadProgress: { [key: string]: number } = {};
  currentUploadIndex: number | null = null;
  isIOS: boolean = false;
  isAndroid: boolean = false;
  thumbnailUrls: string[] = [];
  fileThumbnails: (string | null)[] = [];
  //uploadRequests: ManagedUpload[] = [];
  progressPercentage: any;
  // private s3: AWS.S3;
  // private s3Client: S3Client;
  localFiles: string[] = [];
  estimatedTime: any;
  isSubfolderDialogOpen: boolean = false;
  subfolders: any = [];
  selectedSubfolder: string | null = null;
  newSubfolderName: string = '';
  private identityPoolId = environment.aws.IdentityPoolId;
  private readonly UPLOAD_STATE_KEY = 'picaggo_upload_state';
  private uploadState: {
    eventId: string;
    userId: string;
    files: { name: string; uploaded: boolean; size: number }[];
    totalSize: number;
    uploadedSize: number;
    lastUpdated: number;
  } | null = null;

  /** name -> the entry in uploadState.files, so per-file completion is O(1). */
  private uploadStateIndex: Map<string, { name: string; uploaded: boolean; size: number }> = new Map();

  /** Pending debounced write of uploadState, if one is queued. */
  private uploadStateSaveTimer: any = null;

  /**
   * How long a completed file may sit unpersisted.
   *
   * The cost of losing this window is bounded and small: a tab that dies
   * mid-upload resumes and re-uploads at most the handful of files finished
   * inside it, and re-uploading is idempotent — the same key overwrites the
   * same object. The cost of NOT having the window is a synchronous
   * serialize-and-write of the entire file list on every single completion.
   */
  private readonly UPLOAD_STATE_SAVE_DEBOUNCE = 2000;

  // AWS credential caching for optimized performance
  private s3Client: S3Client | null = null;
  private credentialsExpiryTime: number = 0;
  private isInitializingCredentials: boolean = false;
  private credentialsInitPromise: Promise<void> | null = null;

  // Modern upload dialog properties
  uploadComplete: boolean = false;
  fileUploadStatus: string[] = []; // 'pending', 'uploading', 'completed', 'error'
  successCount: number = 0;
  failedCount: number = 0;
  skippedDuplicates: number = 0;
  // Counters for summary
  originalSelectedCount: number = 0;
  attemptedCount: number = 0;
  isDragOver: boolean = false;
  existingFiles: Set<string> = new Set();
  existingMD5s: Set<string> = new Set();
  duplicateFiles: Map<string, string> = new Map(); // fileName -> existing fileName
  // Two different photos in one selection can share a name (e.g. IMG_0001.jpg
  // from two folders). They'd map to the same S3 key and silently overwrite
  // each other, so the 2nd+ gets a _1/_2 suffix. Resolved once per file so the
  // tracking record, the upload and any retry all use the same key.
  private uploadFileNames: Map<File, string> = new Map();
  private claimedUploadNames: Set<string> = new Set();
  private uploadNameToOriginal: Map<string, string> = new Map(); // S3 name -> original file.name
  showDuplicateWarning: boolean = false;
  failedFiles: Array<FailedFileEntry> = [];
  isRefreshingImages: boolean = false;
  isRetryingFailed: boolean = false;

  // Collapsible sections for upload review
  showSuccessSection: boolean = false;
  showFailedSection: boolean = false;
  showDuplicatesSection: boolean = false;

  // Preserve duplicate info across upload (so review can show names/sizes/thumbs after selectedFiles is modified)
  preservedDuplicates: Array<{ name: string; size: number; thumbnail?: string | null }> = [];

  // Flag to indicate duplicate checking is in progress
  isCheckingDuplicates: boolean = false;

  // Flag and message for file gathering progress
  isGatheringFiles: boolean = false;
  gatheringMessage: string = '';

  // Upload progress tracking
  uploadProgressMap: Map<string, UploadProgressInfo> = new Map();
  progressPollingIntervals: Map<string, any> = new Map();
  showDetails: Map<string, boolean> = new Map();
  private uploadProgressApiUrl = (environment as any).uploadProgressApi || 'https://gtt38vh7y4.execute-api.us-west-1.amazonaws.com/default/file-upload-progress';
  private bulkProgressInterval: any = null;
  /**
   * Guards against overlapping progress polls. setInterval does not await its
   * callback, so when the progress API slowed to ~18s responses a new request
   * still fired every 3s — six stacked in-flight requests competing with the
   * uploads themselves for the browser's ~6 connections per host.
   */
  private pollInFlight = false;
  private s3KeysToTrack: Set<string> = new Set();
  /**
   * Every S3 key in the current batch, never pruned. s3KeysToTrack is an
   * in-flight set — pollBulkProgress deletes from it the moment a file reaches
   * a terminal state — so it is the wrong thing to persist in a minimize
   * snapshot: by the time the user minimizes, the files that finished are
   * already gone from it, and a batch that has fully uploaded leaves an empty
   * key list behind. Restoring from that returned zero rows and emptied the
   * dialog. This set is what the snapshot records.
   */
  private batchS3Keys: Set<string> = new Set();
  /**
   * Every key initBulk wrote a tracking row for, whether or not its file ever
   * started. batchS3Keys only grows as files begin, so a batch whose window
   * died early would leave most of its rows with no key to correct them by.
   */
  private batchTrackedKeys: Set<string> = new Set();

  /**
   * Backoff between post-upload existence checks. A 404 on the first look is
   * not trusted — the ingest trigger can remove an object from the staging
   * prefix soon after it lands — so we look again across ~7s before calling a
   * file missing.
   */
  private readonly VERIFY_RETRY_DELAYS_MS = [1000, 2000, 4000];

  /** Consecutive unreadable checks before we stop trying for this session. */
  private readonly VERIFY_DENIAL_LIMIT = 3;
  private verificationDisabled = false;
  private consecutiveVerifyDenials = 0;

  /**
   * Files that uploaded without error but could not be confirmed in S3 —
   * neither a success nor a failure, and reported as its own outcome so the
   * user is never told something we don't actually know.
   */
  unverifiedFiles: Map<File, string> = new Map();

  /**
   * Files whose bytes could not be read at all before an upload was even
   * attempted — the `files_selected` vs `files_readable` gap this closes.
   * Distinct from `failedFiles`: this is caught up front, before spending a
   * network attempt on a file whose picker handle already went stale.
   */
  unreadableFiles: Set<string> = new Set();

  /**
   * Queued picks already found unreadable by `sweepQueuedForUnreadable`, so
   * their worker turn records them without an upload attempt. Cloud-only
   * photos come in bulk — a Google Photos pick of 190 where most were never
   * on the phone — and letting each one discover that alone cost two console
   * errors per file and left the host watching the count fail one by one.
   */
  private knownUnreadable = new Set<File>();
  private unreadableSweep: Promise<void> | null = null;

  /**
   * Picking-time readability check. On Android the picker hands over handles,
   * not copies, and the browser can later refuse to read one (a
   * NotReadableError). Why is not established: the logs show it on photos that
   * were readable moments earlier, and the browser only says "typically due to
   * permission problems". Photos stored only in Google Photos are one
   * suspected cause, not a measured one — which is why nothing the person reads
   * states it as fact. Reading one byte of every file right after picking
   * surfaces the ones already blocked while the person is still here, and the
   * upload then skips them instead of losing them silently.
   */
  isCheckingReadable = false;
  /**
   * Names whose thumbnail failed to decode on the result screens.
   *
   * The selection grid tracks this per index in thumbnailError[], but the
   * result and review lists iterate uploadedFiles and look the thumbnail up by
   * name, so they need their own record. Without it a file that has a blob URL
   * but cannot be decoded — HEIC on Android Chrome, which has no HEIC decoder —
   * renders a broken <img> while the placeholder stays hidden, because the
   * placeholder only checks whether a URL exists.
   */
  resultThumbError = new Set<string>();

  /** Names already probed in this selection, readable or not. */
  readableChecked: Set<string> = new Set();
  /** Names that failed the picking-time probe (usually cloud-only photos). */
  precheckUnreadable: Set<string> = new Set();
  isRetryingUnreadable = false;
  /** True once a probe has run for this selection; tags upload-time misses. */
  private precheckRan = false;
  /** Bumped when the selection is thrown away, so older probe runs stop writing. */
  private readableCheckGen = 0;
  /** Probe runs in flight; "Add More" mid-check starts a second one. */
  private activeReadChecks = 0;
  /** Picked files the upload skipped because the probe found them unreadable. */
  precheckLeftOut: File[] = [];

  /**
   * How many of the selected files were byte-identical to another file in the
   * same selection, and so were not uploaded. Reported separately from
   * "already in the event" duplicates because they mean something different to
   * the user.
   */
  duplicatesWithinSelection: number = 0;

  /** Files larger than this are not hashed for within-selection dedupe. */
  private readonly DEDUPE_MAX_FILE_BYTES = 512 * 1024 * 1024; // 512MB


  // ETA smoothing helpers
  private etaSecondsEma: number | null = null;
  private etaStartTs: number = 0;
  private etaMinSamples: number = 2;

  // Network speed monitoring
  private uploadSpeedSamples: number[] = []; // Track recent upload speeds in KB/s
  private lastUploadedBytes: number = 0;
  private lastSpeedCheckTime: number = 0;
  private slowNetworkAlertShown: boolean = false;
  currentUploadSpeed: string = '--';
  isSlowNetwork: boolean = false;
  slowNetworkMessage: string = ''; // Message to display in UI
  private readonly SLOW_NETWORK_THRESHOLD = 100; // KB/s - threshold for slow network
  private readonly SPEED_CHECK_INTERVAL = 5000; // Check speed every 5 seconds
  private speedMonitoringInterval: any = null;
  private totalUploadedBytes: number = 0;

  /**
   * Stalls. A connection that stops answering moves no bytes, and the speed
   * samples above cannot see that: a zero reading is discarded as "not a
   * sample", so the average stays frozen at the last good value. In a real run
   * the log read "708 KB/s, Slow: false" eleven times in a row while the parts
   * were hanging and failing, and the slow-network message never appeared.
   * Progress is therefore tracked directly: how long since any byte landed.
   */
  private lastProgressAt = 0;
  private stalledNow = false;
  private stallReported = false;
  private stallProbeInFlight = false;
  /**
   * Progress moves when a whole 5MB part lands, so on a weak link a healthy
   * upload can go a long time without moving: nine parts sharing 1.3 Mbps each
   * need minutes. Silence alone therefore cannot say "stopped" — after this long
   * the network is asked, and only that answer picks the message.
   */
  private readonly STALL_AFTER_MS = 45000;

  /**
   * Said instead of the slow-network line while uploads are paused waiting for
   * the connection to come back. Separate from slowNetworkMessage so one
   * clearing cannot wipe the other.
   */
  connectionNotice = '';
  /** One recovery wait shared by every worker, so three files do not probe three times. */
  private connectionRecovery: Promise<boolean> | null = null;
  /** Per file: how many outages it may wait out before it is given up on. */
  private readonly MAX_CONNECTION_WAITS = 4;

  /** The line under the progress card; the outage notice wins over the slow one. */
  get networkNotice(): string {
    return this.connectionNotice || this.slowNetworkMessage;
  }

  /** Shown before an upload starts. See upload-failure-copy.ts for why it says this. */
  readonly keepOpenHint = KEEP_PICAGGO_OPEN_HINT;
  readonly browseWhileUploading = BROWSE_WHILE_UPLOADING;
  readonly stopKeepLabel = STOP_UPLOAD_KEEP;
  readonly stopConfirmLabel = STOP_UPLOAD_CONFIRM;
  readonly stoppingTitle = STOPPING_TITLE;
  readonly stoppingNote = STOPPING_NOTE;

  /** ✕ pressed while photos are still being sent: the question is on screen. */
  confirmingStop = false;
  /**
   * The person stopped the upload. Files not yet started are recorded as
   * stopped instead of sent. The ones already on their way are left to
   * finish: abandoning a half-sent file saves almost nothing, and a multipart
   * upload dropped midway is exactly the orphan this component works to avoid.
   */
  stopRequested = false;
  /** Files the stop kept from being sent, for the result card and analytics. */
  stoppedCount = 0;

  /** When each file was picked, so a later failure can say how long it had been. */
  private pickedAt = new WeakMap<File, number>();
  /** Bumped when the selection is thrown away or the dialog goes, so a running read trace stops. */
  private readTraceGen = 0;
  /** When this run's upload began — lets the read trace say how long picking took. */
  private uploadStartedAt = 0;

  /**
   * Files the upload gave up on, waiting to be told to the server. Batched
   * because a bad connection fails a whole queue within a second or two.
   */
  private failureReportQueue: Array<{ s3_key: string; error_stage: string; error_message: string; retry_count: number }> = [];
  private failureReportTimer: any = null;

  // Session keepalive to prevent timeout during long uploads
  private keepaliveInterval: any = null;
  private readonly KEEPALIVE_INTERVAL_MS = 180000; // Refresh every 3 minutes (180000ms)
  private idleWasStopped: boolean = false; // Track if idle timer was stopped during upload

  // MD5 hashes for files
  fileMD5s: Map<string, string> = new Map();

  // Thumbnail error flags
  thumbnailError: boolean[] = [];

  // Unique upload session ID for this batch
  uploadSessionId: string = '';

  // Mobile detection
  isMobile: boolean = false;

  /**
   * On a computer the dialog is sized to the window, so the footer's Upload and
   * Cancel stay on screen and the content scrolls instead. The content alone was
   * capped at 80% of the window, and with the footer and its hint added the
   * dialog ran past the bottom of a short window (27px at 615px tall). Phones
   * keep their own sheet layout.
   */
  @HostBinding('class.upload-dialog--fit') get fitToWindow(): boolean {
    return !this.isMobile;
  }
  mobilePickerOpen: boolean = false;
  /** Legacy-browser picker fallback (see ngAfterViewInit); null when not armed. */
  private pickerFallbackTimer: any = null;

  // Storage quota validation
  storageQuotaExceeded: boolean = false;
  isCollaboratorStorageBlock: boolean = false;
  storageQuotaMessage: string = '';
  userStorageData: any = null;
  isCheckingStorage: boolean = false;

  // Web Worker for MD5 computation (Solution 4)
  private md5Worker: Worker | null = null;
  private md5WorkerBusy: boolean = false;

  // Chunked upload control (Solution 3)
  private readonly UPLOAD_CHUNK_SIZE = 500; // Process 500 files at a time
  private readonly CHUNK_DELAY_MS = 1000; // 1 second delay between chunks

  // Memory tracking
  private logMemoryUsage(context: string): void {
    if ('memory' in performance) {
      const mem = (performance as any).memory;
      const usedMB = (mem.usedJSHeapSize / 1048576).toFixed(2);
      const totalMB = (mem.totalJSHeapSize / 1048576).toFixed(2);
      const limitMB = (mem.jsHeapSizeLimit / 1048576).toFixed(2);
      const percentUsed = ((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100).toFixed(1);
      const message = `[MEMORY ${context}] Used: ${usedMB}MB / Total: ${totalMB}MB / Limit: ${limitMB}MB (${percentUsed}% used)`;
      this.loggingService.log(message);
      // console.log(message); //__CONSOLE_LOG__
    } else {
      const message = `[MEMORY ${context}] Performance.memory API not available (only works in Chrome/Edge)`;
      this.loggingService.log(message);
      // console.log(message); //__CONSOLE_LOG__
    }
  }

  // Progress polling pagination (Solution 5)
  private readonly PROGRESS_POLL_BATCH_SIZE = 100; // Poll 100 files at a time
  private progressPollOffset: number = 0;

  // Upload summary interface (Solution 8)
  uploadSummary: {
    totalFiles: number;
    uploadedFiles: number;
    failedFiles: number;
    totalSize: number;
    uploadedSize: number;
    currentSpeed: string;
    eta: string;
    completionPercentage: number;
  } = {
      totalFiles: 0,
      uploadedFiles: 0,
      failedFiles: 0,
      totalSize: 0,
      uploadedSize: 0,
      currentSpeed: '--',
      eta: '--',
      completionPercentage: 0
    };

  // Visible file range for lazy thumbnails (Solution 2)
  private visibleFileRange: { start: number; end: number } = { start: 0, end: 50 };

  // Helper to generate a unique session ID (timestamp + random)
  private generateUploadSessionId(): string {
    return 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).substr(2, 8);
  }

  // Session refresh to prevent timeout during long uploads
  private async refreshSession(): Promise<void> {
    try {
      const currentUser = await this.fbAuth.currentUser;
      if (currentUser) {
        // Refresh Firebase token to prevent expiration
        const newToken = await currentUser.getIdToken(true);
        this.loggingService.log("Firebase token refreshed - valid for another hour");

        // Check if Cognito credentials need refresh (5 min buffer)
        if (this.credentialsExpiryTime - Date.now() < 300000) {
          this.loggingService.log("Refreshing AWS credentials proactively during upload");
          await this.initializeAWSCredentials();
        }

        // Reset idle timer to prevent timeout during upload
        if (this.idle && this.isFileLoading) {
          this.idle.watch();
          this.loggingService.log("Idle timer reset - preventing timeout during upload");
        }

        this.loggingService.log("Session refreshed successfully - upload can continue");
      } else {
        this.loggingService.log("WARNING: No current user found during session refresh");
      }
    } catch (error) {
      this.loggingService.log("Session refresh error: " + error);
      // console.error("Session refresh failed:", error); //__CONSOLE_LOG__
    }
  }

  // Start session keepalive
  private startSessionKeepalive(): void {
    // Clear any existing interval
    this.stopSessionKeepalive();

    // Stop idle timeout to prevent logout during upload
    if (this.idle) {
      this.idle.stop();
      this.idleWasStopped = true;
      this.loggingService.log("Idle timeout stopped - preventing logout during upload");
    }

    // Initial refresh
    this.refreshSession();

    // Start periodic refresh (every 10 minutes for better protection)
    // Firebase tokens expire after 1 hour, so 6 refreshes per hour
    this.keepaliveInterval = setInterval(() => {
      this.refreshSession();
    }, 10 * 60 * 1000); // 10 minutes

    this.loggingService.log(`Session keepalive started - will refresh every 10 minutes`);
  }

  // Stop session keepalive
  private stopSessionKeepalive(): void {
    if (this.keepaliveInterval) {
      clearInterval(this.keepaliveInterval);
      this.keepaliveInterval = null;
      this.loggingService.log("Session keepalive stopped");
    }

    // Restart idle timer after upload completes
    if (this.idleWasStopped && this.idle) {
      this.idle.watch();
      this.idleWasStopped = false;
      this.loggingService.log("Idle timeout restarted - normal session management resumed");
    }
  }

  // Check if device is mobile
  private checkIfMobile(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
  }

  constructor(
    private ngZone: NgZone,
    public sanitizer: DomSanitizer,
    public dialogRef: MatDialogRef<AddFromGalleryPopupComponent>,
    private uploadService: UploadService,
    private eventService: EventService, public fbAuth: AngularFireAuth,
    public dialog: MatDialog, private loggingService: LogToS3Service,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private idle: Idle,
    private analytics: AnalyticsService,
    private toastService: ToastService,
    private uploadBanner: UploadBannerService,
    private guestService: GuestService,
    private guestHostPrompt: GuestHostPromptService,
    @Inject(MAT_DIALOG_DATA) public data: { eventId: any; userId: any; restoreProcessing?: boolean; processingData?: any; isHost?: boolean; }
  ) {
    this.eventId = data.eventId;
    this.userId = data.userId;

    // One surface instead of three. The shared `my-dialog-class` panel paints a
    // #fafafa background with 7px of padding *around* Material's own white
    // dialog surface, which renders as a grey rim — a sheet around a sheet.
    // Added here rather than changed in the shared class because ten other
    // dialogs use that class and rely on its framing.
    this.dialogRef.addPanelClass('upload-dialog-panel');

    // Set synchronously, here rather than in restoreProcessingState(): that
    // runs after `await loadExistingFiles()` in ngOnInit, so for the first
    // several hundred milliseconds isProcessing was false and the dialog
    // rendered the file picker / drop zone before flipping to processing.
    // Reopening from the banner flashed "add photos" at the user every time.
    if (data.restoreProcessing) {
      this.isProcessing = true;
    }

    // Validate and fix if incorrect values passed
    // localStorage 'e' = numeric eventId, 'eventId' = hex string
    if (!/^\d+$/.test(String(this.eventId ?? ''))) {
      this.loggingService.log(`Constructor: Invalid eventId received: ${data.eventId}, using localStorage 'e'`);
      this.eventId = SafeStorage.getItem('e');
    }

    if (!this.userId || this.userId === 'undefined') {
      // Recover before giving up. 'u' is written by event-detail on every
      // successful load and holds the very value that should have been passed
      // in, so a missing `data.userId` almost always means one API call hiccuped
      // — not that the user is logged out.
      const recoveredUserId = SafeStorage.getItem('u');

      if (recoveredUserId) {
        this.userId = recoveredUserId;
        this.loggingService.log(
          `Constructor: userId missing from dialog data; recovered '${recoveredUserId}' from storage`
        );
      } else {
        // Genuinely unresolvable. Two things were wrong with how this used to
        // read: it blamed the session, which is usually fine (Firebase auth is
        // untouched — only this one field is missing), and it used alert(),
        // which blocks the page, cannot be styled, and on mobile can be
        // suppressed entirely so the dialog just vanished with no explanation.
        this.loggingService.logError(
          'Upload dialog opened without a resolvable userId',
          { dataUserId: data.userId, dataEventId: data.eventId }
        );
        this.analytics.capture('upload_blocked', {
          reason: 'missing_user_id',
          category: 'not_ready',
          event_id: this.eventId,
          role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
        });
        this.toastService.showToast(
          'Could not start the upload — your account details did not load. Please try again.',
          5000
        );
        this.closeHandled = true;
        this.dialogRef.close();
        return;
      }
    }

    this.loggingService.log(`Upload dialog: eventId=${this.eventId}, userId=${this.userId}`);

    // Retrieve from localStorage
    let storedData = SafeStorage.getItem('event_subfolders');
    // Check if storedData is not null before parsing
    if (storedData) {
      const event_subfolders = JSON.parse(storedData); // Now TypeScript knows storedData is a string
      // console.log(event_subfolders); //__CONSOLE_LOG__
      this.subfolders = event_subfolders;
    } else {
      // console.log('No data found in localStorage.'); //__CONSOLE_LOG__
    }
    // console.log(this.subfolders); //__CONSOLE_LOG__

    this.loggingService.log("upload dialog opened:" + data);
    dialogRef.afterClosed().subscribe((result) => {
      //window.location.reload();
      //this.eventDetails.loadImages(false);
    });
    //aws
    // AWS.config.update({
    //   accessKeyId: environment.aws.accessKeyId,
    //   secretAccessKey: environment.aws.secretAccessKey,
    //   region: environment.aws.region,
    // });

    // this.s3 = new S3({
    //   httpOptions: { timeout: 300000 }, // 5 min timeout
    //   maxRetries: 3, // Retry 3 times on failures
    // });


    this.count = this.uploadedCheck.filter(
      (element) => element === true
    ).length;

    // Initialize MD5 Web Worker (Solution 4)
    this.initializeMD5Worker();
  }

  async ngOnInit() {
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.platform) ||
      (/MacIntel/.test(navigator.platform) && navigator.maxTouchPoints > 1); // iPadOS 13+
    this.isAndroid = /Android/.test(navigator.userAgent);
    this.isMobile = this.checkIfMobile();
    if (this.isMobile && !this.data?.restoreProcessing) {
      this.mobilePickerOpen = true;
    }
    // Load existing event files for duplicate detection
    await this.loadExistingFiles();
    // Check if we need to restore processing state
    if (this.data.restoreProcessing && this.data.processingData) {
      // The dialog is the detailed view of the same upload; leaving the strip
      // up behind the backdrop would just be the same progress twice.
      this.uploadBanner.hide();
      this.restoreProcessingState(this.data.processingData);
    }
  }

  ngAfterViewInit(): void {
    if (!this.isMobile || this.data?.restoreProcessing) return;

    const input = this.fileInput?.nativeElement;
    // Panel is already hidden via dialog-picker-pending (set at open time).
    // Just trigger the file picker — no delay needed for hiding.
    setTimeout(() => input?.click(), 0);

    // Closing the dialog is the input's 'cancel' event's job alone (bound in the
    // template): it fires when, and only when, the picker closed without a new
    // selection. Everything below is a safety net that may reveal this dialog
    // but must never close it.
    //
    // The old net closed the dialog if the input still had no files 300ms after
    // the page regained focus. On Android focus comes back BEFORE Chrome has
    // finished handing the picked files over, so picking several photos on a
    // busy page — e.g. right after a delete, which hard-reloads the gallery —
    // routinely lost that race: the dialog closed itself, the real 'change'
    // landed on a destroyed component, and the user was left looking at the
    // gallery with no popup and no explanation.
    //
    // Feature-detecting 'cancel' is not an option: `oncancel` is a global
    // handler that came in with <dialog>, so it is present on browsers that
    // never fire it for a file input. Hence a net that is safe either way.
    window.addEventListener('focus', this.onWindowFocusAfterPicker);
  }

  private onWindowFocusAfterPicker = () => {
    window.removeEventListener('focus', this.onWindowFocusAfterPicker);
    this.pickerFallbackTimer = setTimeout(() => {
      this.pickerFallbackTimer = null;
      if (!this.mobilePickerOpen) return;                        // 'change'/'cancel' already ran
      if (this.fileInput?.nativeElement?.files?.length) return;  // ...or 'change' is about to
      // Reveal instead of closing. If files are still on their way, 'change'
      // fills this dialog in a moment; if the picker was genuinely dismissed on
      // a browser with no 'cancel' event, the user gets the upload dialog's own
      // "Choose from Gallery" and close button rather than a hidden, undismissable
      // modal. Either way there is something on screen, which is the whole point.
      this.loggingService.log('Picker fallback: revealing upload dialog (no change/cancel seen)');
      this.mobilePickerOpen = false;
      this.dialogRef.removePanelClass('dialog-picker-pending');
      this.cdr.markForCheck();
    }, 1500);
  };

  onFilePickerCancelled(): void {
    if (!this.mobilePickerOpen) return;
    // A 'cancel' carrying files is not a cancel — never close on top of a
    // selection that is already in the input.
    if (this.fileInput?.nativeElement?.files?.length) return;
    this.clearPickerFallbackTimer();
    this.mobilePickerOpen = false;
    this.dialogRef.removePanelClass('dialog-picker-pending');
    this.closeHandled = true;
    this.dialogRef.close(null);
  }

  private clearPickerFallbackTimer(): void {
    if (this.pickerFallbackTimer) {
      clearTimeout(this.pickerFallbackTimer);
      this.pickerFallbackTimer = null;
    }
  }

  async loadExistingFiles(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.eventService.getEventFileNames(this.eventId).subscribe({
        next: (res: any) => {
          this.loggingService.log(`Response from getEventFileNames: ${JSON.stringify(res)}`);
          let response = res;
          if (response && Array.isArray(response)) {
            response.forEach((file: any) => {
              const fileName = file.filename || file.name || file;
              const normalizedName = this.normalizeFileName(fileName);
              this.existingFiles.add(normalizedName);
              if (file.md5) {
                this.existingMD5s.add(file.md5);
              }
              this.loggingService.log(`Added to existingFiles: ${fileName} (normalized: ${normalizedName})` + (file.md5 ? `, MD5: ${file.md5}` : ''));
            });
            this.loggingService.log(`Loaded ${this.existingFiles.size} existing files for duplicate detection`);
          }
          resolve();
        },
        error: (err: any) => {
          this.loggingService.log(`Error loading existing files: ${err}`);
          resolve(); // still resolve to not block
        }
      });
    });
  }
  /**
   * The file name this file will actually be stored under in S3.
   * Strips spaces/apostrophes like before, then appends _1, _2, ... if that
   * name is already taken by another file in this upload or by a file already
   * in the event. Cached per File so every caller agrees on the key.
   */
  private resolveUploadFileName(file: File): string {
    const cached = this.uploadFileNames.get(file);
    if (cached) return cached;

    const baseName = file.name.replace(/\s/g, "").replace("'", "");
    const dot = baseName.lastIndexOf('.');
    const stem = dot > 0 ? baseName.slice(0, dot) : baseName;
    const ext = dot > 0 ? baseName.slice(dot) : '';

    const isTaken = (name: string) =>
      this.claimedUploadNames.has(this.normalizeFileName(name)) ||
      this.existingFiles.has(this.normalizeFileName(name));

    let candidate = baseName;
    let suffix = 0;
    while (isTaken(candidate)) {
      suffix++;
      candidate = `${stem}_${suffix}${ext}`;
    }

    if (candidate !== baseName) {
      this.loggingService.log(`Name clash: ${file.name} will be uploaded as ${candidate}`);
    }

    this.claimedUploadNames.add(this.normalizeFileName(candidate));
    this.uploadFileNames.set(file, candidate);
    this.uploadNameToOriginal.set(candidate, file.name);
    return candidate;
  }

  /**
   * Guests upload under user id '0'.
   *
   * This has to be applied before the S3 key is built, not halfway through the
   * upload: the key we track, the key the backend records, and the key we
   * actually PUT to must be the same string. They previously diverged for
   * guests — tracking said `pwa/undefined/...` while the object landed at
   * `pwa/0/...` — which post-batch verification would report as a phantom miss
   * for every guest file.
   */
  /**
   * True when there is a usable user id to upload under. Tries storage before
   * giving up; guests upload under '0' by design (see resolveUploadUserId).
   * Otherwise says so, and leaves the picked files in place so a refresh or a
   * second try does not cost the person their selection.
   */
  private ensureUploadUserId(): boolean {
    const bad = (v: any) => v === undefined || v === null || v === '' || v === 'undefined' || v === 'null';
    if (!bad(this.userId)) {
      return true;
    }
    const recovered = SafeStorage.getItem('u');
    if (!bad(recovered)) {
      this.userId = recovered;
      this.loggingService.log(`Upload start: userId was missing; recovered '${recovered}' from storage`);
      return true;
    }
    if (SafeStorage.getItem('isGuestUser') === 'true') {
      return true;
    }

    this.loggingService.logError('Upload blocked: no user id at upload start', { userId: this.userId });
    this.analytics.capture('upload_blocked', {
      reason: 'missing_user_id_at_start',
      category: 'not_ready',
      event_id: this.eventId,
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });
    this.toastService.showToast(
      "Couldn't start the upload — your account details didn't load. Refresh the page and try again.",
      6000
    );
    return false;
  }

  /**
   * True when the event id is the numeric one. A hex code in the key is filed
   * by the backend as SQL and rejected, stranding the photo in S3 with no
   * gallery row, so refuse to upload rather than build such a key.
   */
  private ensureUploadEventId(): boolean {
    if (/^\d+$/.test(String(this.eventId ?? ''))) {
      return true;
    }
    const stored = SafeStorage.getItem('e');
    if (stored && /^\d+$/.test(stored)) {
      this.eventId = stored;
      return true;
    }
    this.loggingService.logError('Upload blocked: event id is not numeric', { eventId: this.eventId });
    this.analytics.capture('upload_blocked', {
      reason: 'bad_event_id',
      category: 'not_ready',
      source: 'upload_dialog',
      event_id: this.eventId,
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });
    this.toastService.showToast(
      "Couldn't start the upload — the event didn't load. Close this and reopen the event.",
      6000
    );
    return false;
  }

  /**
   * Every deliberate stop of an upload, in one shape. `reason` says exactly what
   * stopped it; `category` says whose situation it is, so a report can tell
   * "the plan is full" or "the photos live in the cloud" apart from a fault:
   *
   *   limit      storage plan, or an album paused for lack of room
   *   cloud      the picked photos are not on the device
   *   selection  nothing left to send once duplicates and bad files are removed
   *   not_ready  the page had not finished loading something the upload needs
   *   repeat     another tap on a batch that is already running
   *
   * None of these is an upload failing. A stuck-upload count should read them
   * as stops, not errors; mid-upload faults are `upload_failed` with a `cause`.
   */
  private captureUploadBlocked(
    reason: string,
    category: 'limit' | 'cloud' | 'selection' | 'not_ready' | 'repeat',
    extra: Record<string, unknown> = {}
  ): void {
    this.analytics.capture('upload_blocked', {
      reason,
      category,
      event_id: this.eventId,
      file_count: this.selectedFiles?.length ?? 0,
      network_online: navigator.onLine,
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
      ...extra,
    });
  }

  private resolveUploadUserId(userId: any): any {
    const bad = (v: any) => v === undefined || v === null || v === '' || v === 'undefined' || v === 'null';
    if (bad(userId) && SafeStorage.getItem('isGuestUser') === 'true') {
      return '0';
    }
    return userId;
  }

  /**
   * The S3 key a file will be (or was) uploaded to.
   *
   * Single source of truth for the upload itself, the backend tracking record
   * and the post-batch verification. `resolveUploadFileName` is memoised per
   * File, so repeated calls return the same (possibly suffixed) name.
   */
  /**
   * Content type to store the object with.
   *
   * `file.type` is empty for formats the browser doesn't recognise — notably
   * HEIC/HEIF on Windows, which is why a whole upload of `.heic` files landed
   * with `application/octet-stream` while the same files from another client
   * recorded `image/heic`. Falling back to the extension keeps the stored type
   * meaningful, which matters for anything serving the object directly.
   */
  private resolveContentType(file: File): string {
    if (file.type) return file.type;
    return this.contentTypeFromExtension(file.name) || 'application/octet-stream';
  }

  private contentTypeFromExtension(fileName: string): string | undefined {
    const ext = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
    const byExtension: { [k: string]: string } = {
      heic: 'image/heic', heif: 'image/heif',
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
      gif: 'image/gif', webp: 'image/webp', avif: 'image/avif',
      mp4: 'video/mp4', mov: 'video/quicktime', m4v: 'video/x-m4v',
      avi: 'video/x-msvideo', mkv: 'video/x-matroska', webm: 'video/webm',
      '3gp': 'video/3gpp'
    };
    return byExtension[ext];
  }

  /**
   * Whether a picked or dropped file is a photo or video. The picker's accept
   * list is only the dialog's default view (on desktop anyone can switch it to
   * "All files"), and Add Folder and drag-and-drop have no filter at all, so a
   * text file used to upload, show as a black tile and count as a photo.
   * A file with no extension and no type still goes through: Android's Google
   * Photos picks can arrive like that before they finish loading.
   */
  private isPhotoOrVideo(file: File): boolean {
    const type = (file.type || '').toLowerCase();
    if (type.startsWith('image/') || type.startsWith('video/')) return true;
    if (type && type !== 'application/octet-stream') return false;
    // .DS_Store and other hidden files a folder brings along.
    if (file.name.startsWith('.')) return false;
    if (file.name.lastIndexOf('.') <= 0) return true;
    return !!this.contentTypeFromExtension(file.name);
  }

  private reportSkippedFiles(count: number): void {
    if (count === 0) return;
    this.toastService.showToast(
      `${count} ${count === 1 ? "file wasn't" : "files weren't"} added — only photos and videos can go in the album.`
    );
  }

  private buildS3Key(userId: any, eventId: any, file: File): string {
    const uid = this.resolveUploadUserId(userId);
    return `${environment.aws.uploadFolder}/${uid}/events/${eventId}/${this.resolveUploadFileName(file)}`;
  }

  normalizeFileName(fileName: string): string {
    // Normalize the same way as upload: remove spaces and apostrophes, lowercase
    return fileName.replace(/\s/g, "").replace("'", "").toLowerCase();
  }

  // Use incremental/chunked MD5 computation with SparkMD5 to avoid loading
  // entire large files into memory at once.
  private async computeMD5(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const chunkSize = 4 * 1024 * 1024; // 4MB per chunk
        const chunks = Math.ceil(file.size / chunkSize);
        let currentChunk = 0;
        const spark = new (SparkMD5 as any).ArrayBuffer();
        const fileReader = new FileReader();

        fileReader.onerror = (err) => {
          fileReader.abort();
          reject(err);
        };

        fileReader.onload = (e: any) => {
          const arrayBuffer = e.target.result as ArrayBuffer;
          spark.append(arrayBuffer);
          currentChunk++;
          if (currentChunk < chunks) {
            loadNext();
          } else {
            const md5 = spark.end();
            resolve(md5);
          }
        };

        const loadNext = () => {
          const start = currentChunk * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const blob = file.slice(start, end);
          fileReader.readAsArrayBuffer(blob);
        };

        loadNext();
      } catch (err) {
        reject(err);
      }
    });
  }

  onSubfolderChange(event: any) {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedSubfolder = value === "" ? null : value;
    // console.log("Selected subfolder:", this.selectedSubfolder); //__CONSOLE_LOG__
  }

  triggerAndroidFilePicker() {
    // For Android, we want to guide users to use device storage only
    // The file input will still open, but with specific MIME types that prefer local files
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onAddFolderClick() {
    // Open folder selection dialog
    if (this.folderInput && this.folderInput.nativeElement) {
      this.folderInput.nativeElement.click();
    }
  }

  async onFolderSelected(event: any) {
    // console.log('onFolderSelected called', event); //__CONSOLE_LOG__
    try {
      const files = event.target.files;
      // console.log('files from folder:', files); //__CONSOLE_LOG__

      if (!files || files.length === 0) {
        this.loggingService.log("No files selected from folder");
        // console.log("No files selected from folder"); //__CONSOLE_LOG__
        return;
      }

      await this.processPickedFiles(files, 'folder');

      // Reset folder input value to allow re-selection
      event.target.value = '';
      // console.log('Folder input value reset for re-selection'); //__CONSOLE_LOG__
    } catch (error) {
      this.loggingService.log(`Error in onFolderSelected: ${error}`);
      // console.error("Error selecting folder:", error); //__CONSOLE_LOG__
    }
  }

  // --- Queued behind another upload in this window ---------------------------

  /** True while this upload waits for another one in this window to finish. */
  waitingForOtherUpload = false;
  readonly queuedTitle = UPLOAD_QUEUED_TITLE;
  readonly queuedNote = UPLOAD_QUEUED_NOTE;
  private cancelQueue: (() => void) | null = null;

  /**
   * Resolves true when the other upload has ended and this one may start, or
   * false when this one was called off first (✕, or the dialog closed). A
   * queued upload lives only while its dialog is open: nothing has been sent
   * yet, so there is nothing for the banner to carry, and writing a snapshot
   * for it would overwrite the one the running upload's banner reads.
   */
  private waitForOtherUpload(): Promise<boolean> {
    this.waitingForOtherUpload = true;
    const queuedAt = Date.now();
    this.analytics.capture('upload_queued', {
      event_id: this.eventId,
      files_count: this.selectedFiles.length,
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });
    return new Promise<boolean>((resolve) => {
      const finish = (started: boolean) => {
        this.cancelQueue = null;
        this.waitingForOtherUpload = false;
        if (!started) {
          this.isFileLoading = false;
        }
        this.analytics.capture(started ? 'upload_queue_started' : 'upload_queue_cancelled', {
          event_id: this.eventId,
          waited_ms: Date.now() - queuedAt,
        });
        resolve(started);
      };
      const removeWaiter = this.uploadBanner.onEngineIdle(() => this.ngZone.run(() => finish(true)));
      // onEngineIdle starts at once when the other upload ended a moment ago.
      if (this.waitingForOtherUpload) {
        this.cancelQueue = () => {
          removeWaiter();
          finish(false);
        };
      }
    });
  }

  // --- ✕ while photos are still being sent -----------------------------------

  /**
   * failedFiles.length when this run's batch loop began. The list is never
   * reset between runs and also holds files validation rejected, none of which
   * are in selectedFiles; counting them made "still to send" come out short.
   */
  private failedBeforeLoop = 0;

  /** Photos in this run that have neither landed nor failed yet. */
  get photosNotYetSent(): number {
    const failedThisRun = Math.max(0, this.failedFiles.length - this.failedBeforeLoop);
    return Math.max(0, (this.selectedFiles?.length || 0) - this.count - failedThisRun);
  }

  get stopTitle(): string {
    return stopUploadTitle(this.photosNotYetSent);
  }

  get stopBody(): string {
    return stopUploadBody(this.copyDevice, this.viewerIsHost);
  }

  /**
   * "Keep uploading" after ✕. They pressed ✕ to get the dialog out of the way,
   * so it goes, and the banner carries the progress. Without the banner there
   * would be nothing on screen showing the upload, so the dialog stays.
   */
  keepUploadingFromClose(): void {
    this.confirmingStop = false;
    if (this.uploadBannerEnabled && this.isFileLoading) {
      this.minimizeDialog('close_button');
    }
  }

  /**
   * "Stop uploading". Nothing new starts from here: the workers record each
   * remaining file as stopped instead of sending it, and the photos already on
   * their way finish. The run then ends on the normal path, so the result card,
   * the server rows and upload_completed all account for every file.
   */
  stopUpload(from: 'dialog' | 'banner' = 'dialog'): boolean {
    this.confirmingStop = false;
    if (!this.isFileLoading || this.stopRequested) {
      return false;
    }
    this.stopRequested = true;
    this.loggingService.log(`Upload stopped by the person (${from}): ${this.photosNotYetSent} file(s) not yet sent will be skipped`);
    this.analytics.capture('upload_stopped_by_user', {
      event_id: this.eventId,
      uploaded_count: this.count,
      remaining: this.photosNotYetSent,
      from,
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });
    return true;
  }

  /**
   * A file the stop kept from being sent. Recorded like any other miss, as kind
   * 'aborted' (worded "cancelled before finishing" on the result card), so the
   * counts add up. No upload_failed per file: stopping on purpose is not a
   * failure, and the stop itself was reported once, in upload_stopped_by_user.
   */
  private recordStoppedFile(file: File): void {
    const error = 'Not sent: the upload was stopped.';
    const progress = this.uploadProgressMap.get(file.name);
    if (progress) {
      progress.status = 'failed';
      progress.error_message = error;
    }
    this.failedFiles.push({ file, error, retries: 0, kind: 'aborted', retryable: false, stopped: true });
    this.failedCount++;
    this.stoppedCount++;
    // A file whose batch has started already has a tracking row at 'uploading',
    // and nothing else would ever move it. Files of batches never begun have
    // no row, and reportFailureToBackend skips those itself. No phase: the
    // file never reached one, so the server records the stage as 'aborted'.
    this.reportFailureToBackend(file, { kind: 'aborted', retryable: false } as DiagnosedFailure, error, 0);
  }

  closeDialog(files: any): void {
    // Files are still actively being sent to S3 (not just background
    // processing, which minimizeDialog/the bottom button already treat as
    // safe to leave). Closing here would orphan the upload with no visible
    // failure — exactly what happened to the user whose replay showed them
    // closing the X button mid-upload with no warning. Offer to keep it
    // running in the background instead of silently abandoning it.
    // Still queued behind another upload: nothing has been sent, so ✕ simply
    // calls this one off and closes.
    if (this.waitingForOtherUpload) {
      this.cancelQueue?.();
      this.closeHandled = true;
      this.dialogRef.close({ files, shouldRefresh: false });
      return;
    }

    if (this.isFileLoading) {
      // Already stopping: nothing is left to decide, and the photos already on
      // their way finish either way.
      if (this.stopRequested) {
        if (this.uploadBannerEnabled) {
          this.minimizeDialog('close_button');
        }
        return;
      }

      // ✕ used to minimise on the spot with "track it at the bottom of the
      // screen", which read as "it carries on without me". It only carries on
      // while Picaggo is open: of uploads closed that way over 30 days, 15 of
      // 26 never finished, against 24 of 82 minimised with the button. So ✕
      // asks, says what keeps the upload going, and offers a real stop. In the
      // dialog, not a browser confirm(): that one is unstyleable, and mobile
      // can suppress it, which made ✕ look broken.
      this.confirmingStop = true;
      // Focus follows the question, for keyboard and screen-reader users.
      setTimeout(() => this.stopKeepBtn?.nativeElement?.focus());
      this.analytics.capture('upload_close_attempted', {
        event_id: this.eventId,
        phase: 'uploading',
        remaining: this.photosNotYetSent,
        from: 'dialog',
        surface: this.isMobile ? 'mobile' : 'desktop',
        role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
      });
      return;
    }

    // Stop speed monitoring when dialog closes
    this.stopSpeedMonitoring();

    if (this.uploadComplete && this.uploadedFiles.length > 0) {
      this.handProcessingToBanner();
    }

    this.closeHandled = true;
    this.analytics.capture('upload_dialog_closed', {
      event_id: this.eventId,
      phase: this.uploadComplete ? 'complete' : 'idle',
      files_selected: this.selectedFiles?.length || 0,
      uploaded_count: this.count,
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });

    // Return object with files and refresh flag to indicate parent should refresh
    this.dialogRef.close({
      files: files,
      shouldRefresh: this.uploadComplete && this.uploadedFiles.length > 0
    });
    //window.location.reload();
  }

  /**
   * Done, while the server is still turning some of the photos into gallery
   * photos. The page reloads after Done and showed only the ones already ready
   * (34 of 55 in testing), the rest turning up over the next two minutes with
   * nothing saying more were coming. The banner already handles exactly this
   * for a minimized upload: "Getting <album> ready · 34 of 55 ready", then
   * "Your photos are in" and one more gallery refresh. Done now hands over to it
   * the same way, with the same snapshot.
   */
  private handProcessingToBanner(): void {
    if (!this.uploadBannerEnabled || this.isMinimized) {
      return;
    }
    // Only the files that went up: the server knows nothing of the rest, and
    // counting them would leave the strip waiting on photos that never come.
    const keys = Array.from(this.batchS3Keys);
    if (!keys.length) {
      return;
    }
    let pending = 0;
    this.uploadProgressMap.forEach((p) => {
      const done = p.status === 'completed' || p.progress_percentage === 100 || p.stage === 'completed';
      const failed = p.status === 'failed' || !!p.error_stage || p.stage === 'failed';
      if (!done && !failed) {
        pending++;
      }
    });
    // Everything already ready: the reload after Done shows it all.
    if (pending === 0) {
      return;
    }
    SafeStorage.setItem('uploadProcessingState', JSON.stringify({
      isProcessing: true,
      filesCount: keys.length,
      uploadedCount: keys.length,
      eventId: this.eventId,
      eventHex: this.albumHex(),
      returnUrl: this.router.url,
      eventName: SafeStorage.getItem('event_name') || '',
      isHost: this.viewerIsHost,
      timestamp: Date.now(),
      completed: this.processingCompleted,
      failed: this.processingFailed,
      remaining: pending,
      s3Keys: keys,
      allS3Keys: Array.from(this.batchTrackedKeys),
    }));
    this.analytics.capture('upload_processing_handed_to_banner', {
      event_id: this.eventId,
      files_count: keys.length,
      still_processing: pending,
      surface: this.isMobile ? 'mobile' : 'desktop',
    });
    this.uploadBanner.refresh();
  }

  navigateToSubscription(): void {
    // Close dialog and navigate to subscription page
    this.closeHandled = true;
    this.analytics.capture('upload_dialog_closed', {
      event_id: this.eventId,
      phase: 'idle',
      reason: 'subscription_upgrade',
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });
    this.dialogRef.close();
    this.router.navigate(['/choose-subscription']);
  }

  /**
   * Set right before every deliberate dialogRef.close() (here, closeDialog(),
   * navigateToSubscription()). ngOnDestroy checks it to tell "the user pressed
   * a button" apart from "the dialog was torn down out from under them" — the
   * latter is what happens on browser back / any router navigation, since
   * app.component calls MatDialog.closeAll() on every NavigationStart, and
   * previously left no trace at all: an abandoned mid-upload dialog and a
   * cleanly closed one were indistinguishable in the data.
   */
  private closeHandled = false;

  minimizeDialog(trigger: 'minimize_button' | 'close_button' = 'minimize_button'): void {
    // Store processing state in localStorage
    // Covers isFileLoading too — s3KeysToTrack/uploadProgressMap are already
    // populated per-file as soon as each upload starts (see uploadFile()),
    // so there's nothing isProcessing-specific about what's saved below.
    // Without this, minimizing (or closeDialog's keep-going path) during the
    // active upload phase silently did nothing — the dialog just stayed open.
    if (this.isProcessing || this.isFileLoading) {
      // console.log('Minimizing dialog. uploadProgressMap size:', this.uploadProgressMap.size); //__CONSOLE_LOG__

      // Extract tracking IDs and s3 keys from uploadProgressMap
      const trackingIds: number[] = [];
      const s3Keys: string[] = [];

      this.uploadProgressMap.forEach((progress, fileName) => {
        // console.log(`File: ${fileName}, tracking_id: ${progress.tracking_id}`); //__CONSOLE_LOG__
        if (progress.tracking_id) {
          trackingIds.push(progress.tracking_id);
        }
      });

      // Every key in the batch, not just the ones still in flight. Using
      // s3KeysToTrack here meant a minimize after the last file finished
      // uploading persisted an empty list, and reopening the dialog restored
      // nothing — a blank processing block, and "0 of 0" on the next minimize.
      this.batchS3Keys.forEach((key) => {
        s3Keys.push(key);
      });

      // console.log('Extracted tracking IDs:', trackingIds); //__CONSOLE_LOG__
      // console.log('Extracted s3 keys:', s3Keys); //__CONSOLE_LOG__

      // Calculate total size
      const totalSize = this.selectedFiles.reduce((acc, file) => acc + file.size, 0);

      // A dialog reopened from the banner knows less than the snapshot it was
      // built from (rows, not the picked list), and the original upload loop may
      // have moved on since. Never write the batch smaller or less done than
      // what is already recorded, or the counts change on every reopen.
      let prior: any = null;
      if (this.batchTotal > 0) {
        try { prior = JSON.parse(SafeStorage.getItem('uploadProcessingState') || 'null'); } catch { prior = null; }
        if (prior && String(prior.eventId ?? '') !== String(this.eventId ?? '')) {
          prior = null;
        }
      }

      const processingState = {
        isProcessing: true,
        filesCount: Math.max(this.selectedFiles.length, this.batchTotal, prior?.filesCount || 0),
        uploadedCount: Math.max(this.count, this.restoredLanded, prior?.uploadedCount || 0),
        eventId: this.eventId,
        eventHex: this.albumHex(),
        // Read by the app-level upload banner: where to send the user back to,
        // and what to call the album while they are somewhere else entirely.
        returnUrl: this.router.url,
        eventName: SafeStorage.getItem('event_name') || '',
        isHost: this.viewerIsHost,
        timestamp: Date.now(),
        completed: this.processingCompleted,
        failed: this.processingFailed,
        remaining: this.processingRemaining,
        trackingIds: trackingIds,
        s3Keys: s3Keys,
        allS3Keys: Array.from(this.batchTrackedKeys),
        totalSize: totalSize
      };
      // console.log('Minimizing dialog, storing state with tracking IDs:', processingState); //__CONSOLE_LOG__
      SafeStorage.setItem('uploadProcessingState', JSON.stringify(processingState));

      // Leaving this screen is the moment the page is most likely to be lost
      // before it ever flushes: in a real session the log went missing for 54s
      // from the minimize onwards, the stretch that mattered. Send what exists.
      void this.loggingService.uploadLogsToS3();

      // We have no data at all on whether anyone uses minimize — it shipped
      // without an event and event-detail never even rendered the indicator it
      // wrote state for. This is what decides whether the persistent banner
      // (and the upload-engine extraction behind it) is worth building out.
      this.analytics.capture('upload_minimized', {
        event_id: this.eventId,
        // Which phase they walked away from: mid-upload is the case that
        // actually needs an engine outside the dialog.
        phase: this.isFileLoading ? 'uploading' : 'processing',
        // Whether they pressed a dedicated "Continue in background" affordance
        // or pressed ✕ mid-upload and were converted into a minimize by
        // closeDialog() — these are different intents wearing the same event.
        trigger,
        files_count: this.selectedFiles.length,
        uploaded_count: this.count,
        remaining: this.processingRemaining,
        surface: this.isMobile ? 'mobile' : 'desktop',
        role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
      });

      // Show the banner immediately rather than on the next navigation — the
      // user has just chosen to leave this dialog and needs to see the upload
      // did not vanish with it.
      this.isMinimized = true;
      this.uploadBanner.refresh();
      // Seed it with what we already know, so the strip opens on the real
      // count instead of waiting up to 5s for the first poll.
      this.syncMinimizedProgress();

      // Close dialog with minimize flag
      this.closeHandled = true;
      this.dialogRef.close({
        minimized: true,
        shouldRefresh: false
      });
    } else {
      // console.log('Cannot minimize - not in processing state. isProcessing:', this.isProcessing); //__CONSOLE_LOG__
    }
  }

  /**
   * Stands in for the real file list when the progress API cannot supply it, so
   * a restored dialog still shows the size of the batch instead of nothing.
   * Also keeps the minimize snapshot honest: filesCount is read straight off
   * selectedFiles.length, so an empty list here is what produced "0 of 0" on
   * the next minimize.
   */
  /**
   * The album's hex id, for the banner to navigate back with. `eventId` here is
   * the numeric id; the event page is keyed on localStorage('eventId'), the hex.
   * A snapshot that already has one keeps it: a dialog reopened from another
   * screen would otherwise record whichever album that screen happens to hold.
   */
  private albumHex(): string | undefined {
    try {
      const existing = JSON.parse(SafeStorage.getItem('uploadProcessingState') || 'null');
      if (existing?.eventHex && String(existing.eventId ?? '') === String(this.eventId ?? '')) {
        return existing.eventHex;
      }
    } catch { /* fall through */ }
    return this.batchTotal > 0 ? undefined : (SafeStorage.getItem('eventId') || undefined);
  }

  /** Rows that have left 'uploading' without failing: transferred, whether or not processed. */
  private countLanded(): number {
    let landed = 0;
    this.uploadProgressMap.forEach((p) => {
      const failed = p.status === 'failed' || !!p.error_stage || p.stage === 'failed';
      if (!failed && p.status && p.status !== 'uploading') {
        landed++;
      } else if (!failed && (p.progress_percentage === 100 || p.stage === 'completed')) {
        landed++;
      }
    });
    return landed;
  }

  private ensurePlaceholderFiles(processingData: any): void {
    if (this.selectedFiles && this.selectedFiles.length > 0) {
      return;
    }
    // Take the largest count any source can vouch for. A snapshot written by a
    // cycle that had already gone wrong carries filesCount: 0, and trusting it
    // alone reproduces the blank panel forever — the empty list writes 0 back
    // to the snapshot on the next minimize, so the bad state is self-sustaining
    // in localStorage until something breaks the loop.
    const filesCount = Math.max(
      processingData?.filesCount || 0,
      processingData?.s3Keys?.length || 0,
      this.processingCompleted + this.processingFailed + this.processingRemaining
    );
    this.selectedFiles = Array(filesCount).fill(null).map((_, i) => ({
      name: `File ${i + 1}`,
      size: 0,
      type: 'application/octet-stream',
      lastModified: Date.now()
    } as File));
  }

  /**
   * The batch behind this snapshot can no longer be tracked. Tear the snapshot
   * down rather than leaving a dialog (and a banner) parked at 0% with nothing
   * capable of moving them — and clear storage so the state cannot be reopened
   * on the next tap, which is how this became sticky across reloads.
   */
  private expireRestoredBatch(processingData: any, reason: string): void {
    this.loggingService.logError(
      `Discarding untrackable upload snapshot (${reason})`,
      { processingData }
    );
    this.analytics.capture('upload_restore_expired', {
      event_id: this.eventId,
      reason,
      files_count: processingData?.filesCount ?? null,
      s3_keys: processingData?.s3Keys?.length ?? 0,
      // How stale the snapshot was when we gave up on it.
      age_seconds: processingData?.timestamp
        ? Math.round((Date.now() - processingData.timestamp) / 1000)
        : null,
    });

    SafeStorage.removeItem('uploadProcessingState');
    this.uploadBanner.hide();
    this.isProcessing = false;
    this.stopBulkProgressPolling();
    this.toastService.showToast(
      `That upload has already finished or expired. Refresh your ${this.albumLabel} to see the photos.`,
      4000
    );
    this.closeHandled = true;
    this.dialogRef.close({ shouldRefresh: true });
  }

  /**
   * Keeps the minimize snapshot — and the banner — in step with the upload as
   * it happens.
   *
   * The upload loop outlives this component: minimizing destroys the dialog,
   * but the in-flight async work carries on and is what actually finishes the
   * transfer. Before this, the banner's only source was the progress API, so
   * every poll failure (silently caught) looked identical to an upload making
   * no progress. Local counts are immediate, free, and cannot fail.
   */
  /**
   * Drops the "this batch has already refreshed the gallery" markers written by
   * event-detail. Paired with the start of an upload run, so the guard is
   * per-batch rather than per-session.
   */
  private clearUploadRefreshMarkers(): void {
    try {
      const stale: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('uploadRefreshed:')) {
          stale.push(key);
        }
      }
      stale.forEach(key => sessionStorage.removeItem(key));
    } catch {
      // sessionStorage can throw in private mode; the guard degrades to
      // in-memory only, which is the behaviour before it existed.
    }
  }

  private syncMinimizedProgress(): void {
    if (!this.isMinimized || !this.uploadBannerEnabled) {
      return;
    }
    try {
      const raw = SafeStorage.getItem('uploadProcessingState');
      if (!raw) {
        return;
      }
      const state = JSON.parse(raw);
      state.uploadedCount = Math.max(this.count, this.restoredLanded, state.uploadedCount || 0);
      state.failed = this.failedFiles.length;
      state.filesCount = Math.max(this.selectedFiles?.length || 0, this.batchTotal, state.filesCount || 0);
      // Keys accumulate as files start, so a snapshot taken early would
      // otherwise hold only the first few.
      state.s3Keys = Array.from(this.batchS3Keys);
      if (this.batchTrackedKeys.size) {
        state.allS3Keys = Array.from(this.batchTrackedKeys);
      }
      state.timestamp = Date.now();
      SafeStorage.setItem('uploadProcessingState', JSON.stringify(state));

      this.uploadBanner.reportUploadProgress(
        state.uploadedCount,
        this.failedFiles.length,
        state.filesCount
      );
    } catch {
      // Progress reporting must never be able to break an upload.
    }
  }

  async restoreProcessingState(processingData: any): Promise<void> {
    // console.log('Restoring processing state:', processingData); //__CONSOLE_LOG__

    // Restore the processing state
    this.isProcessing = true;
    this.uploadComplete = false;
    this.isFileLoading = false;
    this.count = processingData.uploadedCount || 0;
    this.batchTotal = processingData.filesCount || 0;
    this.restoredLanded = Math.min(processingData.uploadedCount || 0, this.batchTotal || Infinity);

    // Initialize processing counters from saved data
    this.processingCompleted = processingData.completed || 0;
    this.processingFailed = processingData.failed || 0;
    this.processingRemaining = processingData.remaining || processingData.filesCount || 0;

    // Carry the batch's keys across the restore. batchS3Keys is only filled by
    // uploadFile(), which does not run on this path, so without this a second
    // minimize would persist an empty key list and the banner would have
    // nothing left to poll — the same failure, one round later.
    if (processingData.s3Keys?.length) {
      processingData.s3Keys.forEach((key: string) => this.batchS3Keys.add(key));
    }

    // Fetch actual upload progress data from backend using s3 keys
    if (processingData.s3Keys && processingData.s3Keys.length > 0) {
      try {
        // console.log('Fetching upload progress for s3 keys:', processingData.s3Keys); //__CONSOLE_LOG__

        // Fetch progress for all s3 keys using the existing getBulk API
        const response: any = await this.http.post(`${this.uploadProgressApiUrl}?action=getBulk`, {
          keys: processingData.s3Keys
        }).toPromise();

        // `.length` matters as much as the null check: a successful call that
        // simply knows about none of these keys used to fall straight through
        // the block below and assign selectedFiles = [], wiping the dialog to a
        // blank processing panel. The catch-branch fallback never fired because
        // nothing had thrown. An empty answer now takes the placeholder path.
        if (response && response.uploads && response.uploads.length > 0) {
          // console.log(`Fetched ${response.uploads.length} upload records`); //__CONSOLE_LOG__

          // Clear existing upload progress map
          this.uploadProgressMap.clear();
          this.s3KeysToTrack.clear();

          // Populate uploadProgressMap and selectedFiles with actual data
          const actualFiles: File[] = [];

          response.uploads.forEach((upload: any) => {
            // Create progress info
            const progressInfo: UploadProgressInfo = {
              tracking_id: upload.id,
              file_name: upload.file_name,
              original_file_name: upload.original_file_name,
              status: upload.status,
              progress_percentage: upload.progress_percentage,
              stage: upload.stage,
              error_message: upload.error_message,
              error_stage: upload.error_stage,
              retry_count: upload.retry_count
            };

            // Use original_file_name for the map key to match with selectedFiles
            this.uploadProgressMap.set(upload.original_file_name, progressInfo);

            // Add to tracking set if still processing
            if (upload.status === 'processing' || upload.status === 'uploading') {
              this.s3KeysToTrack.add(upload.s3_key);
            }
            // Terminal or not, the key stays in the batch record.
            if (upload.s3_key) {
              this.batchS3Keys.add(upload.s3_key);
            }

            // Create a File object for display
            const fileObj = {
              name: upload.original_file_name,
              size: upload.file_size || 0,
              type: upload.mime_type || 'application/octet-stream',
              lastModified: Date.now()
            } as File;

            actualFiles.push(fileObj);

            // Set details expanded by default
            this.showDetails.set(upload.original_file_name, true);
          });

          // Update selectedFiles with actual file data
          this.selectedFiles = actualFiles;

          // Recalculate counters from actual data
          let completedCount = 0;
          let failedCount = 0;
          let processingCount = 0;

          this.uploadProgressMap.forEach((progress) => {
            const isCompleted = progress.status === 'completed' || progress.progress_percentage === 100 || progress.stage === 'completed';
            const isFailed = progress.status === 'failed' || !!progress.error_stage || progress.stage === 'failed';
            if (isCompleted) {
              completedCount++;
            } else if (isFailed) {
              failedCount++;
            } else {
              processingCount++;
            }
          });

          this.batchTotal = Math.max(this.batchTotal, actualFiles.length);
          this.processingCompleted = completedCount;
          this.processingFailed = failedCount;
          // Files whose rows do not exist yet are still to come, not missing.
          this.processingRemaining = Math.max(this.batchTotal - completedCount - failedCount, processingCount);
          this.restoredLanded = Math.max(this.restoredLanded, this.countLanded());

          // console.log(`Restored actual data: ${completedCount} completed, ${failedCount} failed, ${processingCount} processing`); //__CONSOLE_LOG__

          // Start bulk polling if there are files to track
          if (this.s3KeysToTrack.size > 0) {
            this.startBulkProgressPolling();
          }
        } else {
          // A successful call that knows about none of these keys means the
          // batch is not merely quiet, it is gone. initBulk() writes a progress
          // row for every file, with its s3_key, BEFORE any upload starts — so
          // there is no window in which an in-flight batch has zero rows.
          // Showing placeholders here is what produced a dialog stuck on
          // "0 of 10 Files, 0 B" forever with nothing left to advance it.
          this.expireRestoredBatch(processingData, 'no_progress_rows');
          return;
        }
      } catch (error) {
        // A network failure IS transient, unlike the empty response above, so
        // this one keeps the batch and shows what it can.
        // console.error('Error fetching upload progress on restore:', error); //__CONSOLE_LOG__
        this.loggingService.log(`Error fetching upload progress: ${error}`);
        this.ensurePlaceholderFiles(processingData);
      }
    } else {
      // No s3 keys at all — an older snapshot format. Placeholders are the best
      // available answer; there is nothing to query.
      this.ensurePlaceholderFiles(processingData);
    }

    // console.log(`Restored: ${this.processingRemaining} files remaining, ${this.processingCompleted} completed, selectedFiles: ${this.selectedFiles.length}`); //__CONSOLE_LOG__

    // Nothing could be recovered from any source. The whole body of this dialog
    // — stats, progress, messages — sits inside a `selectedFiles.length > 0`
    // container, so carrying on here renders a panel with a title and a Close
    // button and nothing in between.
    if (!this.selectedFiles || this.selectedFiles.length === 0) {
      this.expireRestoredBatch(processingData, 'no_files_resolved');
      return;
    }

    // Continue polling - it will show progress or timeout after 5 minutes
    this.waitForAllProcessingComplete().then(() => {
      // console.log('Processing completed or timed out after restore'); //__CONSOLE_LOG__
      // Mark as complete
      this.uploadComplete = true;
      this.isProcessing = false;

      // Stop bulk polling
      this.stopBulkProgressPolling();

      // Leave the finished notice first: it is read from this snapshot, and
      // without it the banner this dialog replaced just disappears.
      this.uploadBanner.announceFinished(
        this.eventId,
        this.processingCompleted,
        this.processingFailed,
        this.processingTotal
      );

      // Clear the minimized state
      SafeStorage.removeItem('uploadProcessingState');

      // Close and refresh
      this.closeDialog(this.selectedFiles);
    });
  }

  openSubfolderDialog() {
    this.isSubfolderDialogOpen = true;
  }

  closeSubfolderDialog() {
    this.isSubfolderDialogOpen = false;
    this.newSubfolderName = '';
  }

  addSubfolder() {
    if (this.newSubfolderName.trim()) {
      this.subfolders.push(this.newSubfolderName.toUpperCase()); // Uppercase for consistency
      this.selectedSubfolder = this.newSubfolderName.toUpperCase(); // Auto-select new subfolder
      this.closeSubfolderDialog();
    }
  }


  // Handler for Add More button
  onAddMoreClick(): void {
    // Prevent adding more files while upload or processing is in progress
    if (this.isFileLoading || this.isProcessing) {
      this.loggingService.log('Add More attempted while upload/processing in progress - action ignored');
      return;
    }

    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.click();
    } else {
      this.loggingService.log('fileInput not found or nativeElement not available');
    }
  }

  async onFileSelected(event: any) {
    // console.log('onFileSelected called', event); //__CONSOLE_LOG__
    try {
      const files = event.target.files;
      // console.log('files from event:', files); //__CONSOLE_LOG__

      if (!files || files.length === 0) {
        this.loggingService.log("No files selected");
        // console.log("No files selected"); //__CONSOLE_LOG__
        this.onFilePickerCancelled();
        return;
      }
      // Files are here — disarm the fallback before anything below can await.
      this.clearPickerFallbackTimer();
      this.mobilePickerOpen = false;
      this.dialogRef.removePanelClass('dialog-picker-pending');

      await this.processPickedFiles(files, 'file');

      // Reset file input value to allow re-selection
      event.target.value = '';
    } catch (error) {
      this.loggingService.log(`Error in onFileSelected: ${error}`);
      // console.error("Error selecting files:", error); //__CONSOLE_LOG__
    }
  }

  /**
   * Everything that happens once files have been chosen, whether through the
   * file picker, Add Folder or by dropping them: type filter, readability check, duplicate
   * check, storage check. One body for both, so a way of adding files can never
   * skip a check the other has.
   */
  private async processPickedFiles(files: FileList | File[], source: 'file' | 'drop' | 'folder'): Promise<void> {
    const what = source === 'drop' ? 'Drop' : source === 'folder' ? 'Folder' : 'File';
    this.loggingService.log(`${what} selection started: ${files.length} files`);
    // console.log(`File selection started: ${files.length} files`); //__CONSOLE_LOG__

    // Detect if this might be from Google Photos (Android devices)
    const isAndroid = /Android/.test(navigator.userAgent);

    // Log file details for debugging Google Photos issues
    let hasVirtualFiles = false;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      this.loggingService.log(`File ${i}: name=${file.name}, size=${file.size}, type=${file.type}`);

      // Check if file is accessible (Google Photos virtual files might have size 0 initially)
      if (file.size === 0) {
        hasVirtualFiles = true;
        this.loggingService.log(`Warning: File ${file.name} has size 0 - may be virtual file from Google Photos`);
      }
    }

    // If Android and has virtual files, wait a bit for Google Photos to fully load them
    if (isAndroid && hasVirtualFiles) {
      this.loggingService.log("Detected potential Google Photos virtual files, waiting 500ms for files to load...");
      await new Promise(resolve => setTimeout(resolve, 500));
    }


    // New selection — clear any preserved duplicates from previous runs. Add
    // Folder can be used again to add to the selection, which keeps them.
    if (source !== 'folder' || this.selectedFiles.length === 0) {
      this.preservedDuplicates = [];
    }
    this.addFilesToSelection(files);

    // Update UI with selected files
    this.selectedFileNames = [];
    if (this.selectedFiles && this.selectedFiles.length > 0) {
      for (let i = 0; i < this.selectedFiles.length; i++) {
        this.uploadProgress[this.selectedFiles[i].name] = 0;
        this.selectedFileNames.push(this.selectedFiles[i].name);
      }
      this.loggingService.log(`Total selected files: ${this.selectedFiles.length}`);

      // Quick duplicate check by filename only (no MD5 - instant)
      this.checkForDuplicatesQuick();

      // Check storage quota after files selected
      await this.checkStorageQuota();

      // Log memory usage after file selection
      this.logMemoryUsage(`After ${what} Selection`);
    }
  }

  /**
   * Checks selectedFiles against existingFiles set and updates duplicateFiles map.
   * Normalizes file names for accurate detection.
   */

  generateThumbnails(file: File) {
    const video = document.createElement("video");
    const fileReader = new FileReader();

    fileReader.onload = () => {
      if (fileReader.result) {
        const result = fileReader.result as string;
        video.src = result;
        video.addEventListener("loadedmetadata", () => {
          const duration = video.duration; // Get video duration in seconds
          const interval = 5; // Interval in seconds to capture thumbnails
          const numThumbnails = Math.floor(duration / interval);

          for (let i = 1; i <= numThumbnails; i++) {
            const time = i * interval;
            this.captureThumbnail(video, time);
            // console.log(this.thumbnailUrls); //__CONSOLE_LOG__
          }
        });
      }
    };

    fileReader.readAsDataURL(file);
  }

  captureThumbnail(video: HTMLVideoElement, time: number) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    video.currentTime = time;
    video.addEventListener("seeked", () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context?.drawImage(video, 0, 0, canvas.width, canvas.height);

      const thumbnailUrl = canvas.toDataURL("image/jpeg"); // Convert canvas to base64 thumbnail URL
      this.thumbnailUrls.push(thumbnailUrl);
    });
  }

  getDeviceType() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
    // console.log('isMobile:', isMobile ? 'Mobile' : 'Desktop'); //__CONSOLE_LOG__
    if (userAgent.match(/ipad|iphone|ipod/)) {
      // console.log('iOS'); //__CONSOLE_LOG__
    }
    return isMobile ? 'Mobile' : 'Desktop';
  }

  // Check user storage quota and validate upload size
  private async checkStorageQuota(): Promise<void> {
    if (!this.selectedFiles || this.selectedFiles.length === 0) {
      // console.log('[Storage Check] No files selected, skipping'); //__CONSOLE_LOG__
      return;
    }

    // Guest users do not have their own storage plan; quota is enforced server-side
    // against the host's account, so skip the check to avoid hanging on a null response.
    const isGuest = SafeStorage.getItem('isGuestUser') === 'true';
    if (isGuest) {
      // console.log('[Storage Check] Guest user detected, skipping storage check'); //__CONSOLE_LOG__
      return;
    }

    // Collaborators upload into the host's storage bucket — check host's quota.
    const isHost = SafeStorage.getItem('isHost');
    if (isHost !== 'true') {
      const hostAvailableGB = parseFloat(SafeStorage.getItem('hostStorageAvailableGB') ?? '-1');
      if (hostAvailableGB < 0) {
        // Host storage data not available; allow upload (server will enforce)
        this.analytics.capture('storage_check_skipped', {
          reason: 'host_storage_unknown',
          event_id: this.eventId,
          file_count: this.selectedFiles.length,
          upload_bytes: this.selectedFiles.reduce((acc, f) => acc + f.size, 0),
          surface: this.isMobile ? 'mobile' : 'desktop',
          role: 'collaborator',
        });
        // console.log('[Storage Check] Collaborator: host storage info unavailable, skipping check'); //__CONSOLE_LOG__
        return;
      }
      const totalUploadSize = this.selectedFiles.reduce((acc, file) => acc + file.size, 0);
      const GiB = 1073741824; // 1024^3
      // Convert host GB values to bytes for a precise byte-level comparison
      const hostAvailableBytes = hostAvailableGB * GiB;
      const hostUsedGB = parseFloat(SafeStorage.getItem('hostStorageUsedGB') ?? '0');
      const hostTotalGB = parseFloat(SafeStorage.getItem('hostStorageTotalGB') ?? '0');
      const formatBytesCollaborator = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return parseFloat((bytes / 1024).toFixed(2)) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return parseFloat((bytes / (1024 * 1024)).toFixed(2)) + ' MB';
        return parseFloat((bytes / (1024 * 1024 * 1024)).toFixed(2)) + ' GB';
      };
      // console.log(`[Storage Check] Collaborator: host available=${hostAvailableGB}GB (${hostAvailableBytes} bytes) //__CONSOLE_LOG__, upload=${totalUploadSize} bytes`);
      if (totalUploadSize > hostAvailableBytes) {
        this.storageQuotaExceeded = true;
        this.isCollaboratorStorageBlock = true;
        this.storageQuotaMessage = `This Event Gallery has been temporarily paused and new uploads are not available at this time.`;
        this.captureUploadBlocked('host_storage_paused', 'limit', {
          upload_bytes: totalUploadSize,
          available_bytes: Math.max(0, Math.round(hostAvailableBytes)),
          plan_total_gb: hostTotalGB,
          plan_used_gb: hostUsedGB,
          role: 'collaborator',
        });
        // console.log('[Storage Check] Host quota exceeded (collaborator) //__CONSOLE_LOG__:', this.storageQuotaMessage);
        this.isCheckingStorage = false;
        this.cdr.detectChanges();
      } else {
        this.storageQuotaMessage = `Upload: ${formatBytesCollaborator(totalUploadSize)} • Host has ${hostAvailableGB.toFixed(2)} GB available`;
        // console.log('[Storage Check] Collaborator: host has sufficient storage'); //__CONSOLE_LOG__
      }
      return;
    }

    // console.log('[Storage Check] Starting check...'); //__CONSOLE_LOG__
    this.isCheckingStorage = true;
    this.storageQuotaExceeded = false;
    this.storageQuotaMessage = '';

    const formatBytes = (bytes: number): string => {
      if (bytes <= 0) return '0 B';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return parseFloat((bytes / 1024).toFixed(2)) + ' KB';
      if (bytes < 1024 * 1024 * 1024) return parseFloat((bytes / (1024 * 1024)).toFixed(2)) + ' MB';
      return parseFloat((bytes / (1024 * 1024 * 1024)).toFixed(2)) + ' GB';
    };

    try {
      // Calculate total upload size
      const totalUploadSize = this.selectedFiles.reduce((acc, file) => acc + file.size, 0);
      const uploadSizeFormatted = formatBytes(totalUploadSize);
      // console.log('[Storage Check] Upload size:', uploadSizeFormatted); //__CONSOLE_LOG__

      // Get user plan data
      // console.log('[Storage Check] Fetching user plan...'); //__CONSOLE_LOG__
      const userPlan = await new Promise<any>((resolve, reject) => {
        this.eventService.getUserPlan().then(observable => {
          observable.subscribe({
            next: (data) => {
              if (data === null || data === undefined) return; // skip BehaviorSubject initial null
              // console.log('[Storage Check] User plan received:', data); //__CONSOLE_LOG__
              resolve(data);
            },
            error: (err) => {
              // console.error('[Storage Check] Error fetching plan:', err); //__CONSOLE_LOG__
              reject(err);
            }
          });
        }).catch(err => {
          // console.error('[Storage Check] Promise error:', err); //__CONSOLE_LOG__
          reject(err);
        });
      });

      this.userStorageData = userPlan;
      this.loggingService.log(`User storage data: ${JSON.stringify(userPlan)}`);

      // Parse storage values from nested storage object
      // API returns occupied_bytes and total_bytes (in bytes)
      const storage = userPlan.storage || {};
      // console.log('[Storage Check] Storage object:', storage); //__CONSOLE_LOG__
      const GiB = 1073741824; // 1024^3
      const totalStorageGB = (storage.total_bytes != null ? storage.total_bytes : 0) / GiB;
      const usedStorageGB = (storage.occupied_bytes != null ? storage.occupied_bytes : 0) / GiB;
      const availableStorageGB = totalStorageGB - usedStorageGB;
      // Use exact bytes for comparison to avoid toFixed(2) rounding masking small files
      const availableBytes = totalStorageGB * GiB - usedStorageGB * GiB;

      // console.log('[Storage Check] Total:', totalStorageGB, 'GB, Used:', usedStorageGB, 'GB, Available:', availableStorageGB, 'GB'); //__CONSOLE_LOG__
      this.loggingService.log(`Storage check: Total=${totalStorageGB}GB, Used=${usedStorageGB}GB, Available=${availableStorageGB}GB, Upload=${totalUploadSize} bytes`);

      // Check if upload exceeds available storage (compare bytes directly to avoid rounding errors)
      if (totalUploadSize > availableBytes) {
        this.storageQuotaExceeded = true;
        this.storageQuotaMessage = `Upload size (${uploadSizeFormatted}) exceeds available storage (${availableStorageGB.toFixed(2)} GB). You are using ${usedStorageGB.toFixed(2)} GB of ${totalStorageGB.toFixed(2)} GB total storage.`;
        // console.log('[Storage Check] QUOTA EXCEEDED:', this.storageQuotaMessage); //__CONSOLE_LOG__
        this.loggingService.log(`Storage quota exceeded: ${this.storageQuotaMessage}`);
        // The one stop the person sees as "Storage Limit Exceeded / Upgrade
        // Plan". It used to leave no event at all, so the batch read as an
        // upload that started and never finished. Byte counts, not GB strings,
        // so a 1.59 GB pick against a 0.5 GB plan is visible as exactly that.
        this.captureUploadBlocked('storage_limit', 'limit', {
          upload_bytes: totalUploadSize,
          available_bytes: Math.max(0, Math.round(availableBytes)),
          plan_total_bytes: storage.total_bytes ?? null,
          plan_used_bytes: storage.occupied_bytes ?? null,
        });
      } else {
        // Show storage info even if enough space
        this.storageQuotaMessage = `Upload: ${uploadSizeFormatted} • Available: ${availableStorageGB.toFixed(2)} GB of ${totalStorageGB.toFixed(2)} GB total`;
        // console.log('[Storage Check] Sufficient storage:', this.storageQuotaMessage); //__CONSOLE_LOG__
      }

      this.isCheckingStorage = false;
      // console.log('[Storage Check] Complete. isCheckingStorage:', this.isCheckingStorage, 'storageQuotaMessage:', this.storageQuotaMessage, 'storageQuotaExceeded:', this.storageQuotaExceeded); //__CONSOLE_LOG__

      // Manually trigger change detection to update UI
      this.cdr.detectChanges();

    } catch (error: any) {
      // console.error('[Storage Check] Error:', error); //__CONSOLE_LOG__
      this.loggingService.log(`Error checking storage quota: ${error}`);
      // If API fails, don't block upload
      // The upload is let through unchecked here, so an over-limit pick gets as
      // far as `upload_started` and is only refused later (server side). Say so,
      // otherwise that refusal reads as an upload that hung.
      this.analytics.capture('storage_check_skipped', {
        reason: 'plan_lookup_failed',
        event_id: this.eventId,
        file_count: this.selectedFiles?.length ?? 0,
        upload_bytes: (this.selectedFiles || []).reduce((acc, f) => acc + f.size, 0),
        error_message: String(error?.message ?? error).slice(0, 200),
        network_online: navigator.onLine,
        surface: this.isMobile ? 'mobile' : 'desktop',
        role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
      });
      this.isCheckingStorage = false;
      this.storageQuotaMessage = '';
      this.cdr.detectChanges();
      // console.log('[Storage Check] Failed, allowing upload anyway'); //__CONSOLE_LOG__
    }
  }

  // //uploads in batch of 100
  // async uploadImages() {
  //   this.isFileLoading = true;

  //   if (!this.selectedFiles || this.selectedFiles.length === 0) {
  //     console.error("No files selected.");
  //     return;
  //   }

  //   const batchSize = 100;
  //   const totalFiles = this.selectedFiles.length;
  //   const fileBatches = [];

  //   // 📌 Split files into batches of 100
  //   for (let i = 0; i < totalFiles; i += batchSize) {
  //     fileBatches.push(this.selectedFiles.slice(i, i + batchSize));
  //   }

  //   console.log(`Total Batches: ${fileBatches.length}`);

  //   for (const [index, batch] of fileBatches.entries()) {
  //     console.log(`Uploading batch ${index + 1}/${fileBatches.length} with ${batch.length} files...`);

  //     // Upload each batch in parallel
  //     await Promise.all(batch.map((file) => this.uploadFile(this.userId, this.eventId, file)));

  //     console.log(`Batch ${index + 1} completed.`);
  //   }

  //   this.isFileLoading = false;
  //   console.log("All files uploaded successfully!");

  //   // Move originals after all batches are completed
  //   setTimeout(() => {
  //     this.eventService.moveOriginals(this.eventId).subscribe(
  //       (res) => console.log("Move result:", res),
  //       (error) => console.error("Error moving originals:", error)
  //     );
  //   }, 5000);

  //   this.closeDialog(this.selectedFiles);
  // }

  /**
   * Public entry point, kept deliberately thin.
   *
   * The body below used to be `uploadImages` itself, with no top-level catch —
   * so anything that threw between starting the batch and the `upload_completed`
   * capture at the end produced *no* analytics at all. That is not theoretical:
   * the worst observed failure (21 files lost in one session) emitted attempt
   * events and then nothing, because the batch never reached its completion
   * event. It also left `isFileLoading` true forever, which pins the progress UI
   * open and keeps the beforeunload warning armed on every navigation.
   */
  async uploadImages() {
    // Not awaited: the name saves alongside the upload, never in front of it.
    void this.saveGuestName();
    try {
      await this.runUploadImages();
    } catch (err) {
      this.loggingService.logError('Upload batch aborted before completion', err);

      this.analytics.capture('upload_batch_failed', {
        event_id: this.eventId,
        attempted_count: this.attemptedCount,
        success_count: this.successCount,
        failed_count: this.failedCount,
        ...(this.lastDiagnosedFailure ?? { kind: 'unknown' }),
        cause: uploadFailureCause(this.lastDiagnosedFailure?.kind),
        // Deliberately overwrites any error_message from the diagnosis: this is
        // the throw that killed the batch, not the last per-file failure.
        error_message: describeError(err).slice(0, 300),
        surface: this.isMobile ? 'mobile' : 'desktop',
        role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
      });

      // Release the UI. Without this the dialog stays in its uploading state
      // with no way forward. The intervals go with it: a batch that threw is
      // not going to make any more progress for the speed monitor to measure,
      // and nothing is left to keep the session alive for.
      this.isProcessing = false;
      this.abortUploadRun('batch threw before completion');
    }
  }

  private async runUploadImages() {
    // Re-entrancy guard: the "Upload" button is hidden via *ngIf once isFileLoading
    // flips true, but a fast double-tap can fire this handler twice before Angular's
    // next change-detection cycle removes the button from the DOM — producing two
    // upload_started events and duplicate upload work for one tap.
    if (this.isFileLoading || this.isProcessing) {
      this.loggingService.log('Upload blocked: an upload is already in progress');
      this.captureUploadBlocked('already_uploading', 'repeat');
      return;
    }

    // Generate a unique session ID for this upload batch
    this.uploadSessionId = this.generateUploadSessionId();
    this.loggingService.log(`Upload session started: ${this.uploadSessionId}`);
    this.logMemoryUsage('Upload Start');

    // Storage already checked after file selection, just verify again before upload
    if (this.isCheckingStorage) {
      this.loggingService.log('Upload blocked: Storage check still in progress');
      this.captureUploadBlocked('storage_check_pending', 'not_ready');
      return;
    }
    if (this.storageQuotaExceeded) {
      this.loggingService.log('Upload blocked: Storage quota exceeded');
      this.captureUploadBlocked(
        this.isCollaboratorStorageBlock ? 'host_storage_paused' : 'storage_limit',
        'limit',
        { at: 'upload_tap' }
      );
      return;
    }
    if (this.isCheckingReadable) {
      this.loggingService.log('Upload blocked: picking-time read check still running');
      this.captureUploadBlocked('read_check_pending', 'not_ready');
      return;
    }

    // Checked before anything is started, not after: this guard needs none of
    // the state below, and running it later meant bailing out with the loading
    // flag already set and two intervals already running.
    if (!this.selectedFiles || this.selectedFiles.length === 0) {
      this.captureUploadBlocked('no_files_selected', 'selection');
      alert("No files selected.");
      return;
    }

    // Without a user id the files are written under pwa/undefined/ and tracked
    // with user_id null: they land in the wrong place and nothing can find them.
    // The constructor only checks once, when the dialog opens, and not for the
    // strings 'null' / 'undefined'. Check again at the moment it matters.
    if (!this.ensureUploadUserId() || !this.ensureUploadEventId()) {
      return;
    }

    this.isFileLoading = true;
    this.stopRequested = false;
    this.confirmingStop = false;
    this.stoppedCount = 0;
    this.failedBeforeLoop = this.failedFiles.length;
    this.count = 0; // Track uploaded files count
    this.batchS3Keys.clear(); // New batch: previous run's keys must not leak into its snapshot
    this.batchTrackedKeys.clear();
    // A new batch earns a new gallery refresh; without this the once-per-batch
    // marker event-detail writes would suppress the refresh for every
    // subsequent upload in the same browser session.
    this.clearUploadRefreshMarkers();
    let uploadedBytes = 0; // Track uploaded bytes
    const startTime = Date.now(); // Start time for estimating speed

    // Reset network speed monitoring
    this.uploadSpeedSamples = [];
    this.lastUploadedBytes = 0;
    this.lastSpeedCheckTime = Date.now(); // Initialize to current time
    this.slowNetworkAlertShown = false;
    this.currentUploadSpeed = '--';
    this.isSlowNetwork = false;
    this.slowNetworkMessage = '';
    this.connectionNotice = '';
    this.lastProgressAt = Date.now();
    this.stalledNow = false;
    this.stallReported = false;

    // One upload at a time in this window. Another one still sending (from
    // this album or another, usually minimized) goes first, and this one says
    // it is waiting instead of sitting on "0 of N · Calculating…".
    if (this.uploadBanner.engineRunning) {
      const started = await this.waitForOtherUpload();
      if (!started) {
        return;
      }
    }
    this.uploadStartedAt = Date.now();

    // This loop lives in this window's memory and nothing on the server knows it
    // exists. The heartbeat is how another window — or this one after a reload —
    // can tell the difference between "still uploading" and "the page doing it
    // is gone". Ended when the batch loop does, however it ends.
    this.uploadBanner.beginEngineHeartbeat();
    // Once minimized, the banner's ✕ is the only way left to reach this loop.
    // Cleared by endEngineHeartbeat.
    this.uploadBanner.registerStop(() => this.stopUpload('banner'));

    // Start real-time speed monitoring
    this.startSpeedMonitoring();

    // Start session keepalive to prevent timeout during long uploads
    this.startSessionKeepalive();

    // --- PATCH: Always preserve original selection and summary counts ---
    // 1. Preserve original selection count
    this.originalSelectedCount = this.selectedFiles.length;
    // Held separately because the field above is later overwritten by the
    // backend's total_selected, which counts tracking rows (i.e. attempted)
    // rather than what the host picked. Telemetry needs the pick count.
    const hostSelectedCount = this.selectedFiles.length;

    // 1a. A name already in the album is only skipped once its content matches.
    // Usually settled at pick time; this covers an Upload tap that beat it.
    await this.confirmAlbumDuplicates();

    // 1b. Catch files that are byte-identical to another file in this same
    // selection. The existing check only compares against files already in the
    // event, so 29 Windows "- Copy" files once uploaded in full and were then
    // collapsed server-side by MD5 — the user was told "109 successful" and
    // saw 80. Detecting them here saves the bandwidth and lets the summary say
    // what actually happened.
    this.duplicatesWithinSelection = await this.markDuplicatesWithinSelection(this.selectedFiles);

    // 2. Filter out duplicates and preserve duplicate info
    let filesToUpload = this.selectedFiles.filter(file => !this.duplicateFiles.has(file.name));
    const duplicateCount = this.selectedFiles.length - filesToUpload.length;
    this.skippedDuplicates = duplicateCount;
    this.preservedDuplicates = [];
    this.selectedFiles.forEach((file, idx) => {
      if (this.duplicateFiles.has(file.name)) {
        this.preservedDuplicates.push({ name: file.name, size: file.size, thumbnail: this.fileThumbnails[idx] || null });
      }
    });

    const alreadyInEvent = duplicateCount - this.duplicatesWithinSelection;
    this.loggingService.log(
      `Selection: ${this.originalSelectedCount} chosen, ${filesToUpload.length} to upload ` +
      `(${alreadyInEvent} already in event, ${this.duplicatesWithinSelection} duplicates within selection)`
    );

    // 3. If all files are duplicates, abort — say which kind, since "already in
    // the event" and "you selected the same file twice" are different problems.
    if (filesToUpload.length === 0) {
      const parts: string[] = [];
      if (alreadyInEvent > 0) parts.push(`${alreadyInEvent} already in this event`);
      if (this.duplicatesWithinSelection > 0) parts.push(`${this.duplicatesWithinSelection} duplicated within your selection`);
      this.captureUploadBlocked('all_duplicates', 'selection', {
        already_in_event: alreadyInEvent,
        duplicate_in_selection: this.duplicatesWithinSelection,
      });
      alert(`Nothing to upload — all ${duplicateCount} selected files are duplicates (${parts.join(', ')}).`);
      this.abortUploadRun(`all ${duplicateCount} selected files were duplicates`);
      return;
    }

    // 3b. Skip what the picking-time check could not read (usually photos
    // that only exist in Google Photos). Sending them would fail at the read
    // step anyway; setting them aside here lets the result card say exactly
    // which ones and what to do, instead of counting them as upload errors.
    this.precheckLeftOut = filesToUpload.filter(file => this.precheckUnreadable.has(file.name));
    filesToUpload = filesToUpload.filter(file => !this.precheckUnreadable.has(file.name));
    if (filesToUpload.length === 0) {
      this.loggingService.log(`Upload blocked: all ${this.precheckLeftOut.length} remaining files unreadable at picking`);
      this.captureUploadBlocked('cloud_only_files', 'cloud', {
        left_out_count: this.precheckLeftOut.length,
        cause: 'cloud_file',
      });
      this.abortUploadRun('all remaining files were unreadable at picking');
      return;
    }

    // 4. Update selectedFiles to only include non-duplicates
    this.selectedFiles = filesToUpload;

    // 5. Attempted count is the number of files actually uploaded
    this.attemptedCount = this.selectedFiles.length;

    this.analytics.capture('upload_started', {
      event_id: this.eventId,
      file_count: this.attemptedCount,
      skipped_duplicates: duplicateCount,
      skipped_already_in_event: alreadyInEvent,
      skipped_duplicate_in_selection: this.duplicatesWithinSelection,
      skipped_unreadable_at_pick: this.precheckLeftOut.length,
      // Bytes, so a batch that later stops against the plan limit can be read
      // as "1.59 GB against 0.5 GB" instead of as an upload that hung.
      total_bytes: this.selectedFiles.reduce((acc, f) => acc + f.size, 0),
      plan_available_bytes: this.userStorageData?.storage?.total_bytes != null
        ? Math.max(0, Math.round(
            this.userStorageData.storage.total_bytes - (this.userStorageData.storage.occupied_bytes ?? 0)))
        : null,
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });

    // 6. Reset uploadedCheck array to match new selectedFiles length
    this.uploadedCheck = new Array(this.selectedFiles.length).fill(false);

    // Names are claimed fresh for this run, in upload order
    this.uploadFileNames.clear();
    this.claimedUploadNames.clear();
    this.uploadNameToOriginal.clear();

    // Verification state is per-run: a second upload in the same dialog must
    // not inherit the first one's unconfirmed files, nor stay disabled because
    // of a transient permissions blip earlier.
    this.unverifiedFiles.clear();
    this.unreadableFiles.clear();
    this.knownUnreadable.clear();
    this.unreadableSweep = null;
    this.verificationDisabled = false;
    this.consecutiveVerifyDenials = 0;
    // Per-run too: the connection this batch runs on is not the one the last
    // batch ran on, so a probe result from earlier must not be reused.
    this.reachabilityChecked = false;
    this.reachabilityProbe = null;

    // 7. Clear duplicate warnings since we're handling them
    this.duplicateFiles.clear();
    this.showDuplicateWarning = false;

    // Check total upload size (1GB limit) - COMMENTED OUT
    // const MAX_TOTAL_SIZE = 1024 * 1024 * 1024; // 1GB in bytes
    const totalSize = this.selectedFiles.reduce((acc, file) => acc + file.size, 0);

    // Initialize ETA calculation and show calculating until enough samples
    this.etaStartTs = Date.now();
    this.etaMinSamples = Math.min(3, Math.max(1, Math.floor(this.selectedFiles.length * 0.1)));
    this.etaSecondsEma = null;
    this.estimatedTime = 'Calculating...';

    // if (totalSize > MAX_TOTAL_SIZE) {
    //   const sizeinGB = (totalSize / (1024 * 1024 * 1024)).toFixed(2);
    //   alert(`Your upload size (${sizeinGB} GB) exceeds the 1 GB per-batch limit. Please upload in multiple batches of up to 1GB each.`);
    //   event?.stopPropagation();
    //   this.isFileLoading = false;
    //   return;
    // }

    // Check for existing upload state
    if (this.loadUploadState()) {
      const resume = confirm('Previous upload session found. Would you like to resume from where you left off?');
      if (resume) {
        // Filter out already uploaded files
        this.selectedFiles = this.selectedFiles.filter(file =>
          !this.uploadState?.files.find(f => f.name === file.name && f.uploaded)
        );
        uploadedBytes = this.uploadState?.uploadedSize || 0;
      } else {
        this.clearUploadState();
      }
    }

    // Initialize new upload state
    this.uploadState = {
      eventId: this.eventId,
      userId: this.userId,
      files: this.selectedFiles.map(file => ({
        name: file.name,
        uploaded: false,
        size: file.size
      })),
      totalSize,
      uploadedSize: uploadedBytes,
      lastUpdated: Date.now()
    };
    // Name -> entry, built once. The per-file completion path used to locate
    // its entry with Array.find(), which is O(n) against a list that is n long.
    this.uploadStateIndex = new Map(this.uploadState.files.map(f => [f.name, f]));

    // Network + unload listeners are registered once per component, not once
    // per upload. Registering them here previously left a new set behind on
    // every run, each closing over dead state, with no removal anywhere.
    this.registerUploadWindowListeners();

    const batchSize = 100;
    const totalFiles = this.selectedFiles.length;
    //const totalSize = this.selectedFiles.reduce((acc, file) => acc + file.size, 0); // Total upload size

    // Validate all files before creating batches
    const validFiles: File[] = [];
    const invalidFiles: { file: File; reason: string }[] = [];

    this.selectedFiles.forEach(file => {
      if (!file || !file.name) {
        invalidFiles.push({ file, reason: 'Invalid file object or missing name' });
      } else if (file.size === 0) {
        invalidFiles.push({ file, reason: 'File size is 0 bytes' });
      } else if (file.size > 5 * 1024 * 1024 * 1024) { // 5GB max per file
        invalidFiles.push({ file, reason: 'File exceeds 5GB size limit' });
      } else {
        validFiles.push(file);
      }
    });

    // Log validation results
    if (invalidFiles.length > 0) {
      this.loggingService.log(`Found ${invalidFiles.length} invalid files that will be skipped:`);
      invalidFiles.forEach(({ file, reason }) => {
        this.loggingService.log(`  - ${file?.name || 'Unknown'}: ${reason}`);
        this.failedFiles.push({ file, error: reason, retries: 0, kind: 'invalid-file', retryable: false });
        this.failedCount++;
      });
    }

    // Update selectedFiles to only valid files
    this.selectedFiles = validFiles;
    this.loggingService.log(`Validated files: ${validFiles.length} valid, ${invalidFiles.length} invalid`);

    if (validFiles.length === 0) {
      this.captureUploadBlocked('no_valid_files', 'selection', { invalid_count: invalidFiles.length });
      alert('No valid files to upload. All files were either invalid or had errors.');
      this.abortUploadRun(`all ${invalidFiles.length} file(s) failed validation`);
      return;
    }

    const fileBatches = [];

    // 📌 Split files into batches of 100
    for (let i = 0; i < validFiles.length; i += batchSize) {
      fileBatches.push(validFiles.slice(i, i + batchSize));
    }

    // console.log(`Total Batches: ${fileBatches.length}`); //__CONSOLE_LOG__

    //this.selectedFiles.forEach(file => this.loggingService.log(`- ${file.name} (Size: ${file.size} bytes)`));
    this.loggingService.log(`Total Valid Files: ${validFiles.length}, Total Batches to Upload: ${fileBatches.length}`);

    // Use validFiles.length for all subsequent calculations
    const totalFilesToUpload = validFiles.length;

    // Files a worker has picked up. The unreadable sweep only probes the rest,
    // so it never flags a file that is already mid-upload.
    const startedFiles = new Set<File>();
    this.failedBeforeLoop = this.failedFiles.length;

    for (const [index, batch] of fileBatches.entries()) {
      // Stopped before this batch began: account for its files without
      // starting it (no credentials refresh, no tracking rows to correct later).
      if (this.stopRequested) {
        batch.forEach(file => this.recordStoppedFile(file));
        continue;
      }

      // console.log(`Uploading batch ${index + 1}/${fileBatches.length} with ${batch.length} files...`); //__CONSOLE_LOG__
      this.loggingService.log(`Uploading Batch ${index + 1}/${fileBatches.length} (Size: ${batch.length} files)`);

      // Ensure AWS credentials are fresh before each batch (especially for large batches)
      try {
        await this.initializeAWSCredentials();
        this.loggingService.log(`AWS credentials verified for batch ${index + 1}`);
      } catch (credError) {
        this.loggingService.log(`Failed to initialize AWS credentials for batch ${index + 1}: ${credError}`);
        // Mark all files in this batch as failed
        batch.forEach(file => {
          this.failedFiles.push({ file, error: 'AWS credentials initialization failed', retries: 0, kind: 'credentials', retryable: true });
          this.failedCount++;
        });
        continue; // Skip this batch
      }

      // Initialize tracking for all files in batch at once
      await this.initializeBulkUploadTracking(batch, this.userId, this.eventId);

      // Upload the batch with a bounded number of uploads in flight
      const batchStartTime = Date.now();
      const { files: fileConcurrency } = this.getUploadConcurrency();
      this.loggingService.log(`Batch ${index + 1} file concurrency: ${fileConcurrency} (connection: ${(navigator as any).connection?.effectiveType || 'unknown'})`);
      const batchResults = await this.runWithConcurrency(
        batch,
        fileConcurrency,
        async (file, batchFileIndex) => {
          startedFiles.add(file);
          if (this.knownUnreadable.has(file)) {
            this.recordSweptUnreadable(file);
            return;
          }
          if (this.stopRequested) {
            this.recordStoppedFile(file);
            return;
          }
          // Hoisted so the terminal catch can report the cause we actually
          // diagnosed, instead of re-deriving it from a rethrown message that
          // has already lost the SDK metadata.
          let lastFacts: DiagnosedFailure | undefined;
          try {
            // Calculate the global file index
            const globalIndex = index * batchSize + batchFileIndex;

            // Set current upload index before starting upload
            this.currentUploadIndex = globalIndex;

            const fileStartTime = Date.now(); // Start time for each file
            this.loggingService.log(`Upload starting for: ${file.name} (size: ${file.size} bytes)`);
            let retryCount = 0;
            let uploadSuccess = false;
            let connectionWaits = 0;

            while (retryCount < this.MAX_UPLOAD_ATTEMPTS && !uploadSuccess) {
              // A retry is a new start, and nothing new starts after a stop.
              if (this.stopRequested) {
                throw UPLOAD_STOPPED;
              }
              try {
                await this.uploadFile(this.userId, this.eventId, file);
                uploadSuccess = true; // Success - exit retry loop
                this.loggingService.log(`Upload successful for: ${file.name}`);
              } catch (uploadError: any) {
                retryCount++;
                const errorMsg = describeError(uploadError);

                // Work out what actually broke before deciding what to do about
                // it. Without this every branch below is guessing from a string.
                const facts = await this.diagnoseUploadFailure(uploadError, file);
                lastFacts = facts;

                // A cloud-only photo is the user's library, not our bug: log it
                // plainly rather than as a console error per file.
                const attemptLine = `Upload attempt ${retryCount}/${this.MAX_UPLOAD_ATTEMPTS} failed for ${file.name} (size=${file.size}, type=${file.type || 'unknown'})`;
                if (facts.kind === 'file-unreadable') {
                  this.loggingService.log(`${attemptLine} | ${errorMsg}`);
                  // Where there is one there are usually dozens. Check the rest
                  // of the queue now instead of letting each find out alone.
                  this.sweepQueuedForUnreadable(validFiles, startedFiles);
                } else {
                  this.loggingService.logError(attemptLine, uploadError);
                }

                // Queryable in PostHog across users/sessions, unlike the S3
                // text log — this is what would have let today's two
                // incidents be found and diagnosed from one query instead of
                // a multi-session manual log/replay hunt.
                const connection = (navigator as any).connection;
                this.analytics.capture('upload_attempt_failed', {
                  event_id: this.eventId,
                  file_name: file.name,
                  file_size: file.size,
                  mime_type: file.type || 'unknown',
                  attempt: retryCount,
                  max_attempts: this.MAX_UPLOAD_ATTEMPTS,
                  // phase/kind/retryable + AWS request id + probe evidence.
                  // `kind` is the field to group by; `error_message` is only
                  // useful once you already know which kind you are looking at.
                  ...facts,
                  cause: uploadFailureCause(facts.kind),
                  // How long after picking this happened, and whether the
                  // file's mtime is the pick moment — see pickContext().
                  ...this.pickContext(file),
                  visibility_state: document.visibilityState,
                  network_online: navigator.onLine,
                  connection_type: connection?.effectiveType,
                  save_data: !!connection?.saveData,
                  surface: this.isMobile ? 'mobile' : 'desktop',
                });

                // Stopped since this attempt began: no waiting and no retry.
                if (this.stopRequested) {
                  throw UPLOAD_STOPPED;
                }

                // A dead or very weak connection is nobody's fault but the
                // network's, and the file does not get less deserving of an
                // attempt because of it. Wait for the connection to come back
                // — once, shared by every worker — and try again without
                // spending one. Capped per file, so an outage that never ends
                // still ends the upload.
                if (facts.retryable && this.isConnectionFailure(facts.kind) && connectionWaits < this.MAX_CONNECTION_WAITS) {
                  connectionWaits++;
                  retryCount--;
                  this.loggingService.log(
                    `Connection problem (${facts.kind}) on ${file.name} — pausing until it recovers (wait ${connectionWaits}/${this.MAX_CONNECTION_WAITS})`
                  );
                  const recovered = await this.waitForConnectionRecovery();
                  // The wait ends early on a stop; that is not the connection giving up.
                  if (this.stopRequested) {
                    throw UPLOAD_STOPPED;
                  }
                  if (!recovered) {
                    throw new Error(`Failed (${facts.kind}) after waiting for the connection: ${errorMsg}`);
                  }
                  continue;
                }

                // Some causes cannot be fixed by doing the same thing again: a
                // file whose bytes are gone, a 403, a blocked AWS endpoint, our
                // own verification verdict. Retrying those spends ~30s per file
                // to reach an identical answer, which is most of the wall-clock
                // a failing batch used to burn before reporting anything.
                if (!facts.retryable) {
                  this.loggingService.log(`Not retrying ${file.name}: '${facts.kind}' cannot be resolved by retrying`);
                  throw new Error(`Failed (${facts.kind}) after ${retryCount} attempt(s): ${errorMsg}`);
                }

                if (retryCount < this.MAX_UPLOAD_ATTEMPTS) {
                  // If we're offline there's no point spending an attempt —
                  // wait for the connection first, then back off.
                  await this.waitForNetwork();

                  // Jittered backoff, longer than the old 2s/4s: that schedule
                  // gave up six seconds after the first blip, and a batch
                  // failing in lockstep retried on the same tick every time.
                  const waitTime = retryDelayMs(retryCount, facts.kind);
                  this.loggingService.log(`Waiting ${waitTime}ms before retry ${retryCount + 1} for ${file.name}`);
                  await new Promise(resolve => setTimeout(resolve, waitTime));
                } else {
                  // Max retries reached - throw to catch block
                  throw new Error(`Failed after ${retryCount} attempts: ${errorMsg}`);
                }
              }
            }

            // Per-file completion, unlike upload_completed which only fires
            // once for the whole batch — needed to measure guest participation
            // depth (event_id/role come from the registered context).
            this.analytics.capture('photo_uploaded', {
              event_id: this.eventId,
              file_size: file.size,
              mime_type: file.type || 'unknown',
              surface: this.isMobile ? 'mobile' : 'desktop',
            });

            this.count++; // Increase count on successful upload
            uploadedBytes += file.size; // Update uploaded bytes
            this.uploadedFiles.push(file); // Track uploaded file
            // This loop keeps running after the dialog is destroyed, so it is
            // the only live source of upload progress once minimized.
            this.syncMinimizedProgress();

            // Update total uploaded bytes for speed calculation
            this.totalUploadedBytes = uploadedBytes;

            // Mark file as uploaded
            this.uploadedCheck[globalIndex] = true;

            // Update upload state
            if (this.uploadState) {
              const fileState = this.uploadStateIndex.get(file.name);
              if (fileState) {
                fileState.uploaded = true;
                this.uploadState.uploadedSize = uploadedBytes;
                this.scheduleSaveUploadState();
              }
            }

            // Update estimated time after each file (not just batches)
            const elapsedTime = (Date.now() - startTime) / 1000;
            const avgTimePerFile = this.count > 0 ? elapsedTime / this.count : 0;
            const remainingFiles = totalFilesToUpload - this.count;
            const remainingTime = avgTimePerFile * remainingFiles;

            const nowTs = Date.now();
            const samplesReady = (this.count >= this.etaMinSamples) || ((nowTs - this.etaStartTs) > 5000);

            if (remainingTime <= 0) {
              this.estimatedTime = 'Almost done...';
            } else if (!samplesReady) {
              this.estimatedTime = 'Calculating...';
            } else {
              const alpha = 0.35; // smoothing factor
              this.etaSecondsEma = this.etaSecondsEma == null
                ? remainingTime
                : (alpha * remainingTime) + ((1 - alpha) * this.etaSecondsEma);
              const etaForDisplay = Math.max(this.etaSecondsEma, 3); // avoid tiny "1 sec" values
              this.estimatedTime = this.formatTimeWords(etaForDisplay);
            }

          } catch (error) {
            if (error === UPLOAD_STOPPED) {
              this.recordStoppedFile(file);
              return;
            }
            const errorMsg = describeError(error);
            const gaveUpLine = `FAILED: ${file.name} gave up after 3 attempts (size=${file.size}, type=${file.type || 'unknown'}, lastModified=${file.lastModified})`;
            if (lastFacts?.kind === 'file-unreadable') {
              this.loggingService.log(`${gaveUpLine} | ${errorMsg}`);
            } else {
              this.loggingService.logError(gaveUpLine, error);
            }

            // Mark upload progress as failed
            if (this.uploadProgressMap.has(file.name)) {
              const progress = this.uploadProgressMap.get(file.name);
              if (progress) {
                progress.status = 'failed';
                progress.error_message = errorMsg;
              }
            }

            this.failedFiles.push({
              file,
              error: errorMsg,
              retries: this.MAX_UPLOAD_ATTEMPTS,
              kind: lastFacts?.kind ?? 'unknown',
              retryable: lastFacts?.retryable ?? true,
            }); // Track failed file with error details
            this.failedCount++;

            // Tell the server, which otherwise keeps this file's row at
            // 'uploading' for ever, and send the log while it still has the
            // lines that explain what happened (guests included — see
            // sendFailureLog).
            this.reportFailureToBackend(file, lastFacts, errorMsg, this.MAX_UPLOAD_ATTEMPTS);
            this.loggingService.sendFailureLog(`file gave up: ${lastFacts?.kind ?? 'unknown'}`);

            // The terminal event. `upload_attempt_failed` says an attempt died;
            // only this says the file is actually lost, and it is the one a
            // failure dashboard should count. Previously the give-up path was
            // silent, so a file that exhausted every attempt produced nothing
            // beyond its attempt events — and if the batch loop itself died
            // before `upload_completed`, the loss was invisible entirely.
            const connection = (navigator as any).connection;
            this.analytics.capture('upload_failed', {
              event_id: this.eventId,
              file_name: file.name,
              file_size: file.size,
              mime_type: file.type || 'unknown',
              attempts: this.MAX_UPLOAD_ATTEMPTS,
              ...(lastFacts ?? { kind: 'unknown', error_message: errorMsg }),
              cause: uploadFailureCause(lastFacts?.kind),
              ...this.pickContext(file),
              visibility_state: document.visibilityState,
              network_online: navigator.onLine,
              connection_type: connection?.effectiveType,
              surface: this.isMobile ? 'mobile' : 'desktop',
              role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
            });
          }
        }
      );

      // Batch boundary: persist for real rather than leaving the last few
      // completions of this batch sitting on the debounce timer.
      this.saveUploadState();

      // Log batch completion details
      const batchEndTime = Date.now();
      const batchDuration = (batchEndTime - batchStartTime) / 1000;
      const successfulInBatch = batchResults.filter(r => r.status === 'fulfilled').length;
      const failedInBatch = batchResults.filter(r => r.status === 'rejected').length;

      this.loggingService.log(`Batch ${index + 1} completed in ${batchDuration.toFixed(2)}s - Success: ${successfulInBatch}/${batch.length}, Failed: ${failedInBatch}/${batch.length}`);
      this.loggingService.log(`Cumulative progress: ${this.count}/${totalFilesToUpload} files uploaded, ${this.failedFiles.length} failed`);

      // Calculate estimated time after each batch completes
      const elapsedTime = (Date.now() - startTime) / 1000; // Time in seconds
      const avgTimePerFile = this.count > 0 ? elapsedTime / this.count : 0; // Average time per file
      const remainingFiles = totalFilesToUpload - this.count; // Files left to upload
      const remainingTime = avgTimePerFile * remainingFiles; // Estimated time left in seconds

      const nowTsBatch = Date.now();
      const samplesReadyBatch = (this.count >= this.etaMinSamples) || ((nowTsBatch - this.etaStartTs) > 5000);
      let formattedETA: string;
      if (remainingTime <= 0) {
        formattedETA = 'Almost done...';
        this.estimatedTime = formattedETA;
      } else if (!samplesReadyBatch) {
        formattedETA = 'Calculating...';
        this.estimatedTime = formattedETA;
      } else {
        const alpha = 0.35;
        this.etaSecondsEma = this.etaSecondsEma == null
          ? remainingTime
          : (alpha * remainingTime) + ((1 - alpha) * this.etaSecondsEma);
        const etaForDisplay = Math.max(this.etaSecondsEma, 3);
        formattedETA = this.formatTimeWords(etaForDisplay);
        this.estimatedTime = formattedETA;
      }

      // console.log(`Batch ${index + 1} complete: ${this.count}/${totalFiles} files, ETA: ${formattedETA}`); //__CONSOLE_LOG__
      this.loggingService.log(`Batch ${index + 1} completed. Total Uploaded: ${this.count}/${totalFiles}, ETA: ${formattedETA}`);

      // Log memory usage after each batch
      this.logMemoryUsage(`After Batch ${index + 1}/${fileBatches.length}`);
    }

    // Keep isFileLoading true until upload is complete
    // this.isFileLoading will be set to false after uploadComplete is set
    // console.log(`All files uploaded successfully! (${this.count}/${totalFiles}) //__CONSOLE_LOG__`);

    // The transfer loop is done. Anything it gave up on has to reach the server
    // BEFORE the summary below is requested, or the summary counts those rows
    // as still uploading. The heartbeat ends with the loop: from here on the
    // work is server-side, and a window that closes now loses nothing.
    await this.flushFailureReports();
    this.uploadBanner.endEngineHeartbeat();
    this.connectionNotice = '';

    // Final upload summary
    this.loggingService.log(`========== UPLOAD SESSION SUMMARY ==========`);
    this.loggingService.log(`Session ID: ${this.uploadSessionId}`);
    this.loggingService.log(`Total files selected: ${this.originalSelectedCount}`);
    this.loggingService.log(`Skipped as duplicates: ${this.skippedDuplicates} (${this.duplicatesWithinSelection} identical within selection)`);
    this.loggingService.log(`Valid files to upload: ${totalFilesToUpload}`);
    this.loggingService.log(`Successfully uploaded: ${this.count}`);
    this.loggingService.log(`Failed uploads: ${this.failedFiles.length}`);
    this.loggingService.log(`Uploaded but unconfirmed in S3: ${this.unverifiedFiles.size}`);
    if (this.unverifiedFiles.size > 0) {
      this.unverifiedFiles.forEach((reason, file) => {
        this.loggingService.log(`  ? ${file.name}: ${reason}`);
      });
    }
    this.loggingService.log(`Upload success rate: ${((this.count / totalFilesToUpload) * 100).toFixed(2)}%`);
    if (this.failedFiles.length > 0) {
      this.loggingService.log(`Failed files list:`);
      this.failedFiles.forEach(failed => {
        this.loggingService.log(`  - ${failed.file.name}: ${failed.error}`);
      });
    }
    this.loggingService.log(`==========================================`);

    // Log memory usage after upload completes
    this.logMemoryUsage('Upload Complete');

    // NOTE: clearUploadState() deliberately runs *after* the summary fetch
    // below, not here. Wiping the resume state first meant a tab that died
    // during the summary request lost both the state and the counts.

    // Keep session keepalive running during processing phase
    // It will be stopped when dialog closes

    // Files are uploaded, now processing in background
    this.isFileLoading = false;
    this.isProcessing = true;
    // console.log('Upload complete, switching to processing state. isProcessing:', this.isProcessing); //__CONSOLE_LOG__



    // Files are uploaded, now processing in background - fetch summary immediately
    this.loggingService.log("Upload complete, fetching summary from backend...");
    // // Move originals after upload completes
    // this.scheduleMovOriginals(this.selectedFiles);

    // --- PATCH: Fetch authoritative summary counts from backend using session ID ---
    try {
      const summaryResp: any = await this.http.post(
        `${this.uploadProgressApiUrl}?action=getBulkSummary`,
        {
          upload_session_id: this.uploadSessionId,
          event_id: parseInt(this.eventId)
        },
        { headers: { 'Content-Type': 'application/json' } }
      ).toPromise();
      if (summaryResp && typeof summaryResp.success_count === 'number' && typeof summaryResp.failed_count === 'number') {
        // The backend counts tracking rows; it cannot know that post-batch S3
        // verification demoted a file, because a key that never landed simply
        // never reaches its pipeline. Taking success_count verbatim therefore
        // re-inflated the number right after verification had corrected it —
        // and since failed_count uses Math.max below, the totals disagreed
        // (success 100 + failed 3 against 100 selected). Client-side failures
        // are authoritative: cap the reported successes by what we did not
        // fail locally.
        const localFailures = this.failedFiles.length;
        const maxPossibleSuccess = Math.max(0, totalFilesToUpload - localFailures);

        // Clamp from BOTH sides, because the backend can be wrong in both
        // directions and each needs a different guard.
        //
        // Too high: it counts tracking rows and cannot know that post-batch S3
        // verification demoted a file, so it must be capped by what we did not
        // fail locally (the original reason for the cap).
        //
        // Too low: the summary is requested the moment the last file finishes —
        // measured at ~280ms after the final upload — so rows still being
        // ingested are simply not counted yet. Taking the backend number alone
        // therefore under-reported real, fully successful uploads: two observed
        // runs reported 16 of 21 and 39 of 43, both with failed_count 0, so
        // nothing looked wrong. `this.count` is the client's own tally of files
        // that uploaded AND verified, which is exactly what the caller means by
        // "succeeded", so it is the floor.
        const localSuccesses = this.count;
        this.successCount = Math.min(Math.max(summaryResp.success_count, localSuccesses), maxPossibleSuccess);
        if (this.successCount !== summaryResp.success_count) {
          const direction = summaryResp.success_count < this.successCount ? 'raised' : 'capped';
          this.loggingService.log(
            `[SessionSummary] Backend reported ${summaryResp.success_count} successes; ${direction} to ${this.successCount} ` +
            `(local: ${localSuccesses} uploaded, ${localFailures} failed)`
          );
        }
        this.failedCount = Math.max(summaryResp.failed_count, localFailures);
        this.skippedDuplicates = this.preservedDuplicates.length;
        this.attemptedCount = this.successCount + this.failedCount;
        if (typeof summaryResp.total_selected === 'number') {
          this.originalSelectedCount = summaryResp.total_selected;
        }
        this.loggingService.log(`[SessionSummary] success=${this.successCount}, failed=${this.failedCount}, attempted=${this.attemptedCount}, total=${this.originalSelectedCount}`);

        // Log failed files for user review
        if (this.failedFiles.length > 0) {
          this.loggingService.log(`[FailedUploads] ${this.failedFiles.length} files failed to upload`);
          this.failedFiles.forEach(({ file, error }) => {
            this.loggingService.log(`  - ${file.name}: ${error}`);
          });
        }
      } else {
        this.loggingService.log('[SessionSummary] Invalid summary response, falling back to local counts');
        this.successCount = this.count;
        this.failedCount = this.failedFiles.length;
        this.attemptedCount = this.successCount + this.failedCount;
      }
    } catch (err) {
      this.loggingService.log('[SessionSummary] Error fetching summary from backend: ' + err);
      this.successCount = this.count;
      this.failedCount = this.failedFiles.length;
      this.attemptedCount = this.successCount + this.failedCount;
    }

    // Safe to drop the resume state now that the counts are settled.
    // Each cleanup step is isolated: previously a single throw here (e.g. a
    // blocked/unavailable localStorage) skipped every line after it — including
    // the upload_completed capture below — even though every file had already
    // uploaded successfully. That made a fully successful upload indistinguishable
    // from a stuck one in the analytics.
    try {
      this.clearUploadState();
    } catch (e) {
      this.loggingService.log('Error clearing upload state: ' + e);
    }

    try {
      // Fetch fresh images in the background
      this.refreshImagesInBackground();
    } catch (e) {
      this.loggingService.log('Error refreshing images in background: ' + e);
    }

    try {
      // Stop speed monitoring
      this.stopSpeedMonitoring();
    } catch (e) {
      this.loggingService.log('Error stopping speed monitoring: ' + e);
    }

    // Fires unconditionally once the actual upload work above is done — this is
    // the "done" signal the upload-health check relies on, so it must not depend
    // on cleanup succeeding.
    this.analytics.capture('upload_completed', {
      event_id: this.eventId,
      attempted_count: this.attemptedCount,
      success_count: this.successCount,
      failed_count: this.failedCount,
      skipped_duplicates: this.skippedDuplicates,
      unverified_count: this.unverifiedFiles.size,
      // The gap this closes: how many of the files the host actually picked
      // could even be read, independent of whether the upload itself
      // succeeded. Previously indistinguishable from a stuck/failed upload.
      //
      // Deliberately NOT this.selectedFiles.length: that array is reassigned
      // twice on the way here — once to drop duplicates, once to drop invalid
      // files — so by this point it holds only what was attempted, which makes
      // the gap look smaller than it is. hostSelectedCount is what the host
      // picked, and unreadable files are counted against attempted, so the
      // two are stated separately rather than subtracted across grains.
      files_selected: hostSelectedCount,
      files_attempted: totalFilesToUpload,
      files_unreadable: this.unreadableFiles.size,
      files_readable: Math.max(0, totalFilesToUpload - this.unreadableFiles.size),
      // Caught by the picking-time check and never attempted, so they are
      // not in files_attempted; counted separately to keep the grains apart.
      files_unreadable_at_pick: this.precheckLeftOut.length,
      // Included in failed_count (they are in failedFiles, kind 'aborted').
      // Separate so a deliberate stop never reads as an upload fault.
      stopped_count: this.stoppedCount,
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });

    // Mark upload as complete - counts are already set from API
    this.uploadComplete = true;
    this.isProcessing = false;

    // Clear minimized state from localStorage — unless the user is watching the
    // banner, in which case dropping it here would make the strip vanish at the
    // exact moment the transfer hit 100%, with the processing leg still to run.
    // The banner clears the snapshot itself once processing is genuinely done.
    if (!this.isMinimized) {
      SafeStorage.removeItem('uploadProcessingState');
    } else {
      this.syncMinimizedProgress();
      // There is no dialog left to close, so nothing else will ever ask the
      // gallery to show what was just uploaded. This runs inside the destroyed
      // component's still-live upload loop, which is the one thing guaranteed
      // to reach this point.
      this.uploadBanner.notifyUploadFinished(
        this.eventId,
        this.count,
        this.failedFiles.length,
        Math.max(this.selectedFiles?.length || 0, this.batchTotal),
        // Lets the gallery learn the photos are in even if the strip was hidden.
        Array.from(this.batchTrackedKeys ?? []),
        this.stoppedCount
      );
    }

    // Start background polling to update processing status (non-blocking)
    this.startBackgroundProcessingPolling();
    // this.closeDialog(this.selectedFiles);
  }

  /**
   * Calls moveOriginals after an initial delay. If the batch contains HEIC/HEIF
   * files (which take longer to process server-side), a second call is made after
   * a longer delay so those files are also moved once they are ready.
   */
  private scheduleMovOriginals(files: File[]): void {
    const hasHeic = files.some(f =>
      f.name.toLowerCase().endsWith('.heic') ||
      f.name.toLowerCase().endsWith('.heif') ||
      f.type === 'image/heic' ||
      f.type === 'image/heif'
    );

    const doMove = (label: string) => {
      this.loggingService.log(`moveOriginals triggered (${label})`);
      this.eventService.moveOriginals(this.eventId).subscribe(
        (res) => this.loggingService.log(`Move Originals Response (${label}): ` + JSON.stringify(res)),
        (error) => this.loggingService.log(`Error moving originals (${label}): ` + error)
      );
    };

    setTimeout(() => doMove('initial'), 5000);

    if (hasHeic) {
      // HEIC files need server-side conversion before they can be moved;
      // retry after 2 minutes to catch any that weren't ready on the first call.
      this.loggingService.log('HEIC files detected – scheduling delayed moveOriginals retry in 2 min');
      setTimeout(() => doMove('heic-retry'), 2 * 60 * 1000);
    }
  }

  // Start background polling to update processing status without blocking UI
  private startBackgroundProcessingPolling(): void {
    const maxWaitTime = 120000; // 2 minutes max
    const startTime = Date.now();
    let pollInterval = 2000; // Start with 2s
    let pollCount = 0;

    const poll = () => {
      pollCount++;

      // Exponential backoff
      if (pollCount < 10) {
        pollInterval = 2000; // 2s for first 10 polls
      } else if (pollCount < 30) {
        pollInterval = 5000; // 5s for next 20 polls
      } else {
        pollInterval = 10000; // 10s after that
      }

      // Check if timeout reached
      if ((Date.now() - startTime) > maxWaitTime) {
        this.loggingService.log('[BackgroundPolling] Max wait time reached, stopping');
        return;
      }

      // Update processing counts from uploadProgressMap
      let completedCount = 0;
      let failedCount = 0;
      let processingCount = 0;

      this.uploadProgressMap.forEach((progress) => {
        const isCompleted = progress.status === 'completed' || progress.progress_percentage === 100 || progress.stage === 'completed';
        const isFailed = progress.status === 'failed' || progress.stage === 'failed';
        if (isCompleted) {
          completedCount++;
        } else if (isFailed) {
          failedCount++;
        } else {
          processingCount++;
        }
      });

      this.processingCompleted = completedCount;
      this.processingFailed = failedCount;
      this.processingRemaining = processingCount;

      // Continue polling if still processing
      if (processingCount > 0) {
        setTimeout(poll, pollInterval);
      } else {
        this.loggingService.log('[BackgroundPolling] All files processed');
      }
    };

    // Start polling
    setTimeout(poll, pollInterval);
  }

  // Wait for all files to complete processing (status = completed or failed)
  private async waitForAllProcessingComplete(): Promise<void> {
    const maxWaitTime = 120000; // 2 minutes max wait
    const checkInterval = 1000; // Check every 1 second
    const startTime = Date.now();

    return new Promise((resolve) => {
      const checkCompletion = () => {
        let allCompleted = true;
        let completedCount = 0;
        let failedCount = 0;
        let processingCount = 0;

        // If we have selectedFiles, check them
        if (this.selectedFiles && this.selectedFiles.length > 0) {
          this.selectedFiles.forEach(file => {
            const progress = this.getProgressForFileName(file.name);
            if (progress) {
              const isCompleted = progress.status === 'completed' || progress.progress_percentage === 100 || progress.stage === 'completed';
              const isFailed = progress.status === 'failed' || progress.stage === 'failed';
              //  const isFailed = progress.status === 'failed' || !!progress.error_stage || progress.stage === 'failed';
              if (isCompleted) {
                completedCount++;
              } else if (isFailed) {
                failedCount++;
              } else {
                allCompleted = false;
                processingCount++;
              }
            } else {
              // No tracking info yet
              allCompleted = false;
              processingCount++;
            }
          });
        } else {
          // When restoring, check uploadProgressMap directly
          if (this.uploadProgressMap.size > 0) {
            this.uploadProgressMap.forEach((progress) => {
              const isCompleted = progress.status === 'completed' || progress.progress_percentage === 100 || progress.stage === 'completed';
              const isFailed = progress.status === 'failed' || progress.stage === 'failed';
              // const isFailed = progress.status === 'failed' || !!progress.error_stage || progress.stage === 'failed';
              if (isCompleted) {
                completedCount++;
              } else if (isFailed) {
                failedCount++;
              } else {
                allCompleted = false;
                processingCount++;
              }
            });
          } else {
            // No data yet, keep waiting
            allCompleted = false;
            processingCount = this.processingRemaining;
          }
        }

        // Update processing progress for UI
        this.processingCompleted = completedCount;
        this.processingFailed = failedCount;
        this.processingRemaining = this.batchTotal
          ? Math.max(this.batchTotal - completedCount - failedCount, processingCount)
          : processingCount;
        if (this.batchTotal) {
          this.restoredLanded = Math.max(this.restoredLanded, this.countLanded());
        }

        const totalFiles = this.selectedFiles?.length || this.uploadProgressMap.size || this.processingRemaining;

        // Update localStorage with current stats and tracking IDs for the indicator
        const processingState = SafeStorage.getItem('uploadProcessingState');
        if (processingState) {
          try {
            const state = JSON.parse(processingState);
            state.completed = completedCount;
            state.failed = failedCount;
            state.remaining = processingCount;

            // Update tracking IDs (they may have changed)
            const trackingIds: number[] = [];
            this.uploadProgressMap.forEach((progress) => {
              if (progress.tracking_id) {
                trackingIds.push(progress.tracking_id);
              }
            });
            state.trackingIds = trackingIds;

            SafeStorage.setItem('uploadProcessingState', JSON.stringify(state));
          } catch (e) {
            // console.error('Error updating processing state:', e); //__CONSOLE_LOG__
          }
        }

        this.loggingService.log(
          `Processing status check: ${completedCount} completed, ${failedCount} failed, ${processingCount} still processing`
        );
        // console.log(`Processing: ${completedCount}/${totalFiles} completed, ${processingCount} remaining`); //__CONSOLE_LOG__        // Check if all done or timeout reached
        if (allCompleted || (Date.now() - startTime) > maxWaitTime) {
          if ((Date.now() - startTime) > maxWaitTime) {
            this.loggingService.log("Max wait time reached, showing completion summary");
          } else {
            this.loggingService.log("All files processing complete!");
          }
          resolve();
        } else {
          // Continue checking
          setTimeout(checkCompletion, checkInterval);
        }
      };

      // Start checking
      checkCompletion();
    });
  }


  /**
   * Queue a write of the resume state, coalescing bursts of completions.
   *
   * saveUploadState() serializes the whole file list and writes it to
   * localStorage synchronously. Calling it per completed file made the cost of
   * persisting one file proportional to the size of the whole batch, so a
   * 1000-file upload performed ~1000 serializations of ~1000 entries each and
   * blocked the main thread on every one of them, interleaved with the uploads
   * they were meant to be recording.
   *
   * The state only has to be good enough to resume from, and it is written
   * from a timer here plus unconditionally at every batch boundary and on
   * unload, so what a crash can cost is a couple of seconds of progress.
   */
  private scheduleSaveUploadState(): void {
    if (this.uploadStateSaveTimer) return;
    this.uploadStateSaveTimer = setTimeout(() => {
      this.uploadStateSaveTimer = null;
      this.saveUploadState();
    }, this.UPLOAD_STATE_SAVE_DEBOUNCE);
  }

  private saveUploadState(): void {
    // Any queued write is now redundant — this one supersedes it.
    if (this.uploadStateSaveTimer) {
      clearTimeout(this.uploadStateSaveTimer);
      this.uploadStateSaveTimer = null;
    }
    if (this.uploadState) {
      this.uploadState.lastUpdated = Date.now();
      SafeStorage.setItem(this.UPLOAD_STATE_KEY, JSON.stringify(this.uploadState));
      this.loggingService.log('Upload state saved');
    }
  }

  private loadUploadState(): boolean {
    const savedState = SafeStorage.getItem(this.UPLOAD_STATE_KEY);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        // Only restore if it's for the same event and user, and less than 24 hours old
        if (state.eventId === this.eventId &&
          state.userId === this.userId &&
          Date.now() - state.lastUpdated < 24 * 60 * 60 * 1000) {
          this.uploadState = state;
          this.loggingService.log('Upload state restored');
          return true;
        }
      } catch (error) {
        this.loggingService.log('Error loading upload state: ' + error);
      }
    }
    return false;
  }

  private clearUploadState(): void {
    // Before the removal, not after: a queued debounced write would otherwise
    // fire afterwards and write the state straight back, leaving a finished
    // upload looking resumable.
    if (this.uploadStateSaveTimer) {
      clearTimeout(this.uploadStateSaveTimer);
      this.uploadStateSaveTimer = null;
    }
    SafeStorage.removeItem(this.UPLOAD_STATE_KEY);
    this.uploadState = null;
    this.uploadStateIndex.clear();
    this.loggingService.log('Upload state cleared');
  }

  // Initialize AWS credentials with caching (reuse for 1 hour)
  private async initializeAWSCredentials(): Promise<S3Client> {
    // If already initializing, wait for that to complete
    if (this.isInitializingCredentials && this.credentialsInitPromise) {
      await this.credentialsInitPromise;
      return this.s3Client!;
    }

    // Check if credentials are still valid (expire in 1 hour, refresh 5 min early)
    const now = Date.now();
    if (this.s3Client && this.credentialsExpiryTime > now + 300000) {
      this.loggingService.log('Using cached AWS credentials');
      return this.s3Client;
    }

    // Initialize new credentials
    this.isInitializingCredentials = true;
    this.credentialsInitPromise = this._initCredentials();

    try {
      await this.credentialsInitPromise;
      return this.s3Client!;
    } finally {
      this.isInitializingCredentials = false;
      this.credentialsInitPromise = null;
    }
  }

  private async _initCredentials(): Promise<void> {
    this.loggingService.log('Initializing fresh AWS credentials');

    const currentUser = await this.fbAuth.currentUser;
    if (!currentUser) {
      throw new Error("No authenticated user found.");
    }

    const firebaseToken = await currentUser.getIdToken(true);

    if (!this.identityPoolId) {
      throw new Error('Missing IdentityPoolId in environment.aws.IdentityPoolId');
    }
    if (!firebaseToken) {
      throw new Error('Failed to acquire firebase token for Cognito Logins');
    }

    const region = 'us-west-1';

    // Create reusable S3 client with Cognito Identity Pool credentials.
    // The provider fetches/refreshes credentials lazily on the first request.
    const credentials = await createCognitoCredentials({
      identityPoolId: this.identityPoolId,
      logins: {
        'securetoken.google.com/picaggo-235807': firebaseToken
      },
      clientConfig: { region }
    });

    // Resolve the credentials here rather than letting the first UploadPart
    // trigger it. The Cognito provider is lazy, so without this the
    // GetCredentialsForIdentity round trip happens inside s3.send() and any
    // Cognito failure arrives as a bare TypeError from the *upload* — which is
    // exactly how a credential problem ends up indistinguishable from S3 being
    // unreachable. Forcing it here keeps credential faults attributable to
    // credentials. The provider caches internally, so this costs at most one
    // round trip and refresh-on-expiry still works through the same provider.
    await inPhase('credentials', async () => { await credentials(); });

    this.s3Client = await createS3Client({ region, credentials });

    // Set expiry time (Cognito credentials valid for 1 hour)
    this.credentialsExpiryTime = Date.now() + (60 * 60 * 1000); // 1 hour

    this.loggingService.log('AWS S3 client initialized and cached');
  }

  /**
   * How many files and how many parts-per-file to keep in flight.
   *
   * These two multiply: 5 files x 3 parts is 15 simultaneous PUTs sharing one
   * pipe. On a degraded link that is actively harmful — each part gets 1/15th
   * of the bandwidth, so a 5MB part that needs ~28s at full speed takes ~7min,
   * and every part is slow enough to look like a stall. Fewer parts in flight
   * on a bad connection finish sooner in wall-clock terms and stay well inside
   * the request timeout, so real hangs stay distinguishable from slow progress.
   *
   * Browsers also cap connections per host at ~6, so anything above that was
   * queueing rather than transferring regardless.
   */
  /**
   * Attempts per file before it is declared lost.
   *
   * Four rather than three because the backoff is now long enough to be worth
   * spending — the old 2s/4s schedule gave up six seconds after the first blip,
   * which is shorter than a typical transient mobile-network interruption. A
   * cause that retrying cannot fix exits early regardless, so raising this does
   * not slow down the failures that were never going to succeed.
   */
  private readonly MAX_UPLOAD_ATTEMPTS = 4;

  /**
   * True once this run's first failure has had its full verdict. Reachability
   * is a fact about the connection, not about any one file, so probes are
   * shared and cached (see getReachability) rather than run once per attempt —
   * but only the FIRST failure may conclude "hosts reachable, so blocked".
   */
  private reachabilityChecked = false;

  /** Last diagnosis in this run, so a batch-level abort can name a cause. */
  private lastDiagnosedFailure?: DiagnosedFailure;

  /**
   * Establish what actually went wrong, rather than recording the error text.
   *
   * A bare `TypeError` is the case that matters: the browser throws it for a
   * dead connection, a blocked host, a rejected CORS preflight and a Blob whose
   * backing file has gone away, wording all four identically. Two cheap checks
   * separate them, cheapest first:
   *
   *  1. Re-read a byte of the source file. A stale picker handle keeps
   *     reporting the `size` captured at pick time, so no size check can catch
   *     it — but reading is local and instant, so it costs nothing to rule out.
   *  2. Probe reachability with no-cors requests, which cannot fail for CORS
   *     reasons and therefore isolate genuine transport failures.
   *
   * Anything the SDK already described structurally (an HTTP status, an AWS
   * fault) skips both checks — that error is not ambiguous to begin with.
   */
  private async diagnoseUploadFailure(error: any, file: File): Promise<DiagnosedFailure> {
    const facts = classifyUploadError(error, 'simple-upload');
    this.lastDiagnosedFailure = facts;

    if (!needsReachabilityProbe(facts)) return facts;

    if (!(await isFileStillReadable(file))) {
      const unreadable: DiagnosedFailure = {
        ...facts,
        kind: 'file-unreadable',
        retryable: false,
        error_message:
          `Source file is no longer readable (original: ${facts.error_message}). ` +
          `The browser refused to read the picked file; why is not known.`,
      };
      this.lastDiagnosedFailure = unreadable;
      return unreadable;
    }

    // The first failure in a run gets the full verdict, as before. Later ones
    // only change course when the connection is actually the problem: a probe
    // that says every host answers proves nothing about a request that died,
    // and "blocked" for every later file would write them off one by one.
    // What did change is that later failures see a CURRENT probe — it used to
    // run once per run, so a connection that went bad halfway through was never
    // noticed by any file but the first.
    const firstProbe = !this.reachabilityChecked;
    this.reachabilityChecked = true;

    try {
      const report = await this.getReachability();
      const connectivityProblem = !report.s3_reachable || !report.cognito_reachable;
      if (!firstProbe && !connectivityProblem) return facts;

      const refined = { ...refineWithReachability(facts, report, this.linkQuality()), ...report };
      this.lastDiagnosedFailure = refined;
      return refined;
    } catch (probeErr) {
      // A probe failure must never replace the real error.
      this.loggingService.log(`Reachability probe itself failed: ${describeError(probeErr)}`);
      return facts;
    }
  }

  private getUploadConcurrency(): { files: number; parts: number } {
    const connection = (navigator as any).connection;
    const effectiveType: string | undefined = connection?.effectiveType;

    // Respect an explicit user request to minimise data usage.
    if (connection?.saveData) return { files: 1, parts: 1 };

    switch (effectiveType) {
      case 'slow-2g':
      case '2g':
        return { files: 1, parts: 1 };
      case '3g':
        return { files: 2, parts: 2 };
      default:
        // '4g' or unknown (older browsers, Safari, Firefox) — keep the
        // previous behaviour rather than penalising an unreported connection.
        return this.isMobile ? { files: 3, parts: 3 } : { files: 5, parts: 3 };
    }
  }

  /**
   * Runs `task` over `items` with at most `limit` running at any time.
   * Browsers only allow ~6 connections per host, so firing a 100-file batch
   * with Promise.allSettled just queued 90+ requests that stalled, timed out
   * or died under memory pressure. Same result shape as Promise.allSettled.
   */
  private async runWithConcurrency<T>(
    items: T[],
    limit: number,
    task: (item: T, index: number) => Promise<void>
  ): Promise<PromiseSettledResult<void>[]> {
    const results: PromiseSettledResult<void>[] = new Array(items.length);
    let nextIndex = 0;

    const worker = async () => {
      while (true) {
        const i = nextIndex++;
        if (i >= items.length) return;
        try {
          await task(items[i], i);
          results[i] = { status: 'fulfilled', value: undefined };
        } catch (reason) {
          results[i] = { status: 'rejected', reason };
        }
      }
    };

    const workerCount = Math.max(1, Math.min(limit, items.length));
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    return results;
  }

  /**
   * Bound handlers, kept so ngOnDestroy can actually remove them. Arrow-function
   * properties (not methods) because `removeEventListener` needs the identical
   * reference that was added.
   */
  private readonly onNetworkStateChange = () => {
    // Honest logging only. There is no auto-resume: the previous handler said
    // "upload will auto-resume when online" while doing nothing but assigning
    // a local variable nothing else read. An offline stretch is handled by the
    // retry loop's online-wait below, not by anything here.
    this.loggingService.log(
      navigator.onLine
        ? 'Network connection restored'
        : 'Network connection lost - in-flight uploads will retry'
    );
  };

  private readonly onBeforeUnloadDuringUpload = (event: BeforeUnloadEvent) => {
    if (this.isFileLoading) {
      // Flush whatever is sitting on the debounce timer while there is still a
      // page to flush it from — this is exactly the crash the resume state
      // exists for. A synchronous localStorage write is permitted here.
      this.saveUploadState();

      // No alert() here — browsers ignore it inside beforeunload and show
      // their own generic prompt. Calling it just blocked the handler.
      event.preventDefault();
      event.returnValue = 'Your files are getting uploaded. Leaving now will cancel it.';
    }
  };

  private uploadWindowListenersBound = false;

  private registerUploadWindowListeners(): void {
    if (this.uploadWindowListenersBound) return;
    window.addEventListener('online', this.onNetworkStateChange);
    window.addEventListener('offline', this.onNetworkStateChange);
    window.addEventListener('beforeunload', this.onBeforeUnloadDuringUpload);
    this.uploadWindowListenersBound = true;
  }

  private removeUploadWindowListeners(): void {
    if (!this.uploadWindowListenersBound) return;
    window.removeEventListener('online', this.onNetworkStateChange);
    window.removeEventListener('offline', this.onNetworkStateChange);
    window.removeEventListener('beforeunload', this.onBeforeUnloadDuringUpload);
    this.uploadWindowListenersBound = false;
  }

  /**
   * Wait out a network outage before burning a retry.
   *
   * Three retries with 2s/4s backoff take ~6 seconds, so a brief connectivity
   * drop used to consume every attempt and fail the file before the network
   * came back. Bounded so a genuinely offline device still fails rather than
   * hanging the upload forever.
   */
  private async waitForNetwork(maxWaitMs = 60000): Promise<boolean> {
    if (navigator.onLine) return true;

    this.loggingService.log(`Offline — waiting up to ${maxWaitMs / 1000}s for connectivity before retrying`);

    return new Promise<boolean>(resolve => {
      let settled = false;
      const finish = (online: boolean) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('online', onOnline);
        clearTimeout(timer);
        resolve(online);
      };
      const onOnline = () => finish(true);
      const timer = setTimeout(() => finish(navigator.onLine), maxWaitMs);
      window.addEventListener('online', onOnline);
    });
  }

  // --- A connection that is down, or too weak to carry the upload ------------

  /** What the browser says about the link; every field is optional by design. */
  private linkQuality(): { effectiveType?: string; rtt?: number; downlink?: number } {
    const c = (navigator as any).connection;
    return { effectiveType: c?.effectiveType, rtt: c?.rtt, downlink: c?.downlink };
  }

  private reachabilityOrigins(): { s3: string; cognito: string; control: string } {
    const region = environment.aws.region;
    return {
      s3: `https://${environment.aws.bucketName}.s3.${region}.amazonaws.com/`,
      cognito: `https://cognito-identity.${region}.amazonaws.com/`,
      control: new URL(environment.uploadProgressApi).origin,
    };
  }

  private reachabilityProbe: { at: number; promise: Promise<ReachabilityReport> } | null = null;

  /**
   * A probe no older than 15 seconds, shared by whoever asks. Three workers
   * failing together would otherwise send nine probes for one answer.
   */
  private getReachability(timeoutMs = 5000): Promise<ReachabilityReport> {
    const cached = this.reachabilityProbe;
    if (cached && Date.now() - cached.at < 15000) {
      return cached.promise;
    }
    return this.probeReachabilityNow(timeoutMs);
  }

  /** Always asks the network, and makes the answer the shared one. */
  private probeReachabilityNow(timeoutMs = 5000): Promise<ReachabilityReport> {
    const origins = this.reachabilityOrigins();
    const promise = probeUploadReachability(origins.s3, origins.cognito, origins.control, timeoutMs)
      .then(report => {
        this.loggingService.log(`Upload reachability probe: ${JSON.stringify(report)}`);
        return report;
      });
    this.reachabilityProbe = { at: Date.now(), promise };
    return promise;
  }

  /** Causes that say "the connection", not "this file". */
  private isConnectionFailure(kind: UploadFailureKind): boolean {
    return kind === 'offline' || kind === 'aws-unreachable' || kind === 'timeout';
  }

  /**
   * Hold every worker until the connection can carry an upload again, or give up.
   *
   * Spending retry attempts on an outage is how a 20-second dead patch used to
   * cost a file all four of its tries. One shared wait instead: the first
   * failing worker starts it, the rest join it, and they all resume together.
   */
  private waitForConnectionRecovery(maxWaitMs = 5 * 60 * 1000): Promise<boolean> {
    if (!this.connectionRecovery) {
      this.connectionRecovery = this.runConnectionRecovery(maxWaitMs).finally(() => {
        this.connectionRecovery = null;
      });
    }
    return this.connectionRecovery;
  }

  private async runConnectionRecovery(maxWaitMs: number): Promise<boolean> {
    const started = Date.now();
    this.connectionNotice = waitingForConnectionNotice();
    this.loggingService.log(`Waiting for the connection to come back (up to ${Math.round(maxWaitMs / 1000)}s)`);
    this.analytics.capture('upload_waiting_for_connection', {
      event_id: this.eventId,
      cause: 'network',
      connection_type: (navigator as any).connection?.effectiveType,
      network_online: navigator.onLine,
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });
    // A connection this bad is exactly when the log is most worth having.
    this.loggingService.sendFailureLog('upload paused: connection lost or too weak');

    let recovered = false;
    let delay = 4000;
    try {
      // A stop ends the wait too: otherwise "Stop uploading" could sit behind
      // up to five minutes of waiting for a connection nobody needs any more.
      while (Date.now() - started < maxWaitMs && !this.stopRequested) {
        // Asks first and waits second: a one-off timeout on a healthy
        // connection should cost one probe, not a fixed pause.
        // A longer budget than the diagnosis probe — this is the question
        // "is it back?", and on a weak link five seconds is too short to say yes.
        const report = await this.probeReachabilityNow(8000);
        if (report.s3_reachable && report.cognito_reachable) {
          recovered = true;
          break;
        }
        if (!navigator.onLine) {
          await this.waitForNetwork(15000);
        } else {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        delay = Math.min(Math.round(delay * 1.5), 15000);
      }
    } finally {
      this.connectionNotice = '';
      // The next failure deserves a fresh verdict, not this outage's.
      this.reachabilityChecked = false;
      this.reachabilityProbe = null;
      // Time spent waiting is not time the upload was stalled. Without this the
      // very next speed check sees "no progress for minutes" and announces a
      // stall the moment uploads resume.
      this.lastProgressAt = Date.now();
      // A "stopped responding" line from before the wait would otherwise
      // outlive the outage it described. Only that line: the ordinary
      // slow-network message is raised once per run and is still true.
      if (this.stalledNow) {
        this.slowNetworkMessage = '';
        this.isSlowNetwork = false;
        this.stalledNow = false;
      }
    }

    const waitedMs = Date.now() - started;
    // Cut short by a stop: the connection did not give up, the person did, and
    // the stop has its own event.
    if (!recovered && this.stopRequested) {
      this.loggingService.log(`Stopped while waiting ${Math.round(waitedMs / 1000)}s for the connection`);
      return false;
    }
    this.loggingService.log(
      recovered
        ? `Connection is back after ${Math.round(waitedMs / 1000)}s — resuming uploads`
        : `Connection did not come back within ${Math.round(waitedMs / 1000)}s — giving up on the queued files`
    );
    // `cause` and `role` match every other upload event, so "the network was
    // down and came back" can be told apart from a failure in one filter.
    this.analytics.capture(recovered ? 'upload_connection_recovered' : 'upload_connection_gave_up', {
      event_id: this.eventId,
      waited_ms: waitedMs,
      cause: 'network',
      network_online: navigator.onLine,
      connection_type: (navigator as any).connection?.effectiveType,
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });
    return recovered;
  }

  // --- What we know about a picked file's age --------------------------------

  /**
   * Two facts a failure report is much more useful with:
   *  - how long after picking it happened — the failing logs showed reads that
   *    worked at under a second and failed at about twenty;
   *  - whether the file's modified time is the moment it was picked. Every
   *    unreadable file inspected had it, which is what a temporary copy made by
   *    the picker looks like. Recorded so it can be compared with the files
   *    that uploaded fine, which the logs never printed.
   */
  private pickContext(file: File): { ms_since_pick?: number; mtime_is_pick_time?: boolean } {
    const picked = this.pickedAt.get(file);
    if (picked === undefined) return {};
    return {
      ms_since_pick: Date.now() - picked,
      mtime_is_pick_time: Math.abs(file.lastModified - picked) <= 5000,
    };
  }

  // --- Telling the server a file is not going to make it ---------------------

  /**
   * Queue a correction for the tracking row initBulk wrote for this file.
   *
   * Only worth sending if a row exists (initBulk succeeded) and the feature is
   * on. Best effort throughout: reporting must never be able to break, delay or
   * fail an upload.
   */
  private reportFailureToBackend(file: File, facts: DiagnosedFailure | undefined, message: string, attempts: number): void {
    if (!environment.features.reportFailedUploads) return;
    if (!this.uploadProgressMap.get(file.name)?.tracking_id) return;

    this.failureReportQueue.push({
      s3_key: this.buildS3Key(this.userId, this.eventId, file),
      error_stage: facts?.phase ?? facts?.kind ?? 'client',
      error_message: `${facts?.kind ?? 'unknown'}: ${message}`.slice(0, 500),
      retry_count: attempts,
    });
    if (!this.failureReportTimer) {
      this.failureReportTimer = setTimeout(() => { void this.flushFailureReports(); }, 1500);
    }
  }

  private async flushFailureReports(): Promise<void> {
    if (this.failureReportTimer) {
      clearTimeout(this.failureReportTimer);
      this.failureReportTimer = null;
    }
    const failures = this.failureReportQueue.splice(0);
    if (!failures.length) return;

    try {
      await this.http.post(`${this.uploadProgressApiUrl}?action=markFailed`, {
        event_id: parseInt(this.eventId),
        upload_session_id: this.uploadSessionId,
        failures,
      }, { headers: { 'Content-Type': 'application/json' } }).toPromise();
      this.loggingService.log(`Told the server ${failures.length} file(s) did not upload`);
    } catch (reportErr) {
      this.loggingService.log(`Could not tell the server about ${failures.length} failed file(s): ${describeError(reportErr)}`);
    }
  }

  /**
   * Confirm an object landed, checked the moment its upload resolved.
   *
   * Three outcomes, kept strictly distinct so the user is told what actually
   * happened rather than a convenient approximation:
   *
   *  - present, size matches   -> 'verified'
   *  - present, size differs   -> throws; truncation is proof of damage
   *  - absent after all retries-> throws; the file is not in S3
   *  - cannot be checked       -> 'unverified'; reported as its own outcome,
   *                               counted as neither success nor failure
   *
   * A 404 is retried rather than trusted first time, because the ingest
   * trigger can legitimately remove an object from the `pwa/` staging prefix
   * shortly after it lands. Retrying costs a few seconds only on the files
   * that look wrong, and lets a genuinely missing file be reported as missing
   * instead of silently passing.
   *
   * An `etag` from the upload response is trusted outright and skips all of
   * the above: S3 only hands one back once the write is durably committed, on
   * the same request that performed it, so there is no race window for the
   * ingest trigger to delete the object out from under a later check. The
   * HeadObject retry loop only runs when no ETag came back.
   */
  private async confirmUploadLanded(
    s3: S3Client,
    s3Key: string,
    file: File,
    etag?: string,
    expectedSize: number = file.size
  ): Promise<'verified' | 'unverified'> {
    if (etag) {
      this.loggingService.log(`Verified via upload ETag: ${s3Key} (${etag})`);
      return 'verified';
    }

    // If the credentials plainly can't read the bucket, stop paying for checks
    // that can only ever come back denied.
    if (this.verificationDisabled) {
      this.recordUnverified(file, 'S3 verification unavailable (access denied)');
      return 'unverified';
    }

    const { HeadObjectCommand } = await loadS3ClientModule();
    let lastError: any = null;

    for (let attempt = 0; attempt <= this.VERIFY_RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) {
        const wait = this.VERIFY_RETRY_DELAYS_MS[attempt - 1];
        this.loggingService.log(
          `Post-upload check: ${s3Key} not confirmed yet, re-checking in ${wait}ms (attempt ${attempt + 1})`
        );
        await new Promise(resolve => setTimeout(resolve, wait));
      }

      try {
        const head: any = await s3.send(new HeadObjectCommand({
          Bucket: environment.aws.bucketName,
          Key: s3Key
        }));

        const storedSize = typeof head?.ContentLength === 'number' ? head.ContentLength : -1;
        if (storedSize >= 0 && storedSize !== expectedSize) {
          throw new UploadVerificationError(
            `Size mismatch in S3: expected ${expectedSize} bytes, found ${storedSize}`
          );
        }

        this.consecutiveVerifyDenials = 0;
        this.loggingService.log(`Verified in S3: ${s3Key} (${storedSize} bytes)`);
        return 'verified';
      } catch (err: any) {
        // Our own verdict — never retry past it.
        if (err instanceof UploadVerificationError) throw err;
        lastError = err;
      }
    }

    const attempts = this.VERIFY_RETRY_DELAYS_MS.length + 1;
    const isAbsent = lastError?.name === 'NotFound' || lastError?.$metadata?.httpStatusCode === 404;

    if (isAbsent) {
      // Checked repeatedly over several seconds and it was never there.
      this.consecutiveVerifyDenials = 0;
      throw new UploadVerificationError(
        `Not found in S3 after upload (checked ${attempts} times over ` +
        `${this.VERIFY_RETRY_DELAYS_MS.reduce((a, b) => a + b, 0) / 1000}s)`
      );
    }

    // Couldn't get an answer — denied, network, throttled. Not success, not
    // failure. Say so.
    const reason = describeError(lastError);
    this.consecutiveVerifyDenials++;
    if (this.consecutiveVerifyDenials >= this.VERIFY_DENIAL_LIMIT && !this.verificationDisabled) {
      this.verificationDisabled = true;
      this.loggingService.logError(
        `S3 verification disabled after ${this.VERIFY_DENIAL_LIMIT} consecutive failures — ` +
        `uploads will continue but cannot be confirmed. Check that the Cognito role has ` +
        `s3:GetObject on ${environment.aws.bucketName}/${environment.aws.uploadFolder}/*`,
        lastError
      );
    }

    this.loggingService.log(`Post-upload check could not confirm ${s3Key}: ${reason}`);
    this.recordUnverified(file, `Could not verify in S3: ${reason}`);
    return 'unverified';
  }

  /** Record a file as uploaded-but-unconfirmed, once per file. */
  private recordUnverified(file: File, reason: string): void {
    if (this.unverifiedFiles.has(file)) return;
    this.unverifiedFiles.set(file, reason);

    const progress = this.uploadProgressMap.get(file.name);
    if (progress) {
      progress.error_message = reason;
    }
  }


  private async processRetryQueue(retryQueue: File[]): Promise<void> {
    while (retryQueue.length > 0 && navigator.onLine) {
      const file = retryQueue.shift();
      if (!file) continue;

      try {
        await this.uploadFile(this.userId, this.eventId, file);
        this.loggingService.log(`Retry successful for file: ${file.name}`);
      } catch (error) {
        this.loggingService.log(`Retry failed for file: ${file.name}`);
        retryQueue.push(file); // Put back in queue if failed
        await new Promise(resolve => setTimeout(resolve, 3000)); // Wait before next retry
      }
    }
  }
  // Utility function to format time in hh:mm:ss
  // Utility function to format time in words
  formatTimeWords(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    let timeWords = [];

    if (hours > 0) {
      timeWords.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
      timeWords.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
    }
    if (remainingSeconds > 0) {
      timeWords.push(`${remainingSeconds} second${remainingSeconds > 1 ? 's' : ''}`);
    }

    return timeWords.length > 0 ? timeWords.join(' ') : "less than a second";
  }

  // Calculate current upload speed and detect slow network
  private startSpeedMonitoring(): void {
    // Clear any existing interval
    if (this.speedMonitoringInterval) {
      clearInterval(this.speedMonitoringInterval);
    }

    // Monitor speed every SPEED_CHECK_INTERVAL
    this.speedMonitoringInterval = setInterval(() => {
      this.calculateUploadSpeed();
    }, this.SPEED_CHECK_INTERVAL);
  }

  private stopSpeedMonitoring(): void {
    if (this.speedMonitoringInterval) {
      clearInterval(this.speedMonitoringInterval);
      this.speedMonitoringInterval = null;
    }
  }

  /**
   * Undo everything runUploadImages() starts before it knows whether there is
   * anything to upload.
   *
   * The bail-outs below it — nothing left after deduping, nothing left after
   * validation — used to clear the loading flag and return, leaving the speed
   * monitor and the session keepalive running for the life of the dialog. Both
   * are intervals, so they kept firing against a run that had already given up,
   * and the keepalive kept refreshing a session for an upload that never
   * happened. Releasing them in one place means a new bail-out cannot forget
   * half of it.
   */
  private abortUploadRun(reason: string): void {
    this.loggingService.log(`Upload run aborted: ${reason}`);
    this.isFileLoading = false;
    this.stopSpeedMonitoring();
    this.stopSessionKeepalive();
    this.uploadBanner.endEngineHeartbeat();
    this.connectionNotice = '';
  }

  private calculateUploadSpeed(): void {
    const now = Date.now();
    const timeDiff = now - this.lastSpeedCheckTime;

    // Need at least some time to calculate speed
    if (timeDiff < 100) {
      return;
    }

    // totalUploadedBytes only advances when a whole file completes, so a single
    // large video produced a flat zero for its entire upload: no speed samples,
    // "--" on the readout, and the slow-network warning could never fire.
    // Prefer the live part-level total; fall back to the per-file figure when
    // there is no selection to measure (a restored/minimised session).
    const liveBytes = this.selectedFiles?.length
      ? this.uploadByteTotals().done
      : this.totalUploadedBytes;

    // Calculate bytes uploaded since last check
    const bytesDiff = liveBytes - this.lastUploadedBytes;

    // Progress, or the lack of it. The sampler below throws zero readings away,
    // so it can never notice a stall on its own — see lastProgressAt.
    if (bytesDiff > 0) {
      this.lastProgressAt = now;
      if (this.stalledNow) {
        this.stalledNow = false;
        this.isSlowNetwork = false;
        this.slowNetworkMessage = '';
      }
    } else if (
      this.isFileLoading &&
      !this.connectionNotice &&
      !this.stalledNow &&
      !this.stallProbeInFlight &&
      now - this.lastProgressAt >= this.STALL_AFTER_MS
    ) {
      void this.explainStall(now - this.lastProgressAt);
    }

    // Calculate speed in KB/s
    const speedKBps = (bytesDiff / 1024) / (timeDiff / 1000);

    // Only add valid speed samples (not zero or negative)
    if (speedKBps > 0) {
      // Store speed sample (keep last 5 samples for averaging)
      this.uploadSpeedSamples.push(speedKBps);
      if (this.uploadSpeedSamples.length > 5) {
        this.uploadSpeedSamples.shift();
      }
    }

    // Calculate average speed if we have samples
    if (this.uploadSpeedSamples.length > 0) {
      const avgSpeed = this.uploadSpeedSamples.reduce((a, b) => a + b, 0) / this.uploadSpeedSamples.length;

      // Format speed for display
      if (avgSpeed < 1024) {
        this.currentUploadSpeed = `${Math.round(avgSpeed)} KB/s`;
      } else {
        this.currentUploadSpeed = `${(avgSpeed / 1024).toFixed(2)} MB/s`;
      }

      // Detect slow network (need at least 2 samples to be reliable)
      if (this.uploadSpeedSamples.length >= 2 && avgSpeed < this.SLOW_NETWORK_THRESHOLD) {
        this.isSlowNetwork = true;

        // Show alert only once per upload session
        if (!this.slowNetworkAlertShown) {
          this.slowNetworkAlertShown = true;
          this.showSlowNetworkAlert(avgSpeed);
        }
      } else if (!this.stalledNow && avgSpeed >= this.SLOW_NETWORK_THRESHOLD * 1.5) {
        // Network improved - reset slow network flag. Not while stalled: the
        // average is built from samples taken before progress stopped, so it
        // reads "fine" for as long as nothing new arrives, which is exactly
        // when the message has to stay.
        this.isSlowNetwork = false;
        this.slowNetworkMessage = ''; // Clear the message
      }

      this.loggingService.log(`[NetworkSpeed] Current: ${this.currentUploadSpeed}, Avg: ${Math.round(avgSpeed)} KB/s, Slow: ${this.isSlowNetwork}`);
    }

    // Update for next check
    this.lastSpeedCheckTime = now;
    this.lastUploadedBytes = liveBytes;
  }

  /**
   * No byte has landed for a while. Ask the network why, and say that.
   *
   *  - AWS (or the whole connection) not answering -> "stopped responding".
   *  - Everything answers but nothing moves -> "slow right now". Both are true
   *    of a weak link; only one of them is true of a dead one, and telling a
   *    person who is merely slow that their connection has died is as wrong as
   *    the reverse.
   */
  private async explainStall(silentMs: number): Promise<void> {
    this.stallProbeInFlight = true;
    try {
      const report = await this.getReachability();
      if (!this.isFileLoading) return;

      const stopped = !report.s3_reachable || !report.cognito_reachable;
      this.stalledNow = true;
      this.isSlowNetwork = true;
      this.slowNetworkMessage = stopped ? stalledConnectionNotice() : slowConnectionNotice(0);

      this.loggingService.log(
        `[SlowNetwork] No progress for ${Math.round(silentMs / 1000)}s — ${stopped ? 'AWS not answering' : 'connection answers but is very slow'}`
      );
      if (!this.stallReported) {
        this.stallReported = true;
        this.analytics.capture('upload_stalled', {
          event_id: this.eventId,
          silent_for_ms: silentMs,
          explained_as: stopped ? 'not-answering' : 'very-slow',
          cause: 'network',
          connection_type: (navigator as any).connection?.effectiveType,
          ...report,
          surface: this.isMobile ? 'mobile' : 'desktop',
          role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
        });
      }
    } catch {
      // A probe that cannot run leaves the message as it was.
    } finally {
      this.stallProbeInFlight = false;
    }
  }

  // Show slow network alert to user
  private showSlowNetworkAlert(speedKBps: number): void {
    this.loggingService.log(`[SlowNetwork] Alert shown - Speed: ${Math.round(speedKBps)} KB/s`);

    // Set message to display in UI
    this.slowNetworkMessage = slowConnectionNotice(speedKBps);
  }
  // async uploadImage() {
  //   this.count = 0;
  //   this.isFileLoading = true;

  //   if (!this.selectedFiles || this.selectedFiles.length === 0) {
  //     console.error("No files selected.");
  //     return;
  //   }

  //   let _platform = '';
  //   const _device = this.getDeviceType();
  //   const userAgent = navigator.userAgent;
  //   if (userAgent.match(/iPad|iPhone|iPod/)) {
  //     _platform = 'iOS';
  //   } else if (userAgent.match(/Linux|Android/)) {
  //     _platform = 'Android';
  //   } else {
  //     _platform = 'Other';
  //   }

  //   // Initialize file progress tracking
  //   this.files = this.selectedFiles.map((file) => ({ name: file.name, progress: 0 }));

  //   const uploadPromises = this.selectedFiles.map(async (file, i) => {
  //     if (!file || !file.name) {
  //       console.error(`File or file name is undefined for file at index ${i}`);
  //       return;
  //     }

  //     this.uploadedCheck[i] = false;
  //     try {
  //       this.loggingService.log("upload starting");
  //       const data = await this.uploadFile(this.userId, this.eventId, file);
  //       console.log("File uploaded successfully:", data);
  //       this.uploadedCheck[i] = true;
  //       //   this.loggingService.log("upload status: File uploaded successfully");
  //       setTimeout(() => {
  //         this.eventService.moveOriginals(this.eventId).subscribe(
  //           (res) => console.log("move result:", res),
  //           (error) => console.error("Error fetching data:", error)
  //         );
  //       }, 8000);

  //       this.count++;
  //       this.currentUploadIndex = i;
  //     } catch (error) {
  //       this.loggingService.log("upload error:" + error);

  //       console.error("Error uploading file:", error);
  //     }
  //     //this.uploadFile(this.userId, this.eventId, file, _platform, _device)

  //   });

  //   await Promise.all(uploadPromises);

  //   this.isFileLoading = false;
  //   this.loggingService.log("upload status: File uploaded successfully");
  //   console.log("All files uploaded successfully!");
  //   this.closeDialog(this.files);
  //   // if (this.count === this.files.length) {
  //   //   setTimeout(() => {
  //   //     this.closeDialog(this.files);
  //   //   }, 5000);
  //   // }
  // }

  async uploadFile(userId: string, eventId: string, file: File) {
    // Validate file before attempting upload
    if (!file) {
      this.loggingService.log("uploadFile: File is null/undefined");
      throw new Error("Invalid file object");
    }

    if (!file.name) {
      this.loggingService.log("uploadFile: File has no name");
      throw new Error("File has no name");
    }

    this.loggingService.log(`uploadFile: Starting upload for ${file.name} (size: ${file.size}, type: ${file.type})`);

    // Prove the picker handle is still live before spending credentials, a
    // tracking row and up to MAX_UPLOAD_ATTEMPTS network attempts on it.
    //
    // A stale handle (cloud-only/moved photo, or one whose OS-granted access
    // expired) keeps reporting the `size` captured at pick time, so no size
    // check can catch it; handing it to fetch makes the *fetch* reject with
    // `TypeError: Failed to fetch`, indistinguishable from a network fault.
    // Reading one byte here settles that question up front.
    //
    // Deliberately a probe and not a full read: buffering the whole file would
    // serialize the disk read against the network instead of pipelining them,
    // and would hold entire videos in the JS heap. Everything below streams
    // from the `File` handle, which lets the browser read each slice inside
    // the network layer without ever materializing it in JS.
    const contentType = this.resolveContentType(file);
    try {
      await inPhase('read-file', () => this.assertFileReadable(file));
    } catch (readError) {
      this.unreadableFiles.add(file.name);
      this.analytics.capture('file_missing_from_library', {
        event_id: this.eventId,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || 'unknown',
        ...this.pickContext(file),
        surface: this.isMobile ? 'mobile' : 'desktop',
        // 'passed' = readable when picked, lost by upload time: the handle
        // went stale, which the picking-time check alone cannot fix.
        precheck: this.precheckRan ? 'passed' : 'not_run',
      });
      throw readError instanceof Error ? readError : new Error(String(readError));
    }

    if (!file.size) {
      throw new Error(`File ${file.name} has invalid size (0 bytes)`);
    }

    // Get cached S3 client (will initialize if needed or expired)
    let s3: S3Client;
    try {
      s3 = await this.initializeAWSCredentials();
      if (!s3) {
        throw new Error('Failed to initialize S3 client');
      }
    } catch (credError) {
      const credMsg = describeError(credError);
      this.loggingService.logError(`AWS credentials failed for ${file.name}`, credError);
      throw new Error(`AWS credentials failed: ${credMsg}`);
    }

    // console.log({ s3 });
    let _metadata;
    const fileName = this.resolveUploadFileName(file);

    // One key, built once, used for the PUT, for progress tracking and for
    // post-batch verification. These used to be three separate template
    // strings that could disagree.
    const s3Key = this.buildS3Key(userId, eventId, file);
    // Add S3 key to tracking set (tracking was initialized in bulk before upload)
    this.s3KeysToTrack.add(s3Key);
    // Parallel record that survives completion — see batchS3Keys.
    this.batchS3Keys.add(s3Key);
    if (!this.bulkProgressInterval) {
      this.startBulkProgressPolling();
    }

    try {
      if (this.selectedSubfolder == undefined || this.selectedSubfolder == null || this.selectedSubfolder == '') {
        this.selectedSubfolder = 'null'; // Default subfolder if none selected
      }
      // The '0' guest id is already applied inside buildS3Key; this branch
      // decides whether the object also carries guest-identifying metadata.
      // Previously gated on `userId == undefined` too, but a guest's own
      // uploads almost always DO have a resolved host userId (it's what
      // buildS3Key uses for the folder) — so that condition was false for
      // the common case and most guest uploads carried no identity at all.
      const isGuestUpload = SafeStorage.getItem('isGuestUser') === 'true';
      if (isGuestUpload) {
        const currentUser = await this.fbAuth.currentUser;
        _metadata = {
          platform: this.getPlatform(),
          device: this.getDeviceType(),
          subfolderId: this.selectedSubfolder?.toString(),
          // The guest's own DB user id — the actual, linkable identity.
          // firebase_id is kept alongside it rather than replaced, since
          // it's unknown what (if anything) on the backend still reads it.
          guest_user_db_id: SafeStorage.getItem('guestUserDbId') || 'unknown',
          firebase_id: currentUser?.uid || 'unknown'
        };
      } else {
        _metadata = {
          platform: this.getPlatform(),
          device: this.getDeviceType(),
          subfolderId: this.selectedSubfolder?.toString(),
        };
      }
      const metadata: { [key: string]: string } = _metadata;
      const params = {
        Bucket: environment.aws.bucketName,
        Key: s3Key,
        Metadata: metadata,
        ContentType: contentType
      };

      // Threshold checked against the source file: nothing transforms the
      // bytes on the way out, so its size is the size being sent.
      let etag: string | undefined;
      if (file.size > 5 * 1024 * 1024) {
        this.loggingService.log("Using Multipart Upload: " + fileName);
        etag = await this.multipartUpload(file, params, s3);
      } else {
        this.loggingService.log("Using Simple Upload: " + fileName);
        etag = await inPhase('simple-upload', () => this.simpleUpload(file, params, s3));
      }

      // Confirm now, while the object is still in the staging prefix.
      await inPhase('verify', () => this.confirmUploadLanded(s3, s3Key, file, etag, file.size));
    } catch (error) {
      this.loggingService.logError(`S3 upload error for ${fileName} (bucket=${environment.aws.bucketName}, key=${s3Key})`, error);
      // Must rethrow: the caller's retry loop and failure accounting depend on
      // it. Swallowing here made every failed upload look successful.
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  /**
   * Throw unless the picked file's bytes can still be reached.
   *
   * On Android/iOS a `File` from the picker is a handle, not a copy — reading
   * it can trigger the OS to fetch the underlying content (e.g. a Google
   * Photos item not yet on-device) in the background. A failed first read
   * doesn't mean the bytes are gone for good; it can mean that fetch just
   * hadn't finished yet, so one retry after a short pause gives it a chance to
   * land before this is reported as a real "file unreadable" failure.
   *
   * Reads one byte, via the same helper the reactive diagnosis path uses, so
   * both the proactive and reactive checks agree on what "readable" means.
   * Reading more would not make the answer more true — a handle either
   * resolves or it doesn't — and reading the whole file costs the pipelining
   * between disk and network that large uploads depend on.
   */
  private async assertFileReadable(file: File, maxAttempts = 2): Promise<void> {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (await isFileStillReadable(file)) return;
      this.loggingService.log(`[ReadCheck] Attempt ${attempt}/${maxAttempts} failed for ${file.name}`);
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 400));
      }
    }
    // Matches the text classifyUploadError already recognises as
    // 'file-unreadable' — reusing that instead of inventing a new kind keeps
    // this failure grouped with the reactive check in upload-error.util.ts.
    const err = new Error(
      `Source file is no longer readable. ` +
      `The browser refused to read the picked file; why is not known.`
    );
    err.name = 'NotReadableError';
    throw err;
  }

  /**
   * On the first unreadable pick, probe every file not yet started and mark
   * the unreadable ones, so their worker turns fail instantly and quietly.
   *
   * Runs once per upload run and is not awaited: readable files keep
   * uploading alongside it, and a worker that reaches a file before the sweep
   * does simply takes the normal path, whose own probe gives the same answer.
   * The probe is a one-byte read, so it doesn't compete with the uploads.
   */
  private sweepQueuedForUnreadable(files: File[], started: Set<File>): void {
    if (this.unreadableSweep) return;
    const queued = files.filter(f => !started.has(f));
    this.loggingService.log(`[ReadCheck] First unreadable file — checking ${queued.length} queued file(s) up front`);

    this.unreadableSweep = this.runWithConcurrency(queued, 6, async file => {
      if (started.has(file)) return;
      try {
        await this.assertFileReadable(file);
      } catch {
        this.knownUnreadable.add(file);
        // Shown in the in-progress notice now, not only once the run ends.
        this.unreadableFiles.add(file.name);
      }
    }).then(() => {
      this.loggingService.log(`[ReadCheck] Sweep done: ${this.knownUnreadable.size} of ${queued.length} queued file(s) unreadable`);
    }).catch(err => {
      this.loggingService.log(`[ReadCheck] Sweep failed: ${describeError(err)}`);
    });
  }

  /**
   * A queued file the sweep already found unreadable. Same failure record and
   * analytics grain as a file that found out itself, minus the upload attempt
   * and the console errors.
   */
  private recordSweptUnreadable(file: File): void {
    const error = 'Source file is no longer readable. The browser refused to read the picked file; why is not known.';
    this.loggingService.log(`Skipping ${file.name}: unreadable (found by sweep)`);

    const progress = this.uploadProgressMap.get(file.name);
    if (progress) {
      progress.status = 'failed';
      progress.error_message = error;
    }
    this.failedFiles.push({ file, error, retries: 0, kind: 'file-unreadable', retryable: false });
    this.failedCount++;

    // The server wrote a row for this file when the batch started and nothing
    // else will ever correct it.
    this.reportFailureToBackend(file, { phase: 'read-file', kind: 'file-unreadable', retryable: false }, error, 0);

    const surface = this.isMobile ? 'mobile' : 'desktop';
    this.analytics.capture('file_missing_from_library', {
      event_id: this.eventId,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type || 'unknown',
      ...this.pickContext(file),
      surface,
      detected_by: 'sweep',
    });
    this.analytics.capture('upload_failed', {
      event_id: this.eventId,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type || 'unknown',
      attempts: 0,
      ...this.pickContext(file),
      phase: 'read-file',
      kind: 'file-unreadable',
      retryable: false,
      cause: 'cloud_file',
      error_name: 'NotReadableError',
      error_message: error,
      detected_by: 'sweep',
      visibility_state: document.visibilityState,
      network_online: navigator.onLine,
      surface,
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });
  }

  // ✅ Optimized Simple Upload (For Small Files)
  private async simpleUpload(file: File, params: { Bucket: string; Key: string; Metadata?: { [key: string]: string } }, s3: S3Client): Promise<string | undefined> {
    // Body is the File itself: the browser streams it from disk inside the
    // network layer, so it never occupies the JS heap.
    const upload = await createUpload({ client: s3, params: { ...params, Body: file } });

    if (!upload) throw new Error("Simple Upload Failed");

    upload.on("httpUploadProgress", (progress) => {
      const loaded = progress.loaded ?? 0;
      const total = progress.total ?? 0;
      const progressPercent = total > 0 ? Math.round((loaded / total) * 100) : 0;

      // Ensure progress only increases, never decreases
      const currentProgress = this.uploadProgress[file.name] || 0;
      if (progressPercent > currentProgress) {
        this.uploadProgress[file.name] = progressPercent;
      }

      // Deliberately does NOT touch totalUploadedBytes. The batch loop assigns
      // that field from completed-file totals, so incrementing it here made the
      // value climb mid-file and snap back on completion. The speed sampler
      // discards negative deltas, so those snap-backs silently threw away
      // samples and made the readout erratic. Speed now samples per completed
      // file — chunkier, but monotonic and correct.
    });

    // lib-storage manages its own requests, so bound the whole operation here
    // for the same reason the multipart sends are bounded: nothing in the
    // upload path may await without a deadline.
    const result: any = await withTimeout(
      upload.done() as Promise<any>,
      S3_REQUEST_TIMEOUT_MS,
      `Simple upload for ${params.Key}`
    );
    this.uploadProgress[file.name] = 100; // Ensure it's set to 100% on completion
    this.loggingService.log(`Simple Upload Complete: ${params.Key}`);
    return result?.ETag as string | undefined;
  }

  private async multipartUpload(
    file: File,
    params: { Bucket: string; Key: string; Metadata?: { [key: string]: string } },
    s3: S3Client
  ): Promise<string | undefined> {
    const partSize = 5 * 1024 * 1024; // 5MB
    const totalParts = Math.ceil(file.size / partSize);
    let completedParts = 0;

    const { CreateMultipartUploadCommand, UploadPartCommand, CompleteMultipartUploadCommand, AbortMultipartUploadCommand } = await loadS3ClientModule();

    // console.log(`[MultipartUpload] Starting upload: ${params.Key}, Size=${file.size}, Parts=${totalParts}`); //__CONSOLE_LOG__
    this.loggingService.log(`[MultipartUpload] Starting upload: ${params.Key}, Size=${file.size}, Parts=${totalParts}`);

    // params already carries ContentType from resolveContentType(); don't
    // overwrite it with the raw file.type, which is empty for HEIC and friends.
    const { UploadId } = await inPhase('create-multipart', () => withTimeout(
      s3.send(new CreateMultipartUploadCommand({ ...params })) as Promise<any>,
      S3_REQUEST_TIMEOUT_MS,
      `CreateMultipartUpload for ${params.Key}`
    ));

    // console.log(`[MultipartUpload] Created UploadId: ${UploadId}`); //__CONSOLE_LOG__
    this.loggingService.log(`[MultipartUpload] Created UploadId: ${UploadId}`);

    if (!UploadId) throw new Error("Multipart Upload Failed to Start");

    const partsData: { ETag: string; PartNumber: number }[] = [];

    // Helper function to update progress based on completed parts
    const updateProgress = () => {
      const progressPercent = Math.round((completedParts / totalParts) * 100);

      // Ensure progress only increases, never decreases
      const currentProgress = this.uploadProgress[file.name] || 0;
      if (progressPercent > currentProgress) {
        this.uploadProgress[file.name] = progressPercent;

        // Update uploadProgressMap for UI display
        const existingProgress = this.uploadProgressMap.get(file.name);
        if (existingProgress) {
          this.uploadProgressMap.set(file.name, {
            ...existingProgress,
            progress_percentage: progressPercent,
            status: 'uploading'
          });
        }
      }
    };

    /**
     * Upload one part, retrying that part alone before giving up on the file.
     *
     * Retrying a part is safe and idempotent: same UploadId + same PartNumber
     * replaces the part in place, it does not add a duplicate. That matters,
     * because the alternative — letting one flaky part reject the whole file —
     * sends the outer per-file retry loop back to CreateMultipartUpload with a
     * brand new UploadId, abandoning every part already uploaded. Retrying here
     * keeps the parts we already paid for and is the difference between one
     * transient blip costing 5MB and it costing the entire file.
     */
    const uploadPartWithRetry = async (partNumber: number): Promise<{ ETag: string; PartNumber: number }> => {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      // Sliced lazily from the File, not from a buffered copy: a Blob slice is
      // a view, so only the ~5MB the browser is actually sending is ever read,
      // and it is read inside the network layer rather than into the JS heap.
      const blob = file.slice(start, end);
      const maxPartAttempts = 3;
      let lastError: any;

      for (let attempt = 1; attempt <= maxPartAttempts; attempt++) {
        try {
          this.loggingService.log(`[MultipartUpload] Uploading part ${partNumber} (bytes ${start} - ${end}), attempt ${attempt}/${maxPartAttempts}`);

          // Belt and braces behind the SDK's own requestTimeout: this bounds
          // the whole send() including SDK-internal retries, so no part can
          // ever leave us awaiting indefinitely.
          const { ETag } = await inPhase('upload-part', () => withTimeout(
            s3.send(new UploadPartCommand({
              Bucket: params.Bucket,
              Key: params.Key,
              PartNumber: partNumber,
              UploadId,
              Body: blob,
            })) as Promise<any>,
            S3_REQUEST_TIMEOUT_MS,
            `UploadPart ${partNumber} for ${params.Key}`
          ));

          if (!ETag) throw new Error(`UploadPart failed: missing ETag for part ${partNumber}`);

          this.loggingService.log(`[MultipartUpload] Completed part ${partNumber}, ETag=${ETag}`);
          completedParts++;
          updateProgress();
          return { ETag, PartNumber: partNumber };
        } catch (partErr: any) {
          lastError = partErr;
          this.loggingService.logError(`[MultipartUpload] Part ${partNumber} attempt ${attempt}/${maxPartAttempts} failed for ${params.Key}`, partErr);

          if (attempt < maxPartAttempts) {
            // No point spending an attempt while offline.
            await this.waitForNetwork();
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
          }
        }
      }

      throw lastError instanceof Error
        ? lastError
        : new Error(`UploadPart failed for part ${partNumber}: ${String(lastError)}`);
    };

    try {
      // Parts in flight per file. Multiplied by the file concurrency in the
      // batch loop, so this is deliberately small on degraded connections.
      const { parts: concurrency } = this.getUploadConcurrency();
      this.loggingService.log(`[MultipartUpload] Part concurrency: ${concurrency} for ${params.Key}`);

      for (let i = 0; i < totalParts; i += concurrency) {
        this.loggingService.log(`[MultipartUpload] Uploading chunk group starting at part ${i + 1}`);

        const chunk = Array.from(
          { length: Math.min(concurrency, totalParts - i) },
          (_, j) => uploadPartWithRetry(i + j + 1)
        );

        const results = await Promise.all(chunk);
        partsData.push(...results);
      }

      // console.log(`[MultipartUpload] Completing upload with ${partsData.length} parts...`); //__CONSOLE_LOG__
      this.loggingService.log(`[MultipartUpload] Completing upload with ${partsData.length} parts...`);

      const completeResult: any = await inPhase('complete-multipart', () => withTimeout(
        s3.send(new CompleteMultipartUploadCommand({
          Bucket: params.Bucket,
          Key: params.Key,
          UploadId,
          MultipartUpload: {
            Parts: partsData.sort((a, b) => a.PartNumber - b.PartNumber),
          },
        })) as Promise<any>,
        S3_REQUEST_TIMEOUT_MS,
        `CompleteMultipartUpload for ${params.Key}`
      ));

      // console.log(`[MultipartUpload] Upload complete: ${params.Key}`); //__CONSOLE_LOG__
      this.loggingService.log(`[MultipartUpload] Upload complete: ${params.Key}`);
      this.uploadProgress[file.name] = 100; // Ensure it's set to 100% on completion

      // Update uploadProgressMap for UI display
      const existingProgress = this.uploadProgressMap.get(file.name);
      if (existingProgress) {
        this.uploadProgressMap.set(file.name, {
          ...existingProgress,
          progress_percentage: 100,
          status: 'uploading'
        });
      }

      return completeResult?.ETag as string | undefined;
    } catch (err) {
      this.loggingService.logError(`[MultipartUpload] Failed for ${params.Key} after ${completedParts}/${totalParts} parts`, err);
      this.loggingService.log(`[MultipartUpload] Aborting upload: ${UploadId}`);

      // The abort gets its own try/catch. The usual reason the upload failed is
      // a dropped connection, which would fail the abort too — and an unguarded
      // abort throwing here would replace `err` with its own error, so the
      // retry loop and the file's error_message would report "abort failed"
      // instead of the real cause.
      try {
        // Short bound, separate from the request timeout: cleanup must never
        // delay reporting the real failure upward. If the abort can't complete
        // in 30s the connection is gone anyway and S3 lifecycle rules are what
        // will reclaim the parts.
        await withTimeout(
          s3.send(new AbortMultipartUploadCommand({
            Bucket: params.Bucket,
            Key: params.Key,
            UploadId,
          })) as Promise<any>,
          30000,
          `AbortMultipartUpload for ${params.Key}`
        );
      } catch (abortErr) {
        // Nothing more we can do from the client; S3 lifecycle rules are what
        // eventually reclaim parts orphaned by a failed abort.
        this.loggingService.logError(
          `[MultipartUpload] Abort failed for ${params.Key} (UploadId=${UploadId}) — parts may be orphaned`,
          abortErr
        );
      }

      throw err;
    }
  }



  // // ✅ Optimized Multipart Upload (For Large Files)
  // private async multipartUpload(file: File, params: { Bucket: string; Key: string; Metadata?: { [key: string]: string } }, s3: AWS.S3) {
  //   const partSize = 5 * 1024 * 1024; // 5MB
  //   const totalParts = Math.ceil(file.size / partSize);
  //   console.log({ totalParts }); 

  //   const { UploadId } = await s3.createMultipartUpload(params).promise();
  //  console.log({ UploadId }); 
  //   if (!UploadId) throw new Error("Multipart Upload Failed to Start");

  //   const promises = Array.from({ length: totalParts }, async (_, i) => {
  //     const start = i * partSize;
  //     const end = Math.min(start + partSize, file.size);
  //     const blob = file.slice(start, end);

  //     const uploadPartParams = {
  //       Bucket: params.Bucket,
  //       Key: params.Key,
  //       PartNumber: i + 1,
  //       UploadId,
  //       Body: blob,
  //     };

  //     const { ETag } = await s3.uploadPart(uploadPartParams).promise();
  //     return { ETag, PartNumber: i + 1 };
  //   });

  //   const partsData = await Promise.all(promises);

  //   await s3.completeMultipartUpload({
  //     Bucket: params.Bucket,
  //     Key: params.Key,
  //     UploadId,
  //     MultipartUpload: { Parts: partsData },
  //   }).promise();

  //   this.loggingService.log(`Multipart Upload Complete: ${params.Key}`);
  // }

  // async uploadFile(userId: any, eventId: any, file: File, platform: string, device: string) {
  //   const s3 = new S3();
  //   const fileName = file.name.replace(/\s/g, "").replace("'", "");
  //   console.log(this.selectedSubfolder);

  //   const metadata = {
  //     platform: this.getPlatform(),
  //     device: this.getDeviceType(),
  //     subfolderId: this.selectedSubfolder.toString()
  //   };
  //   const params = {
  //     Bucket: environment.aws.bucketName,
  //     Key: `pwa/${userId}/events/${eventId}/${fileName}`,
  //     Metadata: metadata,
  //   };
  //   this.loggingService.log("upload file to:" + params);
  //   const onProgress = (progress: number) => {
  //     const fileProgress = this.files.find((f) => f.name === file.name);
  //     if (fileProgress) fileProgress.progress = progress;
  //   };

  //   const fileProgress = this.files.find((f) => f.name === file.name);
  //   if (!fileProgress) return;

  //   this.loggingService.log("upload progress:" + fileProgress);


  //   try {
  //     if (file.size > 5 * 1024 * 1024) {
  //       this.loggingService.log("multipart upload starting");

  //       // Multipart upload for large files
  //       await this.multipartUpload(s3, file, params, onProgress);

  //     } else {
  //       // Simple upload for smaller files
  //       const upload = s3.upload({ ...params, Body: file });
  //       // upload.on("httpUploadProgress", (progress) => {
  //       //   const fileProgress = this.files.find((f: { name: string; }) => f.name === file.name);
  //       //   if (fileProgress) {
  //       //     fileProgress.progress = Math.round((progress.loaded / progress.total) * 100);
  //       //   }
  //       // });
  //       upload.on("httpUploadProgress", (progress) => {
  //         this.uploadProgress[file.name] = Math.round(
  //           (progress.loaded / progress.total) * 100
  //         );
  //         console.log(this.uploadProgress[file.name]);
  //       });
  //       await upload.promise();
  //       this.loggingService.log("upload status:" + upload);

  //     }
  //   } catch (error) {
  //     this.loggingService.log("upload error:" + `${file.name}:` + error);

  //     console.error(`Error uploading file ${file.name}:`, error);
  //   }
  // }

  // async multipartUpload(
  //   s3: S3,
  //   file: File,
  //   params: { Bucket: string; Key: string; Metadata?: { [key: string]: string } },
  //   onProgress: (progress: number) => void
  // ): Promise<void> {
  //   const multipartParams = { Bucket: params.Bucket, Key: params.Key, Metadata: params.Metadata };
  //   const createResponse = await s3.createMultipartUpload(multipartParams).promise();
  //   const uploadId = createResponse.UploadId;
  //   console.log(uploadId)
  //   // Ensure UploadId is defined
  //   if (!uploadId) {
  //     throw new Error("Failed to initiate multipart upload.");
  //   }
  //   const partSize = 5 * 1024 * 1024; // 5 MB
  //   const totalParts = Math.ceil(file.size / partSize);
  //   let uploadedBytes = 0;

  //   const promises = [];

  //   for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
  //     const start = (partNumber - 1) * partSize;
  //     const end = Math.min(start + partSize, file.size);
  //     const blob = file.slice(start, end);

  //     const uploadPartParams = {
  //       Bucket: params.Bucket,
  //       Key: params.Key,
  //       PartNumber: partNumber,
  //       UploadId: uploadId,
  //       Body: blob,
  //     };

  //     promises.push(
  //       s3.uploadPart(uploadPartParams).promise().then((data) => {
  //         uploadedBytes += blob.size;
  //         const progressPercentage = Math.round((uploadedBytes / file.size) * 100);
  //         onProgress(progressPercentage);
  //         return { ETag: data.ETag || '', PartNumber: partNumber };
  //       })
  //     );
  //   }

  //   const partsData = await Promise.all(promises);
  //   console.log({partsData})
  //   await s3.completeMultipartUpload({
  //     Bucket: params.Bucket,
  //     Key: params.Key,
  //     UploadId,
  //     MultipartUpload: { Parts: partsData },
  //   }).promise();

  //   this.loggingService.log(`Multipart Upload Complete: ${params.Key}`);
  // }

  getPlatform(): string {
    // const userAgent = navigator.userAgent;
    // if (/Linux|Android/.test(userAgent)) return 'Linux/Android';
    // if (/Windows/.test(userAgent)) return 'Windows';
    const _platform = /iPad|iPhone|iPod/.test(navigator.userAgent)
      ? "iOS"
      : /Linux|Android/.test(navigator.userAgent)
        ? "Android"
        : "Other";
    return _platform;
  }




  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    // Optional: Add styles to indicate that the area is a drop zone
    document.getElementById('drop-area')?.classList.add('drag-over');
  }

  // Handle drag leave event (to remove drop zone styles)
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    document.getElementById('drop-area')?.classList.remove('drag-over');
  }

  // Handle drop event
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    document.getElementById('drop-area')?.classList.remove('drag-over');
    this.isDragOver = false;

    if (this.isFileLoading || this.isProcessing) {
      this.loggingService.log('Drop ignored - upload/processing in progress');
      return;
    }

    // The entries must be taken from the event synchronously; the list is
    // emptied once the handler returns.
    const entries: any[] = [];
    const looseFiles: File[] = [];
    const items = event.dataTransfer?.items;
    if (items && items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind !== 'file') continue;
        const entry = typeof (item as any).webkitGetAsEntry === 'function' ? (item as any).webkitGetAsEntry() : null;
        if (entry) {
          entries.push(entry);
        } else {
          const file = item.getAsFile();
          if (file) looseFiles.push(file);
        }
      }
    } else if (event.dataTransfer?.files) {
      for (let i = 0; i < event.dataTransfer.files.length; i++) looseFiles.push(event.dataTransfer.files[i]);
    }

    if (entries.length === 0 && looseFiles.length === 0) {
      this.loggingService.log('Drop contained no files');
      return;
    }
    this.loggingService.log(`Drop started: ${entries.length} items, ${looseFiles.length} loose files`);

    void this.collectDroppedFiles(entries).then(({ files, failed }) => {
      this.ngZone.run(() => void this.handleDroppedFiles([...looseFiles, ...files], failed));
    });
  }

  /**
   * Walks dropped files and folders. Unlike the old callback walk, a file or
   * folder the browser cannot read (cloud placeholder, moved mid-drop) is
   * counted and named instead of leaving the walk waiting forever, and each
   * folder is read until it returns an empty batch, since one call hands back
   * at most about 100 entries.
   */
  private async collectDroppedFiles(entries: any[]): Promise<{ files: File[]; failed: string[] }> {
    const files: File[] = [];
    const failed: string[] = [];

    const walk = async (entry: any): Promise<void> => {
      try {
        if (entry.isFile) {
          files.push(await new Promise<File>((resolve, reject) => entry.file(resolve, reject)));
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const children: any[] = [];
          for (;;) {
            const batch = await new Promise<any[]>((resolve, reject) => reader.readEntries(resolve, reject));
            if (batch.length === 0) break;
            children.push(...batch);
          }
          for (const child of children) await walk(child);
        }
      } catch (err) {
        failed.push(entry?.name || 'unknown');
        this.loggingService.log(`Drop: could not read ${entry?.name || 'an item'}: ${describeError(err)}`);
      }
    };

    for (const entry of entries) await walk(entry);
    return { files, failed };
  }

  /**
   * A dropped selection goes through exactly the steps a picked one does
   * (processPickedFiles).
   */
  private async handleDroppedFiles(files: File[], failed: string[]): Promise<void> {
    this.loggingService.log(`Drop collected: ${files.length} files, ${failed.length} could not be read`);

    if (failed.length > 0) {
      this.analytics.capture('drop_files_unreadable', {
        event_id: this.eventId,
        files_collected: files.length,
        files_unreadable: failed.length,
        surface: this.isMobile ? 'mobile' : 'desktop',
        role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
      });
      this.toastService.showToast(
        `${failed.length} ${failed.length === 1 ? "item couldn't" : "items couldn't"} be read and ${failed.length === 1 ? "wasn't" : "weren't"} added. If they're in a cloud folder, make sure they're downloaded to this computer.`
      );
    }
    if (files.length === 0) return;

    try {
      await this.processPickedFiles(files, 'drop');
    } catch (error) {
      this.loggingService.log(`Error in handleDroppedFiles: ${error}`);
    }
    this.cdr.detectChanges();
  }

  // traverseFileTree(item: any, path: string = '') {
  //   if (item.isFile) {
  //     item.file((file: File) => {
  //       console.log('File:', path + file.name, file);
  //       this.selectedFiles.push(file);
  //     });
  //   } else if (item.isDirectory) {
  //     const dirReader = item.createReader();
  //     dirReader.readEntries((entries: any) => {
  //       for (let entry of entries) {
  //         this.traverseFileTree(entry, path + item.name + '/');
  //       }
  //     });
  //   }
  // }

  // Add files to the selected files array
  addFilesToSelection(files: FileList | File[]) {
    if (!files || files.length === 0) {
      this.loggingService.log("addFilesToSelection: No files provided");
      return;
    }

    this.loggingService.log(`addFilesToSelection: Starting - Processing ${files.length} files`);

    const added: File[] = [];
    let skipped = 0;
    // A new array, so the desktop list sees the additions (see removeFile).
    this.selectedFiles = [...this.selectedFiles];
    for (let i = 0; i < files.length; i++) {
      let file = files[i];

      // Validate file before adding
      if (!file) {
        this.loggingService.log(`addFilesToSelection: File at index ${i} is null/undefined`);
        continue;
      }

      if (!this.isPhotoOrVideo(file)) {
        this.loggingService.log(`addFilesToSelection: Skipped ${file.name} - not a photo or video (type=${file.type || 'none'})`);
        skipped++;
        continue;
      }

      // Check for virtual files from Google Photos (sometimes have size 0 or no type)
      if (file.size === 0) {
        this.loggingService.log(`addFilesToSelection: Warning - File ${file.name} has size 0, adding anyway for retry`);
      }

      if (!file.type || file.type === '') {
        this.loggingService.log(`addFilesToSelection: Warning - File ${file.name} has no MIME type, adding anyway`);
      }

      this.selectedFiles.push(file);
      this.thumbnailError.push(false);
      added.push(file);

      // Generate thumbnail for the file (using createObjectURL to avoid
      // creating large base64 strings in memory).
      this.generateFileThumbnail(file, this.selectedFiles.length - 1);
    }

    this.loggingService.log(`addFilesToSelection: Completed - ${this.selectedFiles.length} files added`);
    this.reportSkippedFiles(skipped);

    // Not awaited: the grid and duplicate check render immediately and the
    // tiles pick up their state as each probe lands.
    this.runReadabilityCheck(added);
    void this.traceReadDecay(added);
  }

  /**
   * Find out WHEN a picked file stops being readable, and what the picked files
   * that survive have in common.
   *
   * The failing sessions showed the same sequence: a one-byte read passed within
   * a second of picking, and the same file refused to be read about twenty
   * seconds later. What the logs could not say is whether that happens to files
   * that go on to upload fine (they never printed a read time), whether the end
   * of the file goes before the start, or what distinguishes the two groups.
   * This samples a few of the picked files and re-reads the first and the last
   * byte at fixed moments after picking, then reports once.
   *
   * It reads one byte at a time from at most 24 files, stops the moment the
   * upload starts so it can never compete with it, and changes nothing the
   * person sees.
   */
  private async traceReadDecay(files: File[]): Promise<void> {
    if (!files.length) return;
    const pickedAt = Date.now();
    files.forEach(f => this.pickedAt.set(f, pickedAt));

    const stride = Math.max(1, Math.ceil(files.length / 24));
    const sample = files.filter((_, i) => i % stride === 0).slice(0, 24);
    const epoch = this.readTraceGen;
    const checkpointsMs = [0, 5000, 15000, 30000, 60000];

    const props: Record<string, number> = {};
    let lastCheckpointMs = -1;
    for (const t of checkpointsMs) {
      const wait = pickedAt + t - Date.now();
      if (wait > 0) await new Promise(resolve => setTimeout(resolve, wait));
      if (epoch !== this.readTraceGen || this.isFileLoading || this.uploadComplete) break;

      const [heads, tails] = await Promise.all([
        Promise.all(sample.map(f => probeFileRead(f, 'head'))),
        Promise.all(sample.map(f => probeFileRead(f, 'tail'))),
      ]);
      props[`head_ok_at_${t / 1000}s`] = heads.filter(Boolean).length;
      props[`tail_ok_at_${t / 1000}s`] = tails.filter(Boolean).length;
      lastCheckpointMs = t;
    }
    if (lastCheckpointMs < 0 || epoch !== this.readTraceGen) return;

    const nameClass = (n: string) =>
      /^1000\d{6,}\./.test(n) ? 'name_1000xxxxxx'
        : /^\d+\.(jpe?g|png|heic|heif|webp)$/i.test(n) ? 'name_digits_only'
          : 'name_other';
    const names: Record<string, number> = { name_1000xxxxxx: 0, name_digits_only: 0, name_other: 0 };
    sample.forEach(f => { names[nameClass(f.name)]++; });

    this.analytics.capture('picked_files_read_trace', {
      event_id: this.eventId,
      files_picked: files.length,
      files_sampled: sample.length,
      ...props,
      last_checkpoint_s: lastCheckpointMs / 1000,
      // Of the sample, how many carry the moment of picking as their modified
      // time — what a temporary copy made by the picker looks like.
      mtime_is_pick_time: sample.filter(f => Math.abs(f.lastModified - pickedAt) <= 5000).length,
      ...names,
      stopped_by: this.isFileLoading ? 'upload_started' : 'checkpoints_done',
      ms_to_upload_start: this.uploadStartedAt >= pickedAt ? this.uploadStartedAt - pickedAt : null,
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });
  }

  /**
   * Probe each newly picked file with a one-byte read, a few at a time, while
   * the person is still on this screen. A failed first read gets one retry
   * after a short pause, matching assertFileReadable: a cloud fetch that is
   * merely slow should not be reported as missing.
   */
  private async runReadabilityCheck(files: File[]): Promise<void> {
    if (!files.length) return;
    const gen = this.readableCheckGen;
    const current = () => gen === this.readableCheckGen;
    this.precheckRan = true;
    this.activeReadChecks++;
    this.isCheckingReadable = true;
    this.cdr.detectChanges();

    const probe = async (file: File): Promise<boolean> => {
      if (await isFileStillReadable(file)) return true;
      await new Promise(resolve => setTimeout(resolve, 400));
      return isFileStillReadable(file);
    };

    const queue = files.slice();
    const CONCURRENCY = 4;
    const worker = async () => {
      while (queue.length && current()) {
        const file = queue.shift()!;
        const ok = await probe(file);
        if (!current()) return;
        if (!ok) this.precheckUnreadable.add(file.name);
        this.readableChecked.add(file.name);
        this.cdr.detectChanges();
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
    if (!current()) return;

    this.activeReadChecks--;
    if (this.activeReadChecks > 0) return; // the last run to finish reports
    this.isCheckingReadable = false;
    this.loggingService.log(
      `[ReadCheck] Picking-time check: ${this.selectedFiles.length} selected, ${this.precheckUnreadable.size} unreadable`
    );
    this.analytics.capture('upload_precheck_completed', {
      event_id: this.eventId,
      files_selected: this.selectedFiles.length,
      files_checked: files.length,
      files_unreadable: this.precheckUnreadable.size,
      files_duplicate: this.duplicateFiles.size,
      surface: this.isMobile ? 'mobile' : 'desktop',
      role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
    });
    this.cdr.detectChanges();
  }

  /** "Try again" on the cloud panel: re-probe only the files that failed. */
  async retryUnreadable(): Promise<void> {
    if (this.isRetryingUnreadable || this.precheckUnreadable.size === 0) return;
    this.isRetryingUnreadable = true;
    const before = this.precheckUnreadable.size;
    const files = this.selectedFiles.filter(f => this.precheckUnreadable.has(f.name));
    for (const file of files) {
      if (await isFileStillReadable(file)) this.precheckUnreadable.delete(file.name);
    }
    this.isRetryingUnreadable = false;
    const came = before - this.precheckUnreadable.size;
    const left = this.precheckUnreadable.size;
    this.analytics.capture('upload_precheck_retried', {
      event_id: this.eventId,
      files_recovered: came,
      files_still_unreadable: left,
      surface: this.isMobile ? 'mobile' : 'desktop',
    });
    if (came > 0) {
      this.toastService.showToast(`${came} ${came === 1 ? 'photo' : 'photos'} came through`);
    } else {
      this.toastService.showToast(unreadableStillBlocked(left));
    }
    this.cdr.detectChanges();
  }

  /** Forget every picking-time result; the selection they described is gone. */
  private resetReadabilityCheck(): void {
    this.readableCheckGen++;
    this.readTraceGen++;
    this.activeReadChecks = 0;
    this.isCheckingReadable = false;
    this.isRetryingUnreadable = false;
    this.readableChecked.clear();
    this.precheckUnreadable.clear();
    this.precheckRan = false;
  }

  /** Files the upload will actually send: not duplicates, not unreadable. */
  get readyFiles(): File[] {
    return this.selectedFiles.filter(f => !this.duplicateFiles.has(f.name) && !this.precheckUnreadable.has(f.name));
  }

  /** True when the selection holds files the upload is going to skip. */
  get hasSkippedFiles(): boolean {
    return this.duplicateFiles.size > 0 || this.precheckUnreadable.size > 0;
  }

  get readyCount(): number {
    return this.readyFiles.length;
  }

  get readySizeLabel(): string {
    return this.getFileSize(this.readyFiles.reduce((acc, f) => acc + f.size, 0));
  }

  /**
   * A quick-join guest still on their auto name (guest-MMDD-HHmmss). Read once
   * per dialog; cleared once a typed name is saved. The name has to look
   * generated, not just be present — a guest who gave a real name is never
   * asked for it again, including one carrying a key written before that was
   * checked on the way in.
   */
  private askGuestName = environment.features.quickGuestJoin
    && SafeStorage.getItem('isGuestUser') === 'true'
    && isGuestAutoName(SafeStorage.getItem(GUEST_AUTO_NAME_KEY));

  /** What the guest typed in the name field; saved when they press Upload. */
  guestNameDraft = '';

  /** The name field sits with the Upload button and goes when it does. */
  get showGuestNameField(): boolean {
    return this.askGuestName && !this.uploadComplete && this.selectedFiles.length > 0
      && !this.isProcessing && !this.isFileLoading && (this.readyCount > 0 || this.isCheckingReadable);
  }

  /** The name is required for now: Upload stays disabled while the field is empty. */
  get guestNameMissing(): boolean {
    return this.showGuestNameField && !this.guestNameDraft.trim();
  }

  /**
   * Swaps the auto name for the typed one. Uploads point at the guest's
   * user_tbl row, so photos already in the album pick up the new name too.
   * A failure keeps the auto name and asks again on the next upload.
   */
  private async saveGuestName(): Promise<void> {
    const name = this.guestNameDraft.trim();
    if (!this.askGuestName || !name) return;
    this.askGuestName = false;
    try {
      const idToken = await (await this.fbAuth.currentUser)?.getIdToken();
      const eventId = SafeStorage.getItem('e') || this.eventId;
      if (!idToken || !eventId) throw new Error('no id token or event id');
      const res = await firstValueFrom(this.guestService.renameGuest(idToken, name, String(eventId)));
      if (!res?.success) throw new Error(res?.error || 'rename returned no success');
      SafeStorage.removeItem(GUEST_AUTO_NAME_KEY);
      this.analytics.capture('guest_named', { event_id: eventId, surface: 'upload' });
    } catch (err) {
      this.askGuestName = true;
      this.loggingService.logError('Guest rename failed', err);
    }
  }

  /** Label on the Upload button once some picks are known to be skipped. */
  get uploadButtonLabel(): string {
    if (!this.hasSkippedFiles || this.isCheckingReadable) return 'Upload';
    const n = this.readyCount;
    return `Upload ${n} ${n === 1 ? 'photo' : 'photos'}`;
  }

  /**
   * Mobile grid order: tiles the upload will skip come first so their tags
   * are on screen even in a large selection that only shows nine.
   */
  get mobileGridIndices(): number[] {
    const flagged: number[] = [];
    const rest: number[] = [];
    this.selectedFiles.forEach((f, i) => {
      (this.precheckUnreadable.has(f.name) || this.duplicateFiles.has(f.name) ? flagged : rest).push(i);
    });
    return flagged.concat(rest).slice(0, 9);
  }

  /** Keys a mobileGridIndices tile by its file's index, not its position. */
  trackByFileIndex(_pos: number, fileIndex: number): number {
    return fileIndex;
  }

  tileState(file: File): 'pending' | 'cloud' | 'dup' | 'ok' {
    if (this.duplicateFiles.has(file.name)) return 'dup';
    if (this.isCheckingReadable && !this.readableChecked.has(file.name)) return 'pending';
    if (this.precheckUnreadable.has(file.name)) return 'cloud';
    return 'ok';
  }

  /** Nothing picked can be uploaded: start the pick over. */
  chooseOtherPhotos(): void {
    this.selectedFiles.forEach((_, i) => {
      const thumb = this.fileThumbnails[i];
      if (thumb && typeof thumb === 'string' && thumb.startsWith('blob:')) {
        try { URL.revokeObjectURL(thumb); } catch { /* ignore */ }
      }
    });
    this.selectedFiles = [];
    this.selectedFileNames = [];
    this.fileThumbnails = [];
    this.thumbnailError = [];
    // Keyed by name, so a stale entry would suppress the thumbnail of a
    // different photo that happened to share one.
    this.resultThumbError.clear();
    this.fileUploadStatus = [];
    this.duplicateFiles.clear();
    this.showDuplicateWarning = false;
    this.resetReadabilityCheck();
    this.onAddMoreClick();
  }

  onOpenGooglePhotos(): void {
    this.analytics.capture('upload_open_google_photos_clicked', {
      event_id: this.eventId,
      photos_left: this.cloudLeftCount,
      surface: this.isMobile ? 'mobile' : 'desktop',
    });
  }

  /** Cloud-only photos left behind by this upload, whichever check caught them. */
  get cloudLeftCount(): number {
    return this.precheckLeftOut.length + this.cloudOnlyFailedFiles.length;
  }

  // Generate thumbnail for a file
  generateFileThumbnail(file: File, index: number): void {
    // Use createObjectURL for thumbnails to avoid base64 memory blowup.
    //
    // HEIC is no longer special-cased. It used to return null on the assumption
    // that no browser can decode it — but iOS Safari decodes HEIC in an <img>
    // natively, and HEIC is the iPhone camera default, so the one platform that
    // could show these thumbnails was the only one guaranteed not to. Every
    // iPhone host picking from their camera roll saw a grid of placeholders.
    //
    // Try for everything instead. Where the browser genuinely cannot decode it
    // (Android Chrome, most desktops) the <img> fires (error), the template
    // sets thumbnailError[i], and the same placeholder appears as before. The
    // fallback was already written; it just was never reached.
    if (file.type.startsWith('image/') || file.type.startsWith('video/') ||
        /\.(heic|heif)$/i.test(file.name)) {
      try {
        const url = URL.createObjectURL(file);
        this.fileThumbnails[index] = url;
      } catch (err) {
        this.fileThumbnails[index] = null;
      }
    } else {
      this.fileThumbnails[index] = null;
    }
  }

  // Returns the thumbnail (base64 or URL) for a given file name from selectedFiles/fileThumbnails.
  // Used for displaying thumbnails in the Duplicates section.
  getFileThumbnail(fileName: string): string | null {
    // Check preserved duplicates first (after selectedFiles has been filtered for upload)
    if (this.preservedDuplicates && this.preservedDuplicates.length > 0) {
      const found = this.preservedDuplicates.find(d => d.name === fileName);
      if (found) return found.thumbnail || null;
    }

    if (!this.selectedFiles || !this.fileThumbnails) return null;
    const idx = this.selectedFiles.findIndex(f => f.name === fileName);
    if (idx !== -1 && this.fileThumbnails[idx]) {
      return this.fileThumbnails[idx] as string;
    }
    return null;
  }

  // Helper methods for modern upload dialog
  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  getFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  getTotalSize(): string {
    const total = this.selectedFiles.reduce((acc, file) => acc + file.size, 0);
    return this.getFileSize(total);
  }

  /** What actually went up, for the result's "Successfully uploaded" line, not the whole pick. */
  getUploadedTotalSize(): string {
    const total = this.uploadedFiles.reduce((acc, file) => acc + (file?.size || 0), 0);
    return this.getFileSize(total);
  }

  /**
   * Bytes sent so far and bytes in total, folding in the part-level progress of
   * whatever is currently in flight.
   *
   * multipartUpload() already reports every completed 5MB part into
   * uploadProgress[file.name]; nothing was reading it for the overall figure.
   */
  private uploadByteTotals(): { done: number; total: number } {
    let done = 0;
    let total = 0;
    for (const file of this.selectedFiles || []) {
      const size = file.size || 0;
      total += size;
      done += size * Math.min(100, this.uploadProgress[file.name] || 0) / 100;
    }
    return { done, total };
  }

  /**
   * Overall progress, weighted by bytes rather than by file count.
   *
   * The card used to show `count / selectedFiles.length`, which only moves when
   * a whole file lands. Two 859MB videos therefore read "0% Completed" for the
   * entire first upload — several minutes of a screen that looked frozen while
   * the transfer was in fact running normally.
   */
  /**
   * The "N of M" readout for whichever leg is running.
   *
   * `count` is the client's own tally, incremented by the upload loop in THIS
   * component instance. A dialog restored from a minimize snapshot has no such
   * loop — the transfer is being finished by the instance that was destroyed —
   * so `count` sits frozen at whatever it was when the user minimized. That is
   * how a batch whose files had all landed still displayed "0 of 10 Files".
   * During processing the server-side tally is the only one that moves.
   */
  get progressCount(): number {
    return this.isProcessing ? this.processingCompleted : this.count;
  }

  /** Files in the batch as far as the processing counters can tell. */
  get processingTotal(): number {
    return Math.max(this.batchTotal, this.processingCompleted + this.processingFailed + this.processingRemaining);
  }

  /** Reopened while files are still being transferred, not yet being processed. */
  get isTransferring(): boolean {
    return this.isProcessing && !this.isFileLoading && this.batchTotal > 0
      && this.restoredLanded + this.processingFailed < this.batchTotal;
  }

  get transferCountText(): string {
    return transferCount(this.restoredLanded, this.batchTotal);
  }

  get transferTitle(): string {
    return transferTitleFor(this.viewerIsHost);
  }

  /** Hosts call their event an album; guests see it as the Event Gallery. */
  get viewerIsHost(): boolean {
    return SafeStorage.getItem('isHost') === 'true';
  }

  get albumLabel(): string {
    return albumWord(this.viewerIsHost);
  }
  readonly transferNote = TRANSFER_NOTE;

  get processingPercent(): number {
    const total = this.processingTotal;
    if (!total) return 0;
    if (this.isTransferring) {
      return Math.min(100, Math.round((this.restoredLanded / total) * 100));
    }
    return Math.min(100, Math.round(((this.processingCompleted + this.processingFailed) / total) * 100));
  }

  /** Circumference of the r=52 progress dial, so the dash maths stays in one place. */
  readonly dialCircumference = 2 * Math.PI * 52;

  get dialOffset(): number {
    return this.dialCircumference * (1 - this.processingPercent / 100);
  }

  get overallUploadPercent(): number {
    const files = this.selectedFiles || [];
    if (!files.length) return 0;

    if (this.isProcessing) {
      return Math.round((this.processingCompleted / files.length) * 100);
    }

    const { done, total } = this.uploadByteTotals();
    // Sizeless files (rare, but a File can report 0) would divide by zero, so
    // fall back to weighting every file equally.
    if (!total) {
      const sum = files.reduce((acc, f) => acc + Math.min(100, this.uploadProgress[f.name] || 0), 0);
      return Math.round(sum / files.length);
    }
    return Math.round((done / total) * 100);
  }

  /** "142 MB of 1.72 GB" — the readout that keeps moving on a slow link. */
  get uploadedBytesLabel(): string {
    const { done, total } = this.uploadByteTotals();
    if (!total) return '';
    return `${this.getFileSize(done)} of ${this.getFileSize(total)}`;
  }

  /**
   * A real ETA when there is one, otherwise the live speed.
   *
   * estimatedTime is only recomputed as each file finishes, so across one long
   * video it sits on "Calculating…" from start to end. The speed sampler runs
   * every 5s and now sees part-level bytes, so it has something to say much
   * sooner.
   */
  get uploadPaceLabel(): string {
    const eta = this.estimatedTime;
    if (eta && eta !== 'Calculating...' && eta !== '--') return eta;
    if (this.currentUploadSpeed && this.currentUploadSpeed !== '--') return this.currentUploadSpeed;
    return '';
  }

  getTotalSizeForFiles(files: File[]): string {
    const total = files.reduce((acc, file) => acc + file.size, 0);
    return this.getFileSize(total);
  }

  getTotalSizeForDuplicates(): string {
    // If we preserved duplicate metadata (happens before selectedFiles is filtered for upload),
    // use it to compute total size so the UI still shows accurate totals after upload starts.
    if (this.preservedDuplicates && this.preservedDuplicates.length > 0) {
      const total = this.preservedDuplicates.reduce((acc, d) => acc + (d.size || 0), 0);
      return this.getFileSize(total);
    }

    const duplicateFilesList = Array.from(this.duplicateFiles.keys());
    const total = this.selectedFiles
      .filter(file => duplicateFilesList.includes(file.name))
      .reduce((acc, file) => acc + file.size, 0);
    return this.getFileSize(total);
  }

  removeFile(index: number): void {
    const fileName = this.selectedFiles[index].name;

    // Revoke any object URL created for this thumbnail to free memory
    const thumb = this.fileThumbnails[index];
    if (thumb && typeof thumb === 'string' && thumb.startsWith('blob:')) {
      try { URL.revokeObjectURL(thumb); } catch (e) { /* ignore */ }
    }

    // A new array, not splice(): the desktop list (cdkVirtualFor) copies the
    // array it is given and only notices a new one, so an in-place change left
    // it showing the removed file, and its ✕ buttons then removed the file
    // below the one tapped. Same in addFilesToSelection and handleDroppedFiles.
    this.selectedFiles = this.selectedFiles.filter((_, i) => i !== index);
    this.fileThumbnails.splice(index, 1);
    this.thumbnailError.splice(index, 1);
    this.fileUploadStatus.splice(index, 1);

    // Remove from duplicate map if present
    this.duplicateFiles.delete(fileName);
    this.precheckUnreadable.delete(fileName);
    this.readableChecked.delete(fileName);

    // Update warning state
    this.showDuplicateWarning = this.duplicateFiles.size > 0;
  }

  // Quick duplicate check: a first guess by filename (instant), settled by
  // content in confirmAlbumDuplicates().
  private checkForDuplicatesQuick(): void {
    this.isCheckingDuplicates = true;
    this.duplicateFiles.clear();

    try {
      for (const file of this.selectedFiles) {
        const normalizedName = this.normalizeFileName(file.name);
        if (this.existingFiles.has(normalizedName)) {
          this.duplicateFiles.set(file.name, 'name_match');
          this.loggingService.log(`Duplicate detected: ${file.name}`);
        }
      }

      this.showDuplicateWarning = this.duplicateFiles.size > 0;
      this.loggingService.log(`Quick duplicate check complete: ${this.duplicateFiles.size} duplicates found by filename`);
    } finally {
      this.isCheckingDuplicates = false;
    }
    // The name is only a first guess; settle each match by content.
    void this.confirmAlbumDuplicates();
  }

  /** Content hashes of picked files, so a re-check never reads a file twice. */
  private pickedHashes = new WeakMap<File, string>();

  /**
   * A name already in the album is not proof the photo is: phones reuse names
   * (IMG_0001.jpg) across guests and devices, and a different photo that
   * happened to share one was skipped as "already in this album" and never
   * sent. Each name match is hashed and stays a duplicate only when its content
   * matches a photo in the album. One that can't be confirmed (no hash on the
   * album side, or a file that can't be read) is uploaded under a free name
   * (resolveUploadFileName adds _1), and the server's own content check, which
   * uses the same MD5, has the last word.
   *
   * Run at pick time so the "already in this album" line corrects itself, and
   * awaited again when the upload starts so it never acts on a guess.
   */
  private async confirmAlbumDuplicates(): Promise<void> {
    const candidates = this.selectedFiles.filter(f => this.duplicateFiles.get(f.name) === 'name_match');
    if (!candidates.length) {
      return;
    }
    this.isCheckingDuplicates = true;
    let confirmed = 0;
    let released = 0;
    try {
      for (const file of candidates) {
        // Removed, or re-marked by a newer pick, while an earlier file hashed.
        if (this.duplicateFiles.get(file.name) !== 'name_match') {
          continue;
        }
        let md5 = this.pickedHashes.get(file);
        if (!md5 && this.existingMD5s.size > 0) {
          try {
            md5 = await this.computeMD5InWorker(file, 0);
            this.pickedHashes.set(file, md5);
          } catch (err) {
            this.loggingService.log(`Album duplicate check: could not hash ${file.name}, uploading it: ${describeError(err)}`);
          }
        }
        if (this.duplicateFiles.get(file.name) !== 'name_match') {
          continue;
        }
        if (md5 && this.existingMD5s.has(md5)) {
          this.duplicateFiles.set(file.name, 'duplicate_content');
          confirmed++;
        } else {
          this.duplicateFiles.delete(file.name);
          released++;
          this.loggingService.log(`Album duplicate check: ${file.name} shares a name with a photo in the album, not its content — uploading it`);
        }
      }
    } finally {
      this.isCheckingDuplicates = false;
    }
    this.showDuplicateWarning = this.duplicateFiles.size > 0;
    this.loggingService.log(`Album duplicate check: ${confirmed} confirmed by content, ${released} released`);
    this.cdr.detectChanges();
  }

  async checkForDuplicates(): Promise<void> {
    // Use quick filename-only check (no MD5 computation)
    this.checkForDuplicatesQuick();
  }

  getDuplicateFileNames(): string[] {
    if (this.preservedDuplicates && this.preservedDuplicates.length > 0) {
      return this.preservedDuplicates.map(d => d.name);
    }
    return Array.from(this.duplicateFiles.keys());
  }

  // Return duplicate file size string using preservedDuplicates first (useful after selectedFiles changes)
  getDuplicateFileSize(fileName: string): string {
    if (this.preservedDuplicates && this.preservedDuplicates.length > 0) {
      const found = this.preservedDuplicates.find(d => d.name === fileName);
      if (found) return this.getFileSize(found.size);
    }
    const file = this.selectedFiles.find(f => f.name === fileName);
    return this.getFileSize(file ? file.size : 0);
  }

  // Initialize upload tracking for multiple files in bulk
  private async initializeBulkUploadTracking(
    files: File[],
    userId: string,
    eventId: string
  ): Promise<void> {
    try {
      const uploads = files.map(file => {
        const s3Key = this.buildS3Key(userId, eventId, file);
        return {
          user_id: parseInt(userId),
          event_id: parseInt(eventId),
          original_file_name: file.name,
          s3_key: s3Key,
          file_size: file.size,
          // Same resolution as the S3 object, so the tracking row and the
          // stored object agree instead of recording octet-stream for HEIC.
          mime_type: this.resolveContentType(file)
        };
      });

      uploads.forEach(u => this.batchTrackedKeys.add(u.s3_key));

      this.loggingService.log(`Initializing bulk upload tracking for ${files.length} files`);
      this.loggingService.log(`API URL: ${this.uploadProgressApiUrl}?action=initBulk`);

      // Add retry logic for tracking initialization
      let trackingResponse: any = null;
      let trackingRetries = 0;
      while (trackingRetries < 3 && !trackingResponse) {
        try {
          trackingResponse = await this.http.post(`${this.uploadProgressApiUrl}?action=initBulk`, {
            uploads: uploads,
            upload_session_id: this.uploadSessionId,
            event_id: parseInt(eventId)
          }, {
            headers: {
              'Content-Type': 'application/json'
            }
          }).toPromise();
        } catch (trackingError) {
          trackingRetries++;
          this.loggingService.log(`Bulk tracking init attempt ${trackingRetries}/3 failed: ${trackingError}`);
          if (trackingRetries < 3) {
            await new Promise(resolve => setTimeout(resolve, 1000 * trackingRetries)); // Exponential backoff
          }
        }
      }

      if (trackingResponse) {
        this.loggingService.log(`Bulk upload tracking initialized: ${trackingResponse.tracking_ids?.length || 0} records created`);

        // Initialize progress info for each file
        files.forEach((file, index) => {
          this.uploadProgressMap.set(file.name, {
            tracking_id: trackingResponse.tracking_ids?.[index],
            file_name: file.name,
            original_file_name: file.name,
            status: 'uploading',
            progress_percentage: 0,
            stage: 's3_uploading',
            retry_count: 0
          });

          // Set details expanded by default
          this.showDetails.set(file.name, true);
        });
      } else {
        this.loggingService.log(`Failed to initialize bulk upload tracking after 3 retries - continuing without tracking`);
        // Initialize with minimal progress info so upload can continue
        files.forEach((file) => {
          this.uploadProgressMap.set(file.name, {
            tracking_id: undefined,
            file_name: file.name,
            original_file_name: file.name,
            status: 'uploading',
            progress_percentage: 0,
            stage: 's3_uploading',
            retry_count: 0
          });
          this.showDetails.set(file.name, true);
        });
      }
    } catch (error: any) {
      this.loggingService.log(`Critical error in bulk upload tracking: ${JSON.stringify(error)}`);
      // Still initialize progress map so uploads can proceed
      files.forEach((file) => {
        this.uploadProgressMap.set(file.name, {
          tracking_id: undefined,
          file_name: file.name,
          original_file_name: file.name,
          status: 'uploading',
          progress_percentage: 0,
          stage: 's3_uploading',
          retry_count: 0
        });
        this.showDetails.set(file.name, true);
      });
    }
  }

  // Initialize upload tracking before S3 upload (DEPRECATED - use initializeBulkUploadTracking)
  private async initializeUploadTracking(
    file: File,
    userId: string,
    eventId: string,
    s3Key: string
  ): Promise<number | null> {
    try {
      const payload = {
        user_id: parseInt(userId),
        event_id: parseInt(eventId),
        original_file_name: file.name,
        s3_key: s3Key,
        file_size: file.size,
        mime_type: file.type || 'application/octet-stream'
      };

      this.loggingService.log(`Initializing upload tracking for ${file.name}`);
      this.loggingService.log(`API URL: ${this.uploadProgressApiUrl}?action=init`);
      this.loggingService.log(`Payload: ${JSON.stringify(payload)}`);

      const response: any = await this.http.post(`${this.uploadProgressApiUrl}?action=init`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      }).toPromise();

      this.loggingService.log(`Upload tracking initialized for ${file.name}: tracking_id=${response.tracking_id}`);
      this.loggingService.log(`Full response: ${JSON.stringify(response)}`);

      // Initialize progress info
      this.uploadProgressMap.set(file.name, {
        tracking_id: response.tracking_id,
        file_name: file.name,
        original_file_name: file.name,
        status: 'uploading',
        progress_percentage: 0,
        stage: 's3_uploading',
        retry_count: 0
      });

      // Set details expanded by default
      this.showDetails.set(file.name, true);

      return response.tracking_id;
    } catch (error: any) {
      this.loggingService.log(`Failed to initialize upload tracking for ${file.name}`);
      this.loggingService.log(`Error type: ${error?.name}`);
      this.loggingService.log(`Error message: ${error?.message}`);
      this.loggingService.log(`Error status: ${error?.status}`);
      this.loggingService.log(`Error statusText: ${error?.statusText}`);
      this.loggingService.log(`Error body: ${JSON.stringify(error?.error)}`);
      this.loggingService.log(`Full error: ${JSON.stringify(error)}`);

      // Show user-friendly error
      // console.error('Upload tracking API error:', error); //__CONSOLE_LOG__

      return null;
    }
  }

  // Start bulk progress polling for all files (using pagination for large uploads)
  private startBulkProgressPolling(): void {
    // Use paginated version for better performance (Solution 5)
    this.startBulkProgressPollingPaginated();
  }

  // Stop bulk progress polling
  private stopBulkProgressPolling(): void {
    if (this.bulkProgressInterval) {
      clearInterval(this.bulkProgressInterval);
      this.bulkProgressInterval = null;
      this.progressPollOffset = 0; // Reset offset
      this.pollInFlight = false; // So a restart isn't blocked by a stale flag
      this.loggingService.log('Bulk progress polling stopped');
    }
  }

  // Poll for upload progress by S3 key (DEPRECATED - keeping for backward compatibility)
  private pollUploadProgress(s3Key: string, fileName: string): void {
    // Encode the S3 key for URL parameter
    const encodedKey = encodeURIComponent(s3Key);

    const interval = setInterval(async () => {
      try {
        const response: any = await this.http.get(`${this.uploadProgressApiUrl}?action=getByKey&key=${encodedKey}`).toPromise();
        const upload = response.upload;

        if (upload) {
          // Get current progress to ensure we only increase
          const currentProgress = this.uploadProgressMap.get(fileName);
          const currentPercentage = currentProgress?.progress_percentage || 0;
          const newPercentage = upload.progress_percentage;

          // Only update if progress increases, status changes, or error message updates
          if (newPercentage >= currentPercentage ||
            upload.status !== currentProgress?.status ||
            (upload.status === 'failed' && upload.error_message !== currentProgress?.error_message)) {
            // Update progress info
            const progressInfo: UploadProgressInfo = {
              tracking_id: upload.id,
              file_name: upload.file_name,
              original_file_name: upload.original_file_name,
              status: upload.status,
              progress_percentage: Math.max(newPercentage, currentPercentage), // Ensure non-decreasing
              stage: upload.stage,
              error_message: upload.error_message,
              error_stage: upload.error_stage,
              retry_count: upload.retry_count
            };

            this.uploadProgressMap.set(fileName, progressInfo);
          }

          this.loggingService.log(
            `Progress for ${fileName}: ${upload.progress_percentage}% - ${upload.stage} (${upload.status})`
          );

          // Mark as uploaded when completed
          if (upload.status === 'completed') {
            const fileIndex = this.selectedFiles.findIndex(f => f.name === fileName);
            if (fileIndex !== -1) {
              this.uploadedCheck[fileIndex] = true;
            }
          }

          // Stop polling when completed or failed (unless compression error)
          if (upload.status === 'completed' || upload.status === 'failed' || upload.progress_percentage === 100) {
            clearInterval(interval);
            this.progressPollingIntervals.delete(fileName);

            if (upload.status === 'failed') {
              this.loggingService.log(
                `Upload failed for ${fileName}: ${upload.error_message} at stage ${upload.error_stage}`
              );
            } else {
              this.loggingService.log(`Upload completed successfully for ${fileName}`);
            }
          }
        }
      } catch (error) {
        this.loggingService.log(`Failed to fetch progress for ${fileName}: ${error}`);
      }
    }, 2000); // Poll every 2 seconds

    this.progressPollingIntervals.set(fileName, interval);
  }

  // Get formatted stage name for display
  getStageDisplayName(stage: string): string {
    const stageNames: { [key: string]: string } = {
      's3_uploading': 'Uploading to S3',
      's3_uploaded': 'Uploaded to S3',
      'format_detected': 'Detecting format',
      'exif_extracted': 'Extracting metadata',
      'duplicate_check': 'Checking for duplicates',
      'compression_started': 'Compressing',
      'compression_completed': 'Compression complete',
      'db_insert_started': 'Saving to database',
      'completed': 'Completed'
    };
    return stageNames[stage] || stage.replace(/_/g, ' ');
  }

  // Get the current processing stage for Step 2 display
  getCurrentProcessingStage(): string {
    // Find the most common stage among processing files
    const stageCounts = new Map<string, number>();
    let mostCommonStage = 'Processing...';
    let maxCount = 0;

    this.uploadProgressMap.forEach((progress) => {
      if (progress.status === 'processing' && progress.stage) {
        const displayName = this.getStageDisplayName(progress.stage);
        const count = (stageCounts.get(displayName) || 0) + 1;
        stageCounts.set(displayName, count);

        if (count > maxCount) {
          maxCount = count;
          mostCommonStage = displayName;
        }
      }
    });

    // Return a user-friendly stage name
    if (mostCommonStage.includes('compressing') || mostCommonStage.includes('compression')) {
      return 'Compressing...';
    }
    if (mostCommonStage.includes('db')) {
      return 'Saving to database...';
    }
    if (mostCommonStage.includes('metadata') || mostCommonStage.includes('Extracting')) {
      return 'Processing metadata...';
    }
    if (mostCommonStage === 'Processing...') {
      return 'Finishing...';
    }

    return mostCommonStage;
  }

  // Toggle file details visibility
  toggleFileDetails(fileName: string): void {
    const currentState = this.showDetails.get(fileName) || false;
    this.showDetails.set(fileName, !currentState);
  }

  getFileDetails(fileName: string): UploadProgressInfo | undefined {
    return this.getProgressForFileName(fileName);
  }

  // Robust lookup: try direct key, then original_file_name, file_name, and normalized matches
  private getProgressForFileName(fileName: string): UploadProgressInfo | undefined {
    if (!fileName) return undefined;
    const direct = this.uploadProgressMap.get(fileName);
    if (direct) return direct;

    const normalized = (s: string) => s.replace(/\s/g, "").replace("'", "").toLowerCase();

    for (const [key, progress] of this.uploadProgressMap.entries()) {
      if (!progress) continue;
      if (progress.original_file_name === fileName || progress.file_name === fileName) return progress;
      if (progress.original_file_name && normalized(progress.original_file_name) === normalized(fileName)) return progress;
      if (progress.file_name && normalized(progress.file_name) === normalized(fileName)) return progress;
      if (key === fileName) return progress;
      // last resort: check if the progress names include the normalized file name
      try {
        if (progress.original_file_name && normalized(progress.original_file_name).includes(normalized(fileName))) return progress;
        if (progress.file_name && normalized(progress.file_name).includes(normalized(fileName))) return progress;
      } catch (e) {
        // ignore
      }
    }
    return undefined;
  }

  getCompletedCount(): number {
    let count = 0;
    this.uploadProgressMap.forEach((info) => {
      const isCompleted = info.status === 'completed' || info.progress_percentage === 100 || info.stage === 'completed';
      if (isCompleted) count++;
    });
    return count;
  }

  getFailedCount(): number {
    let count = 0;
    this.uploadProgressMap.forEach((info) => {
      const isFailed = info.status === 'failed' || info.stage === 'failed';
      // const isFailed = info.status === 'failed' || !!info.error_stage || info.stage === 'failed';
      if (isFailed) count++;
    });
    return count;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  // Clean up polling intervals when component is destroyed
  ngOnDestroy(): void {
    // Stop any picking-time read check: its next detectChanges() would hit a
    // destroyed view.
    this.readableCheckGen++;
    this.readTraceGen++;

    // closeHandled is only ever set right before a deliberate dialogRef.close()
    // (✕, minimize, subscription upgrade, an expired-batch auto-close). If it
    // is still false here, nothing the user did inside this dialog ended it —
    // the only other thing that can (app.component calls MatDialog.closeAll()
    // on every router NavigationStart) means browser back or any other
    // navigation swept the dialog away underneath them. Previously that left
    // zero trace, indistinguishable from a clean close.
    if (!this.closeHandled) {
      this.analytics.capture('upload_left_via_navigation', {
        event_id: this.eventId,
        phase: this.isFileLoading ? 'uploading' : this.isProcessing ? 'processing' : this.uploadComplete ? 'complete' : 'idle',
        files_selected: this.selectedFiles?.length || 0,
        uploaded_count: this.count,
        remaining: this.processingRemaining,
        surface: this.isMobile ? 'mobile' : 'desktop',
        role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
      });
    }

    window.removeEventListener('focus', this.onWindowFocusAfterPicker);
    this.clearPickerFallbackTimer();
    this.removeUploadWindowListeners();

    // Log memory usage before cleanup
    this.logMemoryUsage('Before Cleanup (ngOnDestroy)');

    // Flush any debounced resume state before this component stops existing.
    if (this.uploadStateSaveTimer) {
      this.saveUploadState();
    }

    // Stop session keepalive
    this.stopSessionKeepalive();

    // Stop speed monitoring
    if (this.speedMonitoringInterval) {
      clearInterval(this.speedMonitoringInterval);
      this.speedMonitoringInterval = null;
    }

    // Clean up MD5 Web Worker
    if (this.md5Worker) {
      this.md5Worker.terminate();
      this.md5Worker = null;
      this.loggingService.log('MD5 Web Worker terminated');
    }

    // Queued behind another upload and nothing sent: it goes with the dialog
    // (see waitForOtherUpload). Called off first, so no snapshot is written
    // over the running upload's.
    if (this.waitingForOtherUpload) {
      this.cancelQueue?.();
    }

    // Save current processing state before component is destroyed. Covers
    // isFileLoading too, so state also survives a destroy mid-upload (e.g.
    // the tab closing outright), not just the post-upload processing phase.
    if ((this.isProcessing || this.isFileLoading) && !this.uploadComplete) {
      // Extract tracking IDs from uploadProgressMap
      const trackingIds: number[] = [];
      this.uploadProgressMap.forEach((progress) => {
        if (progress.tracking_id) {
          trackingIds.push(progress.tracking_id);
        }
      });

      // Calculate total size
      const totalSize = this.selectedFiles?.reduce((acc, file) => acc + file.size, 0) || 0;

      const processingState = {
        isProcessing: true,
        filesCount: this.selectedFiles?.length || (this.processingRemaining + this.processingCompleted + this.processingFailed),
        uploadedCount: this.count,
        eventId: this.eventId,
        eventHex: this.albumHex(),
        // Same fields minimizeDialog() writes, so a destroy mid-upload leaves a
        // snapshot the app-level banner can drive from too — including s3Keys,
        // without which it can only show frozen numbers.
        returnUrl: this.router.url,
        eventName: SafeStorage.getItem('event_name') || '',
        isHost: this.viewerIsHost,
        timestamp: Date.now(),
        completed: this.processingCompleted,
        failed: this.processingFailed,
        remaining: this.processingRemaining,
        trackingIds: trackingIds,
        s3Keys: Array.from(this.batchS3Keys ?? []),
        allS3Keys: Array.from(this.batchTrackedKeys ?? []),
        totalSize: totalSize
      };
      // console.log('Component destroying, saving state with tracking IDs:', processingState); //__CONSOLE_LOG__
      SafeStorage.setItem('uploadProcessingState', JSON.stringify(processingState));
    }

    this.progressPollingIntervals.forEach((interval) => {
      clearInterval(interval);
    });
    this.progressPollingIntervals.clear();
    this.stopBulkProgressPolling();

    // Revoke object URLs asynchronously to avoid blocking UI
    this.revokeObjectURLsAsync();
  }

  // Async cleanup of object URLs to prevent UI freeze
  private revokeObjectURLsAsync(): void {
    // Use setTimeout to defer cleanup and prevent blocking
    setTimeout(() => {
      // Batch revoke thumbnails in chunks to avoid long blocking
      const batchSize = 50;
      let index = 0;

      const revokeBatch = () => {
        const end = Math.min(index + batchSize, this.fileThumbnails.length);
        for (let i = index; i < end; i++) {
          const t = this.fileThumbnails[i];
          if (t && typeof t === 'string' && t.startsWith('blob:')) {
            try { URL.revokeObjectURL(t); } catch (e) { /* ignore */ }
          }
        }
        index = end;

        if (index < this.fileThumbnails.length) {
          setTimeout(revokeBatch, 0); // Continue in next tick
        } else {
          // Now revoke preserved duplicates
          this.revokePreservedDuplicates();
        }
      };

      if (this.fileThumbnails && this.fileThumbnails.length > 0) {
        revokeBatch();
      } else {
        this.revokePreservedDuplicates();
      }
    }, 0);
  }

  private revokePreservedDuplicates(): void {
    if (this.preservedDuplicates && this.preservedDuplicates.length > 0) {
      this.preservedDuplicates.forEach(d => {
        if (d.thumbnail && d.thumbnail.startsWith && d.thumbnail.startsWith('blob:')) {
          try { URL.revokeObjectURL(d.thumbnail as string); } catch (e) { /* ignore */ }
        }
      });
    }
  }

  // Retry only failed uploads
  async retryFailedUploads(): Promise<void> {
    if (this.failedFiles.length === 0) {
      alert('No failed uploads to retry.');
      return;
    }

    if (this.isRetryingFailed) {
      alert('Already retrying failed uploads...');
      return;
    }

    // A file already diagnosed as non-retryable (a stale/cloud-only picker
    // handle, a 0-byte or oversized file that failed validation, etc.) will
    // fail again identically — the diagnosis doesn't change between now and
    // the network call. Retrying it only burns a round-trip and a concurrency
    // slot, so it never leaves this array to begin with.
    const retryable = this.failedFiles.filter(f => f.retryable);
    const nonRetryable = this.failedFiles.filter(f => !f.retryable);

    if (retryable.length === 0) {
      alert("These files can't be retried automatically. See the details below for what to do instead.");
      return;
    }

    const confirmed = confirm(`Retry uploading ${retryable.length} failed file${retryable.length > 1 ? 's' : ''}?`);

    if (!confirmed) return;

    this.isRetryingFailed = true;
    this.loggingService.log(
      `[RetryFailed] Starting retry for ${retryable.length} failed files` +
      (nonRetryable.length > 0 ? ` (${nonRetryable.length} skipped as non-retryable)` : '')
    );

    // Non-retryable entries never make the network call, so they go straight
    // back into the list instead of through runWithConcurrency.
    this.failedFiles = [...nonRetryable];
    this.failedCount = nonRetryable.length;

    let retrySuccessCount = 0;
    let retryFailCount = 0;

    // Same adaptive cap the main loop uses, for the same reason — and more
    // pressingly here. This runs over files that have ALREADY failed, on a
    // connection that has already proven it can't sustain the load, so the old
    // fixed Promise.all over 10 was the heaviest parallelism in the component
    // applied at the worst possible moment. On 2G or with saveData set this is
    // now 1.
    const { files: retryConcurrency } = this.getUploadConcurrency();
    this.loggingService.log(`[RetryFailed] Concurrency: ${retryConcurrency}`);

    await this.runWithConcurrency(retryable, retryConcurrency, async ({ file, retries }) => {
      const connection = (navigator as any).connection;
      try {
        this.loggingService.log(`[RetryFailed] Retrying: ${file.name}`);
        // uploadFile confirms the object landed before it returns, so a
        // clean return here is already verified.
        await this.uploadFile(this.userId, this.eventId, file);
        retrySuccessCount++;
        this.successCount++;
        this.loggingService.log(`[RetryFailed] Success: ${file.name}`);

        // Same event the first-pass loop emits on success. Without it a file
        // that failed and then succeeded on a manual retry was counted as a
        // permanent loss for the rest of time — the upload_failed from the
        // first pass stood with nothing ever contradicting it.
        this.analytics.capture('photo_uploaded', {
          event_id: this.eventId,
          file_size: file.size,
          mime_type: file.type || 'unknown',
          recovered_by_retry: true,
          previous_attempts: retries,
          surface: this.isMobile ? 'mobile' : 'desktop',
        });
      } catch (retryError) {
        const errorMsg = retryError instanceof Error ? retryError.message : String(retryError);
        this.loggingService.log(`[RetryFailed] Failed again: ${file.name} - ${errorMsg}`);
        // Diagnose fresh rather than reading `lastDiagnosedFailure` — that
        // field is shared instance state written by the original (pre-retry)
        // pass, so with a mixed-kind batch or concurrent retries in flight it
        // would attach the wrong file's diagnosis here.
        const facts = await this.diagnoseUploadFailure(retryError, file);
        this.failedFiles.push({
          file,
          error: errorMsg,
          retries: retries + 1,
          kind: facts.kind,
          retryable: facts.retryable,
        });
        retryFailCount++;
        this.failedCount++;

        this.analytics.capture('upload_retry_failed', {
          event_id: this.eventId,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type || 'unknown',
          previous_attempts: retries,
          ...facts,
          cause: uploadFailureCause(facts.kind),
          network_online: navigator.onLine,
          connection_type: connection?.effectiveType,
          surface: this.isMobile ? 'mobile' : 'desktop',
          role: SafeStorage.getItem('isHost') === 'true' ? 'host' : 'guest',
        });
      }
    });

    this.isRetryingFailed = false;

    // Show summary
    const message = `Retry complete:\n${retrySuccessCount} succeeded, ${retryFailCount} still failed.` +
      (nonRetryable.length > 0 ? `\n${nonRetryable.length} skipped — see the failed uploads list for why.` : '');
    this.loggingService.log(`[RetryFailed] ${message}`);
    alert(message);

    // Refresh images if any succeeded
    if (retrySuccessCount > 0) {
      await this.refreshImagesInBackground();
    }
  }

  // Get failed files info for display
  getFailedFilesInfo(): string[] {
    return this.failedFiles.map(({ file, error, retries }) =>
      `${file.name} (${this.getFileSize(file.size)}) - ${error} [${retries} attempts]`
    );
  }

  // The subset the "Retry" button can actually act on.
  get retryableFailedFiles(): FailedFileEntry[] {
    return this.failedFiles.filter(f => f.retryable);
  }

  // The subset a retry would just fail again — shown as an explanation
  // instead of a button that can't deliver on its promise.
  get nonRetryableFailedFiles(): FailedFileEntry[] {
    return this.failedFiles.filter(f => !f.retryable);
  }

  // Grouped by kind so mixed failures (e.g. some cloud-only, some oversized)
  // each get their own honest message rather than one blended sentence.
  get nonRetryableFailureGroups(): Array<{ kind: FailedFileKind; count: number; message: string }> {
    const counts = new Map<FailedFileKind, number>();
    let stopped = 0;
    for (const f of this.nonRetryableFailedFiles) {
      if (f.stopped) {
        stopped++;
        continue;
      }
      counts.set(f.kind, (counts.get(f.kind) ?? 0) + 1);
    }
    const groups = Array.from(counts.entries()).map(([kind, count]) => ({
      kind,
      count,
      message: this.describeNonRetryableKind(kind, count),
    }));
    // Left out by the stop: said as what the person did, first, apart from
    // anything that actually went wrong.
    if (stopped > 0) {
      groups.unshift({ kind: 'aborted', count: stopped, message: stoppedGroupMessage(stopped) });
    }
    return groups;
  }

  /**
   * One sentence per group: what happened, in the person's terms, and what to
   * do next. The wording lives in upload-failure-copy.ts so every screen says
   * the same thing; this only picks the device word.
   */
  private describeNonRetryableKind(kind: FailedFileKind, count: number): string {
    return failureGroupMessage(kind, count, this.copyDevice, this.viewerIsHost);
  }

  /** "phone" or "computer" — the copy names the thing that refused the read. */
  get copyDevice(): 'phone' | 'computer' {
    return this.isMobile ? 'phone' : 'computer';
  }

  /**
   * The same grouping for failures a retry CAN help with. These used to read
   * "N uploads failed" with no cause at all, which is the one thing a person
   * deciding whether to retry needs.
   */
  get retryableFailureGroups(): Array<{ kind: FailedFileKind; count: number; message: string }> {
    const counts = new Map<FailedFileKind, number>();
    for (const f of this.retryableFailedFiles) {
      counts.set(f.kind, (counts.get(f.kind) ?? 0) + 1);
    }
    return Array.from(counts.entries()).map(([kind, count]) => ({
      kind,
      count,
      message: failureGroupMessage(kind, count, this.copyDevice, this.viewerIsHost),
    }));
  }

  /** A few words for one file in the desktop list: the reason, not the error text. */
  reasonFor(entry: FailedFileEntry): string {
    return entry.stopped ? STOPPED_REASON : failureReason(entry.kind, this.copyDevice);
  }

  // --- Copy for the photos the browser would not read (see upload-failure-copy.ts) ---

  /** Under the progress bar, said once the first one turns up. */
  get unreadableInProgressText(): string {
    return unreadableInProgress(this.unreadableFiles.size, this.copyDevice);
  }

  get unreadablePickTitleText(): string {
    return unreadablePickTitle(this.precheckUnreadable.size, this.copyDevice);
  }

  get unreadablePickBodyText(): string {
    return unreadablePickBody(this.precheckUnreadable.size, this.readyCount);
  }

  get unreadableResultTitleText(): string {
    return unreadableResultTitle(this.cloudLeftCount);
  }

  // Cloud-only ("file-unreadable") failures are the one non-retryable kind a
  // user can actually resolve themselves — download from Google Photos, then
  // re-pick. The mobile result card gives them their own panel and CTA
  // instead of folding them into the generic failure list.
  get cloudOnlyFailedFiles(): FailedFileEntry[] {
    return this.failedFiles.filter(f => f.kind === 'file-unreadable');
  }

  // Every other non-retryable kind, shown as plain notice rows once cloud-only
  // has been split out above.
  get otherNonRetryableFailureGroups(): Array<{ kind: FailedFileKind; count: number; message: string }> {
    return this.nonRetryableFailureGroups.filter(g => g.kind !== 'file-unreadable');
  }

  // How many successful uploads are hidden behind the "+N" tile in the mobile
  // result card's 2-up thumbnail row.
  get uploadedThumbOverflowCount(): number {
    return Math.max(0, this.uploadedFiles.length - 2);
  }

  // cdk-virtual-scroll-viewport needs an explicit pixel height to render at
  // all, but a fixed one left a near-empty scrollable void under a single
  // selected file. Sized to the actual row count (itemSize 90, see the
  // template comment by the viewport), capped by the CSS max-height that
  // still shrinks further at narrow breakpoints.
  get filesViewportHeight(): number {
    return Math.min(this.selectedFiles.length * 90, 500);
  }

  /** Every photo that didn't go was one the person stopped. */
  get missedOnlyByStop(): boolean {
    return this.stoppedCount > 0 && this.stoppedCount === this.failedFiles.length && this.precheckLeftOut.length === 0;
  }

  readonly uploadStoppedTitle = UPLOAD_STOPPED_TITLE;
  readonly stoppedSectionTitle = STOPPED_SECTION_TITLE;
  readonly unreadableTag = UNREADABLE_TAG;

  /**
   * The desktop result's "Couldn't be read" reason, for files the pick-time
   * check set aside. Those never reach failedFiles, and the desktop result only
   * listed failedFiles, so on a computer they vanished without a word (an
   * empty file did exactly that). The phone's result card already counts them.
   */
  get leftOutReason(): string {
    return failureReason('file-unreadable', this.copyDevice);
  }

  /**
   * Decided once per dialog, the first time the result card asks, so
   * `guest_host_prompt_shown` is captured once and not on every
   * change-detection pass.
   */
  private hostLineDecision: boolean | null = null;

  /**
   * "Planning your own event?" under Done, on every guest upload (never the
   * host's). Hidden when a failed upload needs a retry: fixing that comes first.
   */
  get showHostLine(): boolean {
    if (!this.uploadComplete || this.uploadedFiles.length === 0 || this.retryableFailedFiles.length > 0) {
      return false;
    }
    if (this.hostLineDecision === null) {
      const isHost = this.data?.isHost ?? SafeStorage.getItem('isHost') === 'true';
      this.hostLineDecision = this.guestHostPrompt.canShowUploadLine(this.eventId, !!isHost);
      if (this.hostLineDecision) this.guestHostPrompt.recordShown('upload_card', this.eventId);
    }
    return this.hostLineDecision;
  }

  /**
   * Leaves for event creation. Not closeDialog(): its shouldRefresh makes
   * event-detail reload the page six seconds later, which would land on the
   * create screen and wipe what they had typed.
   */
  onStartOwnEvent(): void {
    this.stopSpeedMonitoring();
    this.closeHandled = true;
    this.dialogRef.close({ files: this.selectedFiles, shouldRefresh: false });
    this.guestHostPrompt.start('upload_card', this.eventId);
  }

  get mobileResultTitle(): string {
    const uploaded = this.uploadedFiles.length;
    const total = uploaded + this.failedFiles.length;
    if (uploaded === 0) {
      // Stopping on purpose is not "couldn't be added".
      if (this.missedOnlyByStop) {
        return UPLOAD_STOPPED_TITLE;
      }
      return total > 0 ? "Photos couldn't be added" : 'Upload failed';
    }
    if (this.failedFiles.length > 0) {
      return `${uploaded} of ${total} photo${total !== 1 ? 's' : ''} added`;
    }
    return `${uploaded} photo${uploaded !== 1 ? 's' : ''} added`;
  }

  get mobileResultSubtitle(): string {
    const uploaded = this.uploadedFiles.length;
    const cloudOnly = this.cloudOnlyFailedFiles.length;
    // Every miss is a photo the phone would not let us read — the exact case
    // this card was designed for.
    if (cloudOnly > 0 && cloudOnly === this.failedFiles.length) {
      return unreadableResultSubtitle(cloudOnly);
    }
    if (uploaded > 0) {
      return 'You have contributed to the beautiful event story.';
    }
    // The notice below already says what was stopped and what to do.
    if (this.missedOnlyByStop) {
      return '';
    }
    if (this.failedFiles.length > 0) {
      return "These photos couldn't be uploaded.";
    }
    return '';
  }

  // Download list of failed files as text
  downloadFailedFilesList(): void {
    if (this.failedFiles.length === 0) return;

    const content = [
      `Failed Uploads Report - ${new Date().toISOString()}`,
      `Event ID: ${this.eventId}`,
      `Total Failed: ${this.failedFiles.length}`,
      '',
      'Failed Files:',
      ...this.failedFiles.map(({ file, error, retries }, index) =>
        `${index + 1}. ${file.name} (${this.getFileSize(file.size)})\n   Error: ${error}\n   Attempts: ${retries}`
      )
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `failed-uploads-${this.eventId}-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    this.loggingService.log(`[FailedUploads] Downloaded failed files list`);
  }

  // ==================== SOLUTION 1: Virtual Scrolling ====================
  // TrackBy function for virtual scroll performance
  trackByIndex(index: number, item: File): any {
    return index;
  }

  // ==================== SOLUTION 2: Lazy Thumbnail Generation ====================
  // Only generate thumbnails for visible files
  generateFileThumbnailLazy(file: File, index: number): void {
    // Skip if already generated or not visible
    if (this.fileThumbnails[index]) return;
    if (index < this.visibleFileRange.start || index > this.visibleFileRange.end) return;

    // Generate thumbnail
    this.generateFileThumbnail(file, index);
  }

  // Update visible range on scroll (called by virtual scroll viewport)
  onScrollIndexChanged(range: { start: number; end: number }): void {
    this.visibleFileRange = range;
    // Generate thumbnails for newly visible items
    for (let i = range.start; i <= Math.min(range.end, this.selectedFiles.length - 1); i++) {
      if (!this.fileThumbnails[i] && this.selectedFiles[i]) {
        this.generateFileThumbnailLazy(this.selectedFiles[i], i);
      }
    }
  }

  // ==================== SOLUTION 3: Chunked Upload Processing ====================
  // Process uploads in chunks to avoid browser overload
  private async processUploadChunk(chunk: File[], chunkIndex: number, totalChunks: number): Promise<void> {
    this.loggingService.log(`Processing upload chunk ${chunkIndex + 1}/${totalChunks} (${chunk.length} files)`);

    // Initialize tracking for this chunk
    await this.initializeBulkUploadTracking(chunk, this.userId, this.eventId);

    // Upload files in chunk
    const uploadPromises = chunk.map(async (file, fileIndex) => {
      const globalIndex = chunkIndex * this.UPLOAD_CHUNK_SIZE + fileIndex;
      try {
        await this.uploadFile(this.userId, this.eventId, file);
        this.uploadedCheck[globalIndex] = true;
        this.count++;
        this.uploadedFiles.push(file);
        this.syncMinimizedProgress();

        // Update upload summary
        this.updateUploadSummary();
      } catch (error: any) {
        this.loggingService.log(`Chunk ${chunkIndex + 1}: File ${file.name} failed - ${error}`);
        this.failedFiles.push({ file, error: error.message || 'Upload failed', retries: 0, kind: 'unknown', retryable: true });
        this.failedCount++;
      }
    });

    await Promise.allSettled(uploadPromises);
    this.loggingService.log(`Chunk ${chunkIndex + 1}/${totalChunks} completed`);
  }

  // Sleep utility for chunk delays
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ==================== SOLUTION 4: Web Worker for MD5 ====================
  // Initialize MD5 Web Worker
  private initializeMD5Worker(): void {
    if (typeof Worker !== 'undefined') {
      try {
        // Create worker from separate file
        this.md5Worker = new Worker(new URL('../workers/md5-worker.ts', import.meta.url), { type: 'module' });

        this.md5Worker.onerror = (error) => {
          this.loggingService.log(`MD5 Worker error: ${error.message}`);
          this.md5Worker = null;
        };

        this.loggingService.log('MD5 Web Worker initialized successfully');
      } catch (error: any) {
        this.loggingService.log(`Failed to initialize MD5 Worker: ${error.message}`);
        this.md5Worker = null;
      }
    } else {
      this.loggingService.log('Web Workers not supported in this browser');
    }
  }

  /**
   * Mark files that are byte-identical to an earlier file in the same
   * selection, so only one copy of each is uploaded.
   *
   * Hashing every file would be far too slow, so size is used as the filter
   * first: two files cannot be identical unless their byte counts match, and
   * exact size collisions between genuinely different photos are rare. Only
   * the colliding groups are hashed.
   *
   * The first file of each identical group is kept; the rest are marked
   * `duplicate_in_selection` and flow through the existing duplicate handling,
   * which excludes them from the upload and shows them in the duplicates list.
   *
   * @returns how many files were marked
   */
  private async markDuplicatesWithinSelection(files: File[]): Promise<number> {
    const bySize = new Map<number, File[]>();
    for (const file of files) {
      const group = bySize.get(file.size);
      if (group) group.push(file); else bySize.set(file.size, [file]);
    }

    const candidates: File[] = [];
    bySize.forEach(group => {
      if (group.length > 1) candidates.push(...group);
    });

    if (candidates.length === 0) {
      this.loggingService.log('Dedupe: no same-size files in selection, nothing to hash');
      return 0;
    }

    // Very large files are excluded: hashing them costs more time than the
    // upload it would save, and an accidental duplicate of a huge file is
    // unlikely. The backend still dedupes these by MD5 if they are copies.
    const hashable = candidates.filter(f => f.size <= this.DEDUPE_MAX_FILE_BYTES);
    const tooLarge = candidates.length - hashable.length;
    if (tooLarge > 0) {
      this.loggingService.log(`Dedupe: skipping ${tooLarge} file(s) over ${this.DEDUPE_MAX_FILE_BYTES / (1024 * 1024)}MB`);
    }

    this.loggingService.log(
      `Dedupe: ${hashable.length} file(s) share a size with another — hashing to confirm`
    );

    this.isCheckingDuplicates = true;
    const startedAt = Date.now();
    let marked = 0;

    try {
      const firstSeenByHash = new Map<string, File>();

      for (const file of hashable) {
        // Already excluded for matching a file that's in the event.
        if (this.duplicateFiles.has(file.name)) continue;

        try {
          const md5 = await this.computeMD5InWorker(file, 0);
          this.fileMD5s.set(file.name, md5);

          const original = firstSeenByHash.get(md5);
          if (original) {
            this.duplicateFiles.set(file.name, 'duplicate_in_selection');
            marked++;
            this.loggingService.log(`Dedupe: ${file.name} is identical to ${original.name} — skipping`);
          } else {
            firstSeenByHash.set(md5, file);
          }
        } catch (err) {
          // Can't hash it, so can't prove it's a duplicate. Upload it.
          this.loggingService.log(`Dedupe: could not hash ${file.name}, uploading it: ${describeError(err)}`);
        }
      }
    } finally {
      this.isCheckingDuplicates = false;
    }

    const seconds = ((Date.now() - startedAt) / 1000).toFixed(1);
    this.loggingService.log(`Dedupe: found ${marked} duplicate(s) within the selection in ${seconds}s`);
    return marked;
  }

  /** Monotonic id so concurrent hashes don't confuse each other's replies. */
  private md5RequestId = 0;

  /**
   * Compute MD5 in the worker, streaming the file in slices.
   *
   * Reading the whole file into an ArrayBuffer and posting it cost ~2x the
   * file size in memory (the post is a structured clone), which is a crash
   * risk on large video. Slicing keeps peak memory at one chunk.
   */
  private async computeMD5InWorker(file: File, _index: number): Promise<string> {
    if (!this.md5Worker) {
      // Fallback to regular computation if worker not available
      return this.computeMD5(file);
    }

    const worker = this.md5Worker;
    const id = ++this.md5RequestId;
    const CHUNK = 4 * 1024 * 1024; // 4MB slices

    const result = new Promise<string>((resolve, reject) => {
      const messageHandler = (event: MessageEvent) => {
        if (event.data?.id !== id) return;
        worker.removeEventListener('message', messageHandler);
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data.md5);
        }
      };
      worker.addEventListener('message', messageHandler);
    });

    for (let offset = 0; offset < file.size; offset += CHUNK) {
      const end = Math.min(offset + CHUNK, file.size);
      const buffer = await file.slice(offset, end).arrayBuffer();
      worker.postMessage(
        { id, fileName: file.name, chunk: buffer, last: end >= file.size },
        [buffer] // transfer, don't clone
      );
    }

    return result;
  }

  // ==================== SOLUTION 5: Paginated Progress Polling ====================
  // Override bulk progress polling with pagination
  private startBulkProgressPollingPaginated(): void {
    this.loggingService.log('Starting paginated bulk progress polling');

    this.bulkProgressInterval = setInterval(async () => {
      // Skip this tick if the previous poll is still running, rather than
      // stacking another request on top of it. Progress display is a
      // nice-to-have; starving the actual uploads of connections is not.
      if (this.pollInFlight) {
        this.loggingService.log('[bulkProgressPolling] Previous poll still in flight — skipping this tick');
        return;
      }

      const allKeys = Array.from(this.s3KeysToTrack);

      if (allKeys.length === 0) {
        this.stopBulkProgressPolling();
        return;
      }

      // Get current batch
      const batchKeys = allKeys.slice(
        this.progressPollOffset,
        this.progressPollOffset + this.PROGRESS_POLL_BATCH_SIZE
      );

      if (batchKeys.length === 0) {
        // Loop back to start
        this.progressPollOffset = 0;
        return;
      }

      this.pollInFlight = true;
      try {
        // Bounded so a hung progress request can't wedge the poller shut
        // forever via the in-flight guard above.
        await withTimeout(
          this.fetchProgressBatch(batchKeys),
          30000,
          'Bulk progress poll'
        );

        // Move to next batch
        this.progressPollOffset += this.PROGRESS_POLL_BATCH_SIZE;

        // Reset offset if we've reached the end
        if (this.progressPollOffset >= allKeys.length) {
          this.progressPollOffset = 0;
        }
      } catch (err) {
        // Progress polling is cosmetic — never let it surface as an upload
        // failure or stop the poller.
        this.loggingService.logError('[bulkProgressPolling] Poll failed', err);
      } finally {
        this.pollInFlight = false;
      }
    }, 3000); // Poll every 3 seconds
  }

  // Fetch progress for a batch of keys
  private async fetchProgressBatch(keys: string[]): Promise<void> {
    try {
      const apiUrl = `${this.uploadProgressApiUrl}?action=getBulk`;
      const requestBody = { keys: keys };

      this.loggingService.log(`[fetchProgressBatch] Fetching progress for ${keys.length} files`);
      this.loggingService.log(`[fetchProgressBatch] API URL: ${apiUrl}`);
      this.loggingService.log(`[fetchProgressBatch] Request body: ${JSON.stringify(requestBody)}`);
      this.loggingService.log(`[fetchProgressBatch] Sample keys: ${JSON.stringify(keys.slice(0, 3))}`);

      // Use POST with body like the original implementation
      const response: any = await firstValueFrom(
        this.http.post(apiUrl, requestBody, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
      );

      this.loggingService.log(`[fetchProgressBatch] Response received: ${JSON.stringify(response)}`);
      this.loggingService.log(`[fetchProgressBatch] Uploads count: ${response?.uploads?.length || 0}`);

      if (response && response.uploads) {
        response.uploads.forEach((upload: any) => {
          // Map the S3 key back to the file by its exact last segment. The old
          // substring match mixed files up (key ".../IMG_1.jpg" contains the
          // name "1.jpg") and couldn't see through a _1 clash suffix.
          const s3Key: string = upload.s3_key || '';
          const keyName = s3Key.substring(s3Key.lastIndexOf('/') + 1);
          const fileName = this.uploadNameToOriginal.get(keyName);

          const mapKey = fileName || upload.original_file_name || upload.file_name || upload.s3_key;

          // Get current progress
          const currentProgress = this.uploadProgressMap.get(mapKey);
          const currentPercentage = currentProgress?.progress_percentage || 0;
          const newPercentage = upload.progress_percentage;

          // Fix: If completed, force status
          let status = upload.status;
          if ((upload.stage === 'completed' || upload.progress_percentage === 100) && status !== 'completed') {
            status = 'completed';
          }

          // Only update if progress increases or status changes
          if (newPercentage >= currentPercentage || status !== currentProgress?.status) {
            const progressInfo: UploadProgressInfo = {
              tracking_id: upload.id,
              file_name: upload.file_name,
              original_file_name: upload.original_file_name,
              status: status,
              progress_percentage: Math.max(newPercentage, currentPercentage),
              stage: upload.stage,
              error_message: upload.error_message,
              error_stage: upload.error_stage,
              retry_count: upload.retry_count
            };
            this.uploadProgressMap.set(mapKey, progressInfo);
            this.loggingService.log(`Progress: ${mapKey} - ${progressInfo.progress_percentage}% - ${progressInfo.status} - ${progressInfo.stage}`);
          }

          // Mark as uploaded when completed
          if (status === 'completed') {
            let fileIndex = this.selectedFiles.findIndex(f => f.name === fileName);
            if (fileIndex === -1 && upload.original_file_name) {
              const normalized = (n: string) => n.replace(/\s/g, "").replace("'", "").toLowerCase();
              fileIndex = this.selectedFiles.findIndex(f => normalized(f.name) === normalized(upload.original_file_name));
            }
            if (fileIndex !== -1) {
              this.uploadedCheck[fileIndex] = true;
            }
            this.s3KeysToTrack.delete(upload.s3_key);
          } else if (upload.status === 'failed' && upload.error_stage !== 'compression') {
            this.s3KeysToTrack.delete(upload.s3_key);
          }
        });
      }
    } catch (error: any) {
      this.loggingService.log(`Error fetching progress batch: ${error.message}`);
    }
  }

  // ==================== SOLUTION 7: Smart Duplicate Detection ====================
  // Optimize duplicate checking - only check MD5 for name matches
  async checkForDuplicatesSmart(): Promise<void> {
    this.isCheckingDuplicates = true;
    this.duplicateFiles.clear();

    try {
      // First pass: Check by name only (fast)
      const potentialDuplicates: File[] = [];

      for (const file of this.selectedFiles) {
        const normalizedName = this.normalizeFileName(file.name);
        if (this.existingFiles.has(normalizedName)) {
          potentialDuplicates.push(file);
          // Mark as duplicate by name (can be overridden by MD5 check)
          this.duplicateFiles.set(file.name, 'exists');
        }
      }

      this.loggingService.log(`Found ${potentialDuplicates.length} potential duplicates by name`);

      // Second pass: Check MD5 only for potential duplicates (slower but necessary)
      if (potentialDuplicates.length > 0 && this.existingMD5s.size > 0) {
        this.loggingService.log(`Computing MD5 for ${potentialDuplicates.length} potential duplicates...`);

        for (const file of potentialDuplicates) {
          try {
            // Use Web Worker if available for MD5 computation
            const md5 = this.md5Worker
              ? await this.computeMD5InWorker(file, this.selectedFiles.indexOf(file))
              : await this.computeMD5(file);

            this.fileMD5s.set(file.name, md5);

            if (this.existingMD5s.has(md5)) {
              // Confirmed duplicate by content
              this.duplicateFiles.set(file.name, 'duplicate_content');
              this.loggingService.log(`Confirmed duplicate: ${file.name} (MD5: ${md5})`);
            } else {
              // Same name but different content - remove from duplicates
              this.duplicateFiles.delete(file.name);
              this.loggingService.log(`Not a duplicate (different content): ${file.name}`);
            }
          } catch (error: any) {
            this.loggingService.log(`MD5 computation failed for ${file.name}: ${error.message}`);
            // Keep as potential duplicate on error
          }
        }
      }

      this.showDuplicateWarning = this.duplicateFiles.size > 0;
      this.loggingService.log(`Duplicate check complete: ${this.duplicateFiles.size} confirmed duplicates`);
    } catch (error: any) {
      this.loggingService.log(`Error in smart duplicate detection: ${error.message}`);
    } finally {
      this.isCheckingDuplicates = false;
    }
  }

  // ==================== SOLUTION 8: Upload Summary ====================
  // Update aggregate upload summary
  private updateUploadSummary(): void {
    const totalSize = this.selectedFiles.reduce((acc, file) => acc + file.size, 0);
    const uploadedSize = this.uploadedFiles.reduce((acc, file) => acc + file.size, 0);

    this.uploadSummary = {
      totalFiles: this.selectedFiles.length,
      uploadedFiles: this.count,
      failedFiles: this.failedFiles.length,
      totalSize: totalSize,
      uploadedSize: uploadedSize,
      currentSpeed: this.currentUploadSpeed,
      eta: this.estimatedTime || '--',
      completionPercentage: this.selectedFiles.length > 0
        ? Math.round((this.count / this.selectedFiles.length) * 100)
        : 0
    };
  }

  // Get summary statistics for display
  getSummaryStats(): string {
    return `${this.uploadSummary.uploadedFiles}/${this.uploadSummary.totalFiles} files uploaded (${this.uploadSummary.completionPercentage}%)`;
  }

  private async refreshImagesInBackground(): Promise<void> {
    this.isRefreshingImages = true;
    this.loggingService.log('Fetching fresh images in background...');

    try {
      // Fetch fresh images from the event
      (await this.eventService.getEventById(0, 100, 'desc', 'all', 'null', 'date')).subscribe({
        next: (res: any) => {
          if (res && res.images) {
            this.loggingService.log(`Fresh images fetched: ${res.images.length} images`);
            this.isRefreshingImages = false;

            // Optionally trigger a refresh in the parent component
            // The parent component should listen for dialog close and refresh its view
          }
        },
        error: (err: any) => {
          this.loggingService.log(`Error fetching fresh images: ${err}`);
          this.isRefreshingImages = false;
        }
      });
    } catch (error) {
      this.loggingService.log(`Error in refreshImagesInBackground: ${error}`);
      this.isRefreshingImages = false;
    }
  }
}
